import './auth/password-strength.js';
import './auth/username-checker.js';
import './auth/form-validation.js';
import './auth/auth-forms-handler.js';

/**
 * Returns the requested theme as-is — all themes are available to all users.
 * The isProActive parameter is kept for API compatibility but no longer gates themes.
 * 
 * @param {string} requestedTheme - The theme ID requested
 * @param {boolean} isProActive - Whether the user has pro access (unused, kept for compat)
 * @returns {string} - The validated theme ID
 * @since 3.9.4
 */
function validateThemeAccess(requestedTheme, isProActive) {
    // All themes are free — return as-is.
    // Normalize: if just a number, prefix with 'theme-'
    if ( requestedTheme && /^\d+$/.test(requestedTheme) ) {
        return 'theme-' + requestedTheme;
    }
    return requestedTheme || 'theme-0';
}

/**
 * Tiny helper: POST to SimpleForm admin-ajax and return Promise<{json,ok,response}>.
 * Falls back to a simulated { success:true, data:fallback } on network error so
 * offline / free-plugin scenarios still work with graceful degradation.
 *
 * @param {string} action - wp_ajax_ hook action name
 * @param {Object} body   - plain key/value object (sent as application/x-www-form-urlencoded)
 * @param {*}      fallbackSimulated - success payload to use on network failure
 * @returns {Promise<{success:boolean, data:*, message?:string, simulated?:boolean}>}
 */
function sfPostAjax(action, body, fallbackSimulated) {
  var payload = Object.assign({ action: action, nonce: (typeof front_end_data !== 'undefined' && front_end_data.nonce) || '' }, body || {});
  var params = new URLSearchParams();
  Object.keys(payload).forEach(function (k) {
    var v = payload[k];
    if (v === null || v === undefined) v = '';
    params.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
  });
  var url = (typeof front_end_data !== 'undefined' && front_end_data.admin_ajax) ? front_end_data.admin_ajax : (window.location.origin + '/wp-admin/admin-ajax.php');
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
    body: params.toString()
  })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      if (typeof data === 'object' && data !== null && 'success' in data) return data;
      return { success: true, data: data };
    })
    .catch(function () {
      if (fallbackSimulated === undefined) {
        return { success: false, data: null, message: 'AJAX request failed.', simulated: false };
      }
      return { success: true, data: typeof fallbackSimulated === 'function' ? fallbackSimulated(body) : fallbackSimulated, simulated: true };
    });
}

/**
 * 
 * @param {*} formData 
 * @returns 
 */

function generateRenderedForm(formData, ispro, theme = 'theme-0', instanceId = '') {
  // Validate theme access for free users
  theme = validateThemeAccess(theme, ispro);

  function getDomId(rawId) {
    if (!rawId) return '';
    return instanceId ? (instanceId + '_' + rawId) : rawId;
  }
  
  let html = '';
  let currentPage = 1;
  let totalPages = 1;
  let isPageStarted = false;
  let hasSubmitButton = false;
  let progressIndicatorSettings = null;

  // Add theme class to the main container
  html += `
    <style>
      .simple_form_content {
        font-family: 'Arial', sans-serif;
      }
      
      .simple_form_content .page {
        display: none;
      }
      
      .simple_form_content .page.active {
        display: flex !important;
        flex-wrap: wrap !important;
        gap: 12px;
        align-items: flex-start;
        box-sizing: border-box;
        width: 100%;
      }
      
      /* Grid and Column Layout support - allows fields across or within rows to sit side-by-side based on columnWidth */
      .form-row {
        display: flex;
        margin-bottom: 15px;
        width: 100%;
      }
      
      .form-row.horizontal {
        flex-direction: row;
        gap: 15px;
        flex-wrap: wrap;
      }
      
      .form-row.vertical {
        flex-direction: column;
        gap: 10px;
      }
      
      .form-row.horizontal > .text-fields {
        flex: 1;
        min-width: 0;
        margin-bottom: 0;
      }
      
      .form-row.vertical > .text-fields {
        width: 100%;
      }
      
      .simple_form_content .form-row .text-fields,
      .simple_form_content .page .text-fields,
      .simple_form_content .page.active .text-fields,
      .form-row .text-fields {
        box-sizing: border-box !important;
        margin-bottom: 0;
      }
      @media (max-width: 640px) {
        .form-row.horizontal {
          flex-direction: column;
        }
        
        .simple_form_content .form-row .text-fields,
        .simple_form_content .page .text-fields,
        .simple_form_content .form-row > div,
        .simple_form_content .page.active > div,
        .simple_form_content .page.active .text-fields,
        .form-row > .text-fields {
          flex: 0 0 100% !important;
          width: 100% !important;
          max-width: 100% !important;
        }
      }
      
      /* Navigation container */
      .navigation-buttons {
        display: flex;
        justify-content: space-between;
        margin-top: 20px;
        padding: 10px 0;
      }
      
      .navigation-buttons.first-page {
        justify-content: flex-end;
      }
      
      .navigation-buttons.with-submit {
        justify-content: space-between;
      }
      
      /* Button styles - basic shared styles */
      .simple_form_content .form-btn {
        border: none;
        border-radius: 5px;
        padding: 7px 20px;
        cursor: pointer;
        font-size: 16px;
        height: 38px;
        margin-right: 10px;
      }
      
      .simple_form_content .next-btn,
      .simple_form_content .prev-btn,
      .simple_form_content .submit-btn {
        background-color: #a177f8;
        color: #fff;
      }
      
      /* Signature panel styles */
      .signature-panel {
        border: 1px solid gray;
        padding: 5px;
        width: 100%;
        max-width: 300px;
      }
      
      /* Field styles */
      .text-fields {
        margin-bottom: 15px;
        box-sizing: border-box;
      }
      
      .text-fields input:not([type="checkbox"]):not([type="radio"]),
      .text-fields select,
      .text-fields textarea {
        width: 100%;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        box-sizing: border-box;
        margin-top: 5px;
      }
      
      .text-fields label {
        display: block;
        margin-bottom: 5px;
        font-weight: 500;
      }

      /* Required field indicator styles */
      .required-indicator {
        color: #e32;
        margin-left: 3px;
        font-weight: bold;
      }
      
      /* Add tooltip for required fields */
      .required-field-label {
        position: relative;
      }
      
      .required-indicator:hover::after {
        content: 'This field is required';
        position: absolute;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        z-index: 99;
        left: 0;
        top: 100%;
        margin-top: 5px;
      }

      /* Progress Indicator Styles */
      .progress-indicator {
        margin-bottom: 20px;
        text-align: center;
      }
      
      .progress-dots {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;
        margin-top: 10px;
      }
      
      .progress-dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background-color: #ddd;
        transition: background-color 0.3s ease;
      }
      
      .progress-dot.active {
        background-color: #a177f8;
      }
      
      .progress-dot.completed {
        background-color: #4caf50;
      }
      
      .progress-numbers {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 15px;
        margin-top: 10px;
      }
      
      .progress-number {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background-color: #ddd;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        color: #666;
        transition: all 0.3s ease;
      }
      
      .progress-number.active {
        background-color: #a177f8;
        color: white;
      }
      
      .progress-number.completed {
        background-color: #4caf50;
        color: white;
      }
      
      .progress-bar-container {
        width: 100%;
        background-color: #ddd;
        border-radius: 10px;
        height: 8px;
        margin-top: 10px;
        overflow: hidden;
      }
      
      .progress-bar {
        height: 100%;
        background-color: #a177f8;
        border-radius: 10px;
        transition: width 0.3s ease;
      }
      
      .progress-label {
        font-weight: 500;
        margin-bottom: 5px;
        color: #333;
      }

      /* Conditional field styles */
      .conditional-field {
        transition: opacity 0.3s ease, height 0.3s ease;
      }
      
      .conditional-field.hidden {
        display: none !important;
      }


    </style>
  `;

  // Add theme-specific styles
  html += getThemeStyles(theme); // No need now

  // Count total pages and find progress indicator settings
  totalPages = countTotalPages(formData);
  progressIndicatorSettings = findProgressIndicatorSettings(formData);

  // Start the form container with the selected theme
  html += `<div class="simple_form_content_form_area ${theme}">`;

  // console.log(progressIndicatorSettings)

  // Add progress indicator if enabled
  if (progressIndicatorSettings && (progressIndicatorSettings.showProgressIndicator === true || progressIndicatorSettings.showProgressIndicator === "true") && totalPages > 1) {
    // // console.log('Adding progress indicator');
    html += generateProgressIndicator(progressIndicatorSettings, currentPage, totalPages);
  } else {
    // // console.log('Not adding progress indicator');
  }

  // Anti-spam Honeypot field (hidden from users, traps automated bots).
  // No `name` attribute — so FormData(form) never includes it automatically,
  // preventing browser autofill from filling it and causing false positives.
  // The submit handler reads it via data-sf-hp and appends it manually.
  html += `<div class="sf-hp-wrap" style="position:absolute;left:-9999px;top:-9999px;opacity:0;pointer-events:none;z-index:-1;" aria-hidden="true">
    <input type="text" data-sf-hp="1" id="sf_hp_${Math.random().toString(36).substring(2, 7)}" value="" tabindex="-1" autocomplete="nope">
  </div>`;

  // Start with the first page
  html += `<div class="page" data-page="${currentPage}">`;
  isPageStarted = true;

  // Process the form data
  processFormData(formData);

  // Close the last page if it's still open
  if (isPageStarted) {
    // Check for submit button in the last page
    const hasSubmitOnLastPage = checkForSubmitButton(formData);

    // Add navigation for the last page
    if (hasSubmitOnLastPage) {
      if (currentPage > 1) {
        html += `<div class="navigation-buttons with-submit">
                  <button type="button" class="form-btn prev-btn" data-page="${currentPage - 1}">Previous</button>
                </div>`;
      }
    } else {
      if (currentPage > 1) {
        html += `<div class="navigation-buttons">
                  <button type="button" class="form-btn prev-btn" data-page="${currentPage - 1}">Previous</button>
                </div>`;
      }
    }

    html += `</div>`;
  }

  // Close the form container
  html += `</div>`;

  return html;

  // Function to count total pages in the form
  function countTotalPages(data) {
    let pages = 1;
    data.forEach((item) => {
      if (item.type === 'row') {
        item.fields.forEach((field) => {
          if (field.type === 'page_break') {
            pages++;
          }
        });
      } else if (item.type === 'page_break') {
        pages++;
      }
    });
    return pages;
  }

  // Function to find progress indicator settings from page breaks
  function findProgressIndicatorSettings(data) {
    // // console.log('Searching for progress indicator in data:', data);
    // Check if ANY page break has progress indicator enabled
    for (const item of data) {
      if (item.type === 'row') {
        for (const field of item.fields) {
          if (field.type === 'page_break') {
            // // console.log('Found page break field:', field);
            // Check for both boolean true and string "true"
            if (field.showProgressIndicator === true || field.showProgressIndicator === "true") {
              // // console.log('Progress indicator enabled on field:', field);
              return field;
            }
          }
        }
      } else if (item.type === 'page_break') {
        // // console.log('Found page break item:', item);
        // Check for both boolean true and string "true"
        if (item.showProgressIndicator === true || item.showProgressIndicator === "true") {
          // // console.log('Progress indicator enabled on item:', item);
          return item;
        }
      }
    }
    // // console.log('No progress indicator found');
    return null;
  }

  // Function to generate progress indicator HTML
  function generateProgressIndicator(settings, currentPage, totalPages) {
    const label = settings.progressLabel || 'Step';
    const style = settings.progressStyle || 'dots';

    let html = `<div class="progress-indicator" id="progress-indicator">`;
    html += `<div class="progress-label">${label} ${currentPage} of ${totalPages}</div>`;

    if (style === 'dots') {
      html += `<div class="progress-dots">`;
      for (let i = 1; i <= totalPages; i++) {
        const className = i < currentPage ? 'completed' : (i === currentPage ? 'active' : '');
        html += `<div class="progress-dot ${className}" data-page="${i}"></div>`;
      }
      html += `</div>`;
    } else if (style === 'numbers') {
      html += `<div class="progress-numbers">`;
      for (let i = 1; i <= totalPages; i++) {
        const className = i < currentPage ? 'completed' : (i === currentPage ? 'active' : '');
        html += `<div class="progress-number ${className}" data-page="${i}">${i}</div>`;
      }
      html += `</div>`;
    } else if (style === 'bar') {
      const percentage = ((currentPage - 1) / (totalPages - 1)) * 100;
      html += `<div class="progress-bar-container">`;
      html += `<div class="progress-bar" style="width: ${percentage}%"></div>`;
      html += `</div>`;
    }

    html += `</div>`;
    return html;
  }

  // Function to check if the form data contains a submit button
  function checkForSubmitButton(data) {
    for (const item of data) {
      if (item.type === 'submit') {
        return true;
      }

      if (item.type === 'row' && item.fields) {
        for (const field of item.fields) {
          if (field.type === 'submit') {
            return true;
          }
        }
      }
    }
    return false;
  }

  // Function to process the entire form data array
  function processFormData(data) {
    let submitButtonFound = false;

    data.forEach((item) => {
      if (item.type === 'row') {
        processRow(item);
      } else if (item.type === 'submit') {
        submitButtonFound = true;
        processField(item);
      } else {
        // If it's a direct field (not in a row)
        processField(item);
      }
    });

    hasSubmitButton = submitButtonFound;
  }

  // Function to process a row and its fields
  function processRow(row) {
    let currentRowHtml = ''; // Collect HTML for this row
    let rowStarted = false;
    let pageBreakInRow = false;
    let submitButtonInRow = false;

    // First pass to check if there's a submit button in this row
    for (let i = 0; i < row.fields.length; i++) {
      if (row.fields[i].type === 'submit') {
        submitButtonInRow = true;
        break;
      }
    }

    // Process fields in this row
    for (let i = 0; i < row.fields.length; i++) {
      const field = row.fields[i];

      // If we encounter a page break
      if (field.type === 'page_break') {
        pageBreakInRow = true;

        // If we've already started the row, close it
        if (rowStarted) {
          currentRowHtml += `</div>`;
          rowStarted = false;

          // Add the row's HTML so far to the current page
          html += currentRowHtml;
          currentRowHtml = '';
        }

        // Add navigation buttons and close current page
        if (isPageStarted) {
          // Add next/prev buttons for the current page
          if (currentPage === 1) {
            html += `<div class="navigation-buttons first-page">
                      <button type="button" class="form-btn next-btn" data-page="${currentPage + 1}">Next</button>
                    </div>`;
          } else {
            html += `<div class="navigation-buttons">
                      <button type="button" class="form-btn prev-btn" data-page="${currentPage - 1}">Previous</button>
                      <button type="button" class="form-btn next-btn" data-page="${currentPage + 1}">Next</button>
                    </div>`;
          }

          html += `</div>`;
          isPageStarted = false;
        }

        // Increment page counter
        totalPages++;
        currentPage++;

        // Start a new page
        html += `<div class="page" data-page="${currentPage}">`;
        isPageStarted = true;

        continue; // Skip further processing for this field
      }

      // If this is the first field after a page break or the first field in the row
      if (!rowStarted) {
        currentRowHtml += `<div class="form-row ${row.layout}" id="${row.id}">`;
        rowStarted = true;
      }

      // Add the field to the current row HTML
      currentRowHtml += renderField(field, row.layout);
    }

    // If we have a row that's still open, close it
    if (rowStarted) {
      currentRowHtml += `</div>`;
    }

    // Add any remaining row HTML to the current page
    html += currentRowHtml;
  }

  // Function to process a standalone field (not in a row)
  function processField(field) {
    if (field.type === 'page_break') {
      // Close the current page
      if (isPageStarted) {
        // Add next/prev buttons for the current page
        if (currentPage === 1) {
          html += `<div class="navigation-buttons first-page">
                    <button type="button" class="form-btn next-btn" data-page="${currentPage + 1}">Next</button>
                  </div>`;
        } else {
          html += `<div class="navigation-buttons">
                    <button type="button" class="form-btn prev-btn" data-page="${currentPage - 1}">Previous</button>
                    <button type="button" class="form-btn next-btn" data-page="${currentPage + 1}">Next</button>
                  </div>`;
        }

        html += `</div>`;
        isPageStarted = false;
      }

      // Increment page counter
      totalPages++;
      currentPage++;

      // Start a new page
      html += `<div class="page" data-page="${currentPage}">`;
      isPageStarted = true;
    } else {
      // Render any other type of field
      html += renderField(field);
    }
  }

  // Helper function to create label with required indicator if needed
  function getLabel(field) {
    const isRequired = field.required === true || field.required === "true";
    let labelClass = isRequired ? 'required-field-label' : '';
    let requiredIndicator = isRequired ? '<span class="required-indicator">*</span>' : '';

    return `<label for="${getDomId(field.id)}" class="${labelClass}">${field.label}${requiredIndicator}</label>`;
  }

  // Function to render a single field based on its type
  function renderField(field, rowLayout) {
    let fieldHtml = '';
    let fieldAttributes = '';

    if (field.required === true || field.required === "true") {
      fieldAttributes += ' required';
    }

    // Add field ID for conditional logic
    const fieldId = field.uniqueId || field.id;
    fieldAttributes += ` data-field-id="${fieldId}"`;

    // Add conditional logic attributes
    if (field.conditionalLogic && field.conditionalLogic.enabled) {
      fieldAttributes += ` data-conditional-logic='${JSON.stringify(field.conditionalLogic)}'`;
    }

    // Wrap field in conditional container if needed
    const hasConditionalLogic = field.conditionalLogic && field.conditionalLogic.enabled;
    const conditionalClass = hasConditionalLogic ? 'conditional-field' : '';
    const conditionalAttributes = hasConditionalLogic ?
      `data-conditional-logic='${JSON.stringify(field.conditionalLogic)}' data-field-id="${field.uniqueId || field.id}"` : '';

    switch (field.type) {
      case 'image':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            <label for="${field.id}">${field.label}</label>
            <div class="${field.className}">
              <img src="${field.src}" width="${field.width}" height="${field.height}" alt="${field.label}">
            </div>
          </div>
        `;
        break;

      case 'hidden':
        fieldHtml += `
            <input id="${field.id}" type="${field.type}" name="${field.name}" placeholder="${field.placeholder}" class="${field.className} ${conditionalClass}" value="${field.value || ''}"${fieldAttributes} ${conditionalAttributes}>
        `;
        break;

      case 'textarea':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
           ${getLabel(field)}
            <textarea id="${field.id}" name="${field.name}" placeholder="${field.placeholder || ''}" class="${field.className}" data-field-id="${fieldId}"${fieldAttributes}>${field.value || ''}</textarea>
          </div>
        `;
        break;

      case 'file':
        const maxMB = field.maxFileSize || 5;
        const preset = field.fileTypePreset || 'all';
        const customExts = field.customFileTypes || '';
        const isMultiple = field.uploadMode === 'multiple' || field.multiple === true || field.multiple === 'true';
        const maxFilesLimit = field.maxFiles || 5;

        let acceptAttr = '';
        if (preset === 'images') acceptAttr = 'accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"';
        else if (preset === 'documents') acceptAttr = 'accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md"';
        else if (preset === 'audio') acceptAttr = 'accept="audio/*,.mp3,.wav,.ogg"';
        else if (preset === 'video') acceptAttr = 'accept="video/*,.mp4,.avi,.mov,.mkv"';
        else if (preset === 'archive') acceptAttr = 'accept=".zip,.rar,.7z,.tar,.gz"';
        else if (preset === 'custom' && customExts) {
          const formattedCustom = customExts.split(',').map(ext => ext.trim().startsWith('.') ? ext.trim() : '.' + ext.trim()).join(',');
          acceptAttr = `accept="${formattedCustom}"`;
        }

        const helpText = field.helpText || `Allowed file types: ${preset.toUpperCase()} | Max size: ${maxMB}MB`;

        fieldHtml += `
          <div class="text-fields sf-file-upload-wrapper ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <div class="sf-file-dropzone" id="dropzone-${field.id}" data-input-id="${field.id}" data-max-size="${maxMB}" data-preset="${preset}" data-custom-types="${customExts}" data-upload-mode="${isMultiple ? 'multiple' : 'single'}" data-max-files="${maxFilesLimit}" data-required="${field.required ? 'true' : 'false'}">
              <input id="${field.id}" type="file" name="${field.name}" class="sf-file-input ${field.className}" ${acceptAttr} ${isMultiple ? 'multiple' : ''} ${fieldAttributes} style="display: none;">
              <div class="sf-dropzone-content">
                <div class="sf-dropzone-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                </div>
                <div class="sf-dropzone-text">
                  <span class="sf-dropzone-title">Click to upload or drag & drop</span>
                  <span class="sf-dropzone-help">${helpText}</span>
                </div>
              </div>
              <div class="sf-file-preview-list" id="preview-${field.id}"></div>
            </div>
            <div class="sf-file-error-msg" id="error-${field.id}" style="color: #ef4444; font-size: 12px; margin-top: 5px; display: none;"></div>
          </div>
        `;
        break;

      case 'heading':
        fieldHtml += `
            <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
              <${field.headingtype} class="${field.className}">${field.content}</${field.headingtype}>
            </div>
          `;
        break;

      case 'label':
        fieldHtml += `
              <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
                <label for="${field.id}" class="${field.className}">${field.content}</label>
              </div>
            `;
        break;
      case 'link':
        fieldHtml += `
              <div class="simple-form-file ${conditionalClass}" ${conditionalAttributes}>
                ${getLabel(field)}
                <a id="${field.id}" href="${field.href}" target="${field.target}" class="${field.className}">${field.content}</a>
              </div>
            `;
        break;
      case 'video':
        fieldHtml += `
              <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
                <video
                  id="${field.id}"
                  width="${field.width}"
                  height="${field.height}"
                  controls
                  src="${field.src}"
                  class="${field.className}"
                >
                  ${field.content}
                </video>
              </div>
            `;
        break;
      case 'audio':
        fieldHtml += `
              <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
                <audio
                  id="${field.id}"
                  width="${field.width}"
                  height="${field.height}"
                  controls
                  src="${field.src}"
                  class="${field.className}"
                >
                  ${field.content}
                </audio>
              </div>
            `;
        break;

      case 'iframe':
        fieldHtml += `
              <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
                <iframe
                  id="${field.id}"
                  width="${field.width}"
                  height="${field.height}"
                  src="${field.src}"
                  class="${field.className}"
                >
                  ${field.content}
                </iframe>
              </div>
            `;
        break;

      case 'esignature':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <div class="signature-panel">
              <canvas id="${field.id}" class="signature-canvas" width="300" height="150"></canvas>
              <input type="hidden" name="${field.name}" class="signature-data" data-canvas-id="${field.id}">
              <button type="button" class="reset-signature form-btn" data-canvas-id="${field.id}">Reset Signature</button>
            </div>
          </div>
        `;
        break;

      // Never change the class name from editor sf-turnstile-container 
      case 'cloudflare':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
              <div class="${field.className}" id="${field.id}" data-sitekey="${field.sitekey}" data-theme="${field.theme}"   data-submit-button="${field.btnaccess}" data-size="${field.size}" data-error="${field.error}">
              </div>
          </div>
        `;
        break;

      case 'recaptcha_v2':
      case 'recaptcha-v2':
        const r2Key = field.sitekey || (window.front_end_data && window.front_end_data.captcha_keys ? window.front_end_data.captcha_keys.recaptcha_v2_site_key : '');
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            <div class="g-recaptcha ${field.className || ''}" id="${field.id}" data-sitekey="${r2Key}" data-theme="${field.theme || 'light'}" data-size="${field.size || 'normal'}"></div>
          </div>
        `;
        break;

      case 'recaptcha_v3':
      case 'recaptcha-v3':
        const r3Key = field.sitekey || (window.front_end_data && window.front_end_data.captcha_keys ? window.front_end_data.captcha_keys.recaptcha_v3_site_key : '');
        fieldHtml += `
          <div class="sf-recaptcha-v3-wrapper ${conditionalClass}" ${conditionalAttributes} style="display:none;">
            <input type="hidden" name="g-recaptcha-response" class="sf-recaptcha-v3-token" id="${field.id}" data-sitekey="${r3Key}">
          </div>
        `;
        break;

      case 'hcaptcha':
        const hKey = field.sitekey || (window.front_end_data && window.front_end_data.captcha_keys ? window.front_end_data.captcha_keys.hcaptcha_site_key : '');
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            <div class="h-captcha ${field.className || ''}" id="${field.id}" data-sitekey="${hKey}" data-theme="${field.theme || 'light'}" data-size="${field.size || 'normal'}"></div>
          </div>
        `;
        break;

      case 'customcaptcha':
        // Generate two random values and an operator
        const value1 = Math.floor(Math.random() * 10) + 1;
        const value2 = Math.floor(Math.random() * 10) + 1;
        const operators = ['+', '-', '*'];
        const operator = operators[Math.floor(Math.random() * operators.length)];

        // Calculate the correct answer
        let correctAnswer;
        switch (operator) {
          case '+': correctAnswer = value1 + value2; break;
          case '-': correctAnswer = value1 - value2; break;
          case '*': correctAnswer = value1 * value2; break;
          default: break;
        }

        fieldHtml += `
          <div class="text-fields">
            ${getLabel(field)}
            <div>
              <span class="question-${field.className}">${value1} ${operator} ${value2} = </span>
              <input id="${field.id}" type="text" name="${field.name}" placeholder="Your Answer" class="${field.className}" required>
              <input type="hidden" name="${field.name}_correct" value="${correctAnswer}">
            </div>
          </div>
        `;
        break;

      case 'checkbox':
        const isRequired = field.required === true || field.required === "true";
        let requiredIndicator = isRequired ? '<span class="required-indicator">*</span>' : '';

        if (field.toggle === 'true') {
          // Render toggle checkbox
          fieldHtml += `
            <div class="simple-form-checkbox-toggle ${conditionalClass}" ${conditionalAttributes}>
              <label class="switch-label ${isRequired ? 'required-field-label' : ''}">${field.label}${requiredIndicator}
                <input type="checkbox" id="${field.id}" name="${field.name}" class="switch-input ${field.className}" data-field-id="${fieldId}"${fieldAttributes}>
                <span class="slider round"></span>
              </label>
            </div>`;
        } else {
          // Render standard checkboxes
          fieldHtml += `
            <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
              <label class="${isRequired ? 'required-field-label' : ''}">${field.label}${requiredIndicator}</label>`;
          field.options.forEach((option) => {
            fieldHtml += `
              <div class="text-fields-insider">
                <input type="checkbox" id="${option.value}" name="${field.name}" value="${option.value}" data-field-id="${fieldId}"${fieldAttributes}>
                <label for="${option.value}">${option.label}</label>
              </div>`;
          });
          fieldHtml += `
            </div>`;
        }
        break;

      case 'radio':
        const radioIsRequired = field.required === true || field.required === "true";
        let radioRequiredIndicator = radioIsRequired ? '<span class="required-indicator">*</span>' : '';

        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            <label class="${radioIsRequired ? 'required-field-label' : ''}">${field.label}${radioRequiredIndicator}</label>`;
        field.options.forEach((option) => {
          fieldHtml += `
            <div class="text-fields-insider">
              <input
                type="radio"
                id="${option.value}"
                name="${field.name}"
                value="${option.value}"
                ${field.value === option.value ? 'checked' : ''}
                data-field-id="${fieldId}"
                ${fieldAttributes}
              />
              <label for="${option.value}">${option.label}</label>
            </div>`;
        });
        fieldHtml += `
          </div>`;
        break;

      case 'select':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <select id="${field.id}" name="${field.name}" data-field-id="${fieldId}"${fieldAttributes}>`;
        field.options.forEach((option) => {
          fieldHtml += `
            <option value="${option.value}" ${field.value === option.value ? 'selected' : ''}>${option.label}</option>
          `;
        });
        fieldHtml += `
            </select>
          </div>
        `;
        break;

      case 'submit':
        fieldHtml += `
          <div class="text-fields">
            <input id="${field.id}" type="${field.type}" name="${field.name}" class="form-btn submit-btn ${field.className}" value="${field.value || 'Submit'}"${fieldAttributes}>
          </div>
        `;
        hasSubmitButton = true;
        break;

      case 'currency':
        const curSymbol = field.currencySymbol || '$';
        const curPos = (field.currencyPosition || 'left') === 'left';
        const curPlaceholder = field.placeholder || '0.00';
        const curMin = field.min !== undefined && field.min !== '' ? ` min="${field.min}"` : '';
        const curMax = field.max !== undefined && field.max !== '' ? ` max="${field.max}"` : '';
        const curStep = field.step || '0.01';
        fieldHtml += `
          <div class="text-fields sf-currency-wrap ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <div class="sf-currency-container" style="display:flex;align-items:stretch;border:1px solid #d1d5db;border-radius:6px;overflow:hidden;background:#fff;">
              ${curPos ? `<span class="sf-currency-symbol" style="padding:8px 12px;background:#f3f4f6;color:#374151;font-size:14px;font-weight:600;display:flex;align-items:center;border-right:1px solid #e5e7eb;">${curSymbol}</span>` : ''}
              <input
                id="${field.id}"
                type="number"
                step="${curStep}"
                name="${field.name}"
                placeholder="${curPlaceholder}"
                class="${field.className || ''}"
                value="${field.value || ''}"
                data-field-id="${fieldId}"${curMin}${curMax}${fieldAttributes}
                style="flex:1;padding:8px 10px;border:none;outline:none;font-size:14px;width:100%;box-sizing:border-box;">
              ${!curPos ? `<span class="sf-currency-symbol" style="padding:8px 12px;background:#f3f4f6;color:#374151;font-size:14px;font-weight:600;display:flex;align-items:center;border-left:1px solid #e5e7eb;">${curSymbol}</span>` : ''}
            </div>
          </div>
        `;
        break;

      case 'number':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
           ${getLabel(field)}
            <input id="${field.id}" type="${field.type}" name="${field.name}" min="${field.min || ''}" max="${field.max || ''}" placeholder="${field.placeholder || ''}" class="${field.className || ''}" value="${field.value || ''}" data-field-id="${fieldId}"${fieldAttributes}>
          </div>
        `;
        break;

      // Handle standard input types
      case 'text':
      case 'number':
      case 'email':
      case 'button':
      case 'color':
      case 'date':
      case 'datetime-local':
      case 'month':
      case 'password':
      case 'range':
      case 'reset':
      case 'search':
      case 'tel':
      case 'time':
      case 'url':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <input id="${field.id}" type="${field.type}" name="${field.name}" placeholder="${field.placeholder || ''}" class="${field.className}" value="${field.value || ''}" data-field-id="${fieldId}"${fieldAttributes}>
          </div>
        `;
        break;

      case 'section_break':
        const sbShowDivider = field.showDivider !== false && field.showDivider !== 'false';
        const sbShowLabel = field.showLabel !== false && field.showLabel !== 'false';
        const sbThickness = field.dividerThickness || '1';
        const sbStyle = field.dividerStyle || 'solid';
        const sbColor = field.dividerColor || '#e5e7eb';
        const sbTitle = field.label || '';
        const sbDesc = field.description || field.subtitle || '';
        const sbSecStyle = field.sectionStyle || 'classic'; // classic, banner, accent
        
        let sbInnerHtml = '';
        if (sbSecStyle === 'banner') {
          sbInnerHtml = `
            <div style="background:#f8fafc;border:1px solid #e2e8f0;border-left:4px solid #4f46e5;border-radius:6px;padding:14px 18px;">
              ${sbShowLabel && sbTitle ? `<div style="font-size:16px;font-weight:700;color:#1e293b;margin-bottom:${sbDesc ? '4px' : '0'};">${sbTitle}</div>` : ''}
              ${sbDesc ? `<div style="font-size:13px;color:#64748b;line-height:1.5;">${sbDesc}</div>` : ''}
            </div>
          `;
        } else if (sbSecStyle === 'accent') {
          sbInnerHtml = `
            <div style="padding:4px 0 4px 14px;border-left:4px solid #3b82f6;">
              ${sbShowLabel && sbTitle ? `<div style="font-size:17px;font-weight:700;color:#0f172a;margin-bottom:${sbDesc ? '4px' : '0'};">${sbTitle}</div>` : ''}
              ${sbDesc ? `<div style="font-size:13px;color:#64748b;line-height:1.5;">${sbDesc}</div>` : ''}
            </div>
            ${sbShowDivider ? `<hr class="sf-section-break-divider" style="border:none;border-top:${sbThickness}px ${sbStyle} ${sbColor};margin:14px 0 6px 0;width:100%;">` : ''}
          `;
        } else {
          // Classic
          sbInnerHtml = `
            ${sbShowLabel && sbTitle ? `<div class="sf-section-break-label" style="font-size:18px;font-weight:700;color:#111827;letter-spacing:-0.2px;margin-bottom:${sbDesc ? '4px' : '8px'};">${sbTitle}</div>` : ''}
            ${sbDesc ? `<div class="sf-section-break-desc" style="font-size:13px;color:#6b7280;line-height:1.5;margin-bottom:10px;">${sbDesc}</div>` : ''}
            ${sbShowDivider ? `<hr class="sf-section-break-divider" style="border:none;border-top:${sbThickness}px ${sbStyle} ${sbColor};margin:8px 0 14px 0;width:100%;">` : ''}
          `;
        }

        fieldHtml += `
          <div class="text-fields sf-section-break-wrap ${conditionalClass}" ${conditionalAttributes} style="flex:0 0 100% !important;width:100% !important;max-width:100% !important;margin:20px 0 10px 0;clear:both !important;" data-field-type="section_break" data-field-id="${fieldId}">
            ${sbInnerHtml}
          </div>
        `;
        break;

      case 'spacer':
        const spHeight = field.spacerHeight || '20';
        fieldHtml += `
          <div class="sf-spacer-wrap ${conditionalClass}" ${conditionalAttributes} style="height:${spHeight}px;width:100%;display:block;" data-spacer-height="${spHeight}">
          </div>
        `;
        break;

      case 'toggle':
        const tglIsChecked = field.value === 'true' || field.value === true;
        const tglCheckedLabel = field.checkedLabel || 'On';
        const tglUncheckedLabel = field.uncheckedLabel || 'Off';
        fieldHtml += `
          <div class="text-fields sf-toggle-wrap ${conditionalClass}" ${conditionalAttributes}>
            <label for="${field.id}" class="sf-toggle-label" style="display:flex;align-items:center;gap:10px;cursor:pointer;">
              <span class="sf-toggle-field-label" style="font-size:13px;color:#374151;font-weight:500;">
                ${field.label}${field.required === true || field.required === "true" ? '<span class="required-indicator">*</span>' : ''}
              </span>
              <div class="sf-toggle-switch" style="position:relative;display:inline-block;width:44px;height:24px;">
                <input
                  type="checkbox"
                  id="${field.id}"
                  name="${field.name}"
                  class="sf-toggle-input"
                  value="true"
                  data-field-id="${fieldId}"
                  data-unchecked-label="${tglUncheckedLabel}"
                  data-checked-label="${tglCheckedLabel}"
                  data-true-value="true"
                  data-false-value="false"
                  ${tglIsChecked ? 'checked' : ''}${fieldAttributes}
                  style="position:absolute;top:0;left:0;width:100%;height:100%;opacity:0;cursor:pointer;z-index:2;margin:0;padding:0;">
                <span class="sf-toggle-slider" style="position:absolute;top:0;left:0;right:0;bottom:0;background-color:${tglIsChecked ? '#10b981' : '#d1d5db'};border-radius:24px;transition:0.2s;z-index:1;">
                  <span class="sf-toggle-knob" style="position:absolute;content:'';height:18px;width:18px;left:${tglIsChecked ? '24px' : '3px'};bottom:3px;background-color:white;border-radius:50%;transition:0.2s;"></span>
                </span>
              </div>
              <span class="sf-toggle-status" style="font-size:12px;color:#6b7280;">${tglIsChecked ? tglCheckedLabel : tglUncheckedLabel}</span>
            </label>
          </div>
        `;
        break;

      case 'gdpr':
        const gdprAgreement = field.agreementText || 'I consent to having this website store my submitted information so they can respond to my inquiry.';
        const gdprLinkText = field.linkText || 'Privacy Policy';
        const gdprLinkUrl = field.linkUrl || '';
        const gdprTarget = (field.openInNewTab === false || field.openInNewTab === 'false') ? '_self' : '_blank';
        fieldHtml += `
          <div class="text-fields sf-gdpr-wrap ${conditionalClass}" ${conditionalAttributes}>
            <label for="${field.id}" class="sf-gdpr-label" style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-size:13px;line-height:1.5;color:#374151;">
              <input
                type="checkbox"
                id="${field.id}"
                name="${field.name}"
                class="sf-gdpr-input"
                value="agreed"
                data-field-id="${fieldId}"${fieldAttributes}
                style="margin-top:3px;">
              <span>
                ${gdprAgreement}
                ${gdprLinkUrl ? ` <a href="${gdprLinkUrl}" target="${gdprTarget}" rel="noopener noreferrer" style="color:#3b82f6;text-decoration:underline;">${gdprLinkText}</a>` : ''}
              </span>
            </label>
          </div>
        `;
        break;

      case 'terms_conditions':
        const tcHeight = field.termsHeight || '150';
        const tcCollapsed = field.collapseByDefault === true || field.collapseByDefault === 'true';
        const tcContent = field.termsContent || 'This is where your Terms & Conditions text will appear. You can edit this text in the field settings.';
        const tcCheckboxLabel = field.checkboxLabel || 'I agree to the Terms & Conditions';
        fieldHtml += `
          <div class="text-fields sf-terms-wrap ${conditionalClass}" ${conditionalAttributes} data-terms-id="${field.id}" style="margin-bottom:8px;">
            <div class="sf-terms-box" style="border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;margin-bottom:8px;background-color:#f9fafb;">
              <div class="sf-terms-header" style="padding:10px 12px;border-bottom:${tcCollapsed ? 'none' : '1px solid #e5e7eb'};background-color:#f3f4f6;font-size:13px;font-weight:500;color:#374151;display:flex;align-items:center;gap:6px;cursor:pointer;">
                <span>📄</span>
                <span class="sf-terms-title">Terms &amp; Conditions</span>
                <span class="sf-terms-hint" style="font-size:11px;color:#6b7280;margin-left:auto;">${tcCollapsed ? 'Click to expand' : 'Scrollable'}</span>
                <span class="sf-terms-chevron" style="font-size:10px;transition:transform 0.2s;transform:rotate(${tcCollapsed ? '0deg' : '180deg'});">▼</span>
              </div>
              <div class="sf-terms-content" style="${tcCollapsed ? 'display:none;' : ''}padding:10px 12px;max-height:${tcHeight}px;overflow-y:auto;font-size:12px;line-height:1.6;color:#4b5563;">
                ${tcContent}
              </div>
            </div>
            <label for="${field.id}" class="sf-terms-checkbox-label" style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-size:13px;color:#374151;">
              <input
                type="checkbox"
                id="${field.id}"
                name="${field.name}"
                class="sf-terms-input"
                value="agreed"
                data-field-id="${fieldId}"${fieldAttributes}
                style="margin-top:3px;">
              <span>${tcCheckboxLabel}${field.required === true || field.required === "true" ? '<span class="required-indicator">*</span>' : ''}</span>
            </label>
          </div>
        `;
        break;

      case 'currency':
        const currSymbol = field.currencySymbol || '$';
        const currPosLeft = (field.currencyPosition || 'left') === 'left';
        const currDecimal = field.decimalPlaces || '2';
        const currDecimalSep = field.decimalSeparator || '.';
        const currThousandSep = field.thousandSeparator || ',';
        fieldHtml += `
          <div class="text-fields sf-currency-wrap ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <div class="sf-currency-input-group" style="display:flex;align-items:stretch;border:1px solid #d1d5db;border-radius:6px;overflow:hidden;background-color:#fff;">
              ${currPosLeft ? `<span class="sf-currency-symbol sf-currency-left" style="padding:0 10px;background-color:#f3f4f6;display:flex;align-items:center;font-size:14px;color:#374151;border-right:1px solid #e5e7eb;font-weight:500;">${currSymbol}</span>` : ''}
              <input
                id="${field.id}"
                type="text"
                inputmode="decimal"
                name="${field.name}"
                placeholder="${field.placeholder || '0.00'}"
                class="sf-currency-input ${field.className}"
                value="${field.value || ''}"
                data-field-id="${fieldId}"
                data-symbol="${currSymbol}"
                data-position="${currPosLeft ? 'left' : 'right'}"
                data-decimal-places="${currDecimal}"
                data-decimal-sep="${currDecimalSep}"
                data-thousand-sep="${currThousandSep}"
                data-min="${field.min || ''}"
                data-max="${field.max || ''}"${fieldAttributes}>
              ${!currPosLeft ? `<span class="sf-currency-symbol sf-currency-right" style="padding:0 10px;background-color:#f3f4f6;display:flex;align-items:center;font-size:14px;color:#374151;border-left:1px solid #e5e7eb;font-weight:500;">${currSymbol}</span>` : ''}
            </div>
          </div>
        `;
        break;

      case 'phone':
        const phDialCode = field.dialCode !== false;
        const phDefaultCountry = field.defaultCountry || 'us';
        const phValidation = field.validation || 'none';
        fieldHtml += `
          <div class="text-fields sf-phone-wrap sf-phone-group ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <div class="sf-phone-input-group" style="display:flex;align-items:stretch;border:1px solid #d1d5db;border-radius:6px;overflow:hidden;background-color:#fff;">
              ${phDialCode ? `
                <select class="sf-phone-dialcode" data-default-country="${phDefaultCountry}" style="padding:0 8px;background-color:#f3f4f6;border:none;border-right:1px solid #e5e7eb;cursor:pointer;font-size:13px;color:#374151;font-weight:500;min-width:90px;">
                  <option value="">Select...</option>
                </select>` : ''}
              <input
                id="${field.id}"
                type="tel"
                name="${field.name}"
                placeholder="${field.placeholder || 'Enter phone number...'}"
                class="sf-phone-input ${field.className}"
                value="${field.value || ''}"
                data-field-id="${fieldId}"
                data-dial-code="${phDialCode ? 'true' : 'false'}"
                data-default-country="${phDefaultCountry}"
                data-validation="${phValidation}"${fieldAttributes}>
            </div>
          </div>
        `;
        break;

      case 'name':
        const nameSubs = field.subfields || {};
        const nameLayout = field.layout || 'two_columns';
        let nameStyle = '';
        
        // Determine layout style based on configuration
        if (nameLayout === 'single') {
          nameStyle = 'display:flex;flex-direction:column;gap:10px;';
        } else if (nameLayout === 'two_columns') {
          nameStyle = 'display:grid;grid-template-columns:1fr 1fr;gap:10px;';
        } else if (nameLayout === 'full') {
          nameStyle = 'display:grid;grid-template-columns:repeat(auto-fit, minmax(150px, 1fr));gap:10px;';
        } else {
          nameStyle = 'display:flex;flex-direction:column;gap:10px;';
        }
        
        let nameHtml = '';
        
        // Prefix subfield
        if (nameSubs.prefix && (nameSubs.prefix.enabled === true || nameSubs.prefix.enabled === 'true')) {
          const prefixRequired = nameSubs.prefix.required === true || nameSubs.prefix.required === 'true';
          nameHtml += `<div class="sf-name-subfield" style="flex:1;"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${nameSubs.prefix.label || 'Prefix'}${prefixRequired ? '<span class="required-indicator">*</span>' : ''}</label><input type="text" class="custom-input" name="${field.name}[prefix]" placeholder="${nameSubs.prefix.placeholder || 'Mr.'}" data-field-id="${fieldId}__prefix"${prefixRequired ? ' required' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"></div>`;
        }
        
        // First Name subfield
        if (nameSubs.firstName && (nameSubs.firstName.enabled !== false)) {
          const firstNameRequired = nameSubs.firstName.required === true || nameSubs.firstName.required === 'true' || nameSubs.firstName.required === undefined;
          nameHtml += `<div class="sf-name-subfield" style="flex:1;"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${nameSubs.firstName.label || 'First Name'}${firstNameRequired ? '<span class="required-indicator">*</span>' : ''}</label><input type="text" class="custom-input" name="${field.name}[firstName]" placeholder="${nameSubs.firstName.placeholder || 'First'}" data-field-id="${fieldId}__firstName"${firstNameRequired ? ' required' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"></div>`;
        }
        
        // Middle Name subfield — only show when explicitly enabled
        if (nameSubs.middleName && (nameSubs.middleName.enabled === true || nameSubs.middleName.enabled === 'true')) {
          const middleNameRequired = nameSubs.middleName.required === true || nameSubs.middleName.required === 'true';
          nameHtml += `<div class="sf-name-subfield" style="flex:1;"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${nameSubs.middleName.label || 'Middle Name'}${middleNameRequired ? '<span class="required-indicator">*</span>' : ''}</label><input type="text" class="custom-input" name="${field.name}[middleName]" placeholder="${nameSubs.middleName.placeholder || 'Middle'}" data-field-id="${fieldId}__middleName"${middleNameRequired ? ' required' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"></div>`;
        }
        
        // Last Name subfield
        if (nameSubs.lastName && (nameSubs.lastName.enabled !== false)) {
          const lastNameRequired = nameSubs.lastName.required === true || nameSubs.lastName.required === 'true' || nameSubs.lastName.required === undefined;
          nameHtml += `<div class="sf-name-subfield" style="flex:1;"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${nameSubs.lastName.label || 'Last Name'}${lastNameRequired ? '<span class="required-indicator">*</span>' : ''}</label><input type="text" class="custom-input" name="${field.name}[lastName]" placeholder="${nameSubs.lastName.placeholder || 'Last'}" data-field-id="${fieldId}__lastName"${lastNameRequired ? ' required' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"></div>`;
        }
        
        // Suffix subfield
        if (nameSubs.suffix && (nameSubs.suffix.enabled === true || nameSubs.suffix.enabled === 'true')) {
          const suffixRequired = nameSubs.suffix.required === true || nameSubs.suffix.required === 'true';
          nameHtml += `<div class="sf-name-subfield" style="flex:1;"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${nameSubs.suffix.label || 'Suffix'}${suffixRequired ? '<span class="required-indicator">*</span>' : ''}</label><input type="text" class="custom-input" name="${field.name}[suffix]" placeholder="${nameSubs.suffix.placeholder || 'Jr.'}" data-field-id="${fieldId}__suffix"${suffixRequired ? ' required' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"></div>`;
        }
        
        fieldHtml += `
          <div class="text-fields sf-name-wrap ${conditionalClass}" ${conditionalAttributes} data-field-type="name">
            ${getLabel(field)}
            <div class="sf-name-row" style="${nameStyle}">
              ${nameHtml}
            </div>
          </div>
        `;
        break;

      case 'address':
        const addrSubs = field.subfields || {};
        let addrHtml = '';
        if (addrSubs.street && (addrSubs.street.enabled === true || addrSubs.street.enabled === 'true' || addrSubs.street.enabled === undefined)) {
          addrHtml += `<div class="sf-addr-subfield" style="margin-bottom:10px;"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${addrSubs.street.label || 'Street Address'}${addrSubs.street.required ? '<span class="required-indicator">*</span>' : ''}</label><input type="text" class="custom-input" name="${field.name}[street]" placeholder="${addrSubs.street.placeholder || ''}" data-field-id="${fieldId}__street"${addrSubs.street.required ? ' required' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"></div>`;
        }
        if (addrSubs.street2 && (addrSubs.street2.enabled === true || addrSubs.street2.enabled === 'true')) {
          addrHtml += `<div class="sf-addr-subfield" style="margin-bottom:10px;"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${addrSubs.street2.label || 'Address Line 2'}${addrSubs.street2.required ? '<span class="required-indicator">*</span>' : ''}</label><input type="text" class="custom-input" name="${field.name}[street2]" placeholder="${addrSubs.street2.placeholder || ''}" data-field-id="${fieldId}__street2"${addrSubs.street2.required ? ' required' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"></div>`;
        }
        addrHtml += `<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">`;
        if (addrSubs.city && (addrSubs.city.enabled === true || addrSubs.city.enabled === 'true' || addrSubs.city.enabled === undefined)) {
          addrHtml += `<div class="sf-addr-subfield"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${addrSubs.city.label || 'City'}${addrSubs.city.required ? '<span class="required-indicator">*</span>' : ''}</label><input type="text" class="custom-input" name="${field.name}[city]" placeholder="${addrSubs.city.placeholder || ''}" data-field-id="${fieldId}__city"${addrSubs.city.required ? ' required' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"></div>`;
        }
        if (addrSubs.state && (addrSubs.state.enabled === true || addrSubs.state.enabled === 'true' || addrSubs.state.enabled === undefined)) {
          addrHtml += `<div class="sf-addr-subfield"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${addrSubs.state.label || 'State'}${addrSubs.state.required ? '<span class="required-indicator">*</span>' : ''}</label><input type="text" class="custom-input" name="${field.name}[state]" placeholder="${addrSubs.state.placeholder || ''}" data-field-id="${fieldId}__state"${addrSubs.state.required ? ' required' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"></div>`;
        }
        addrHtml += `</div>`;
        addrHtml += `<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">`;
        if (addrSubs.zip && (addrSubs.zip.enabled === true || addrSubs.zip.enabled === 'true' || addrSubs.zip.enabled === undefined)) {
          addrHtml += `<div class="sf-addr-subfield"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${addrSubs.zip.label || 'ZIP'}${addrSubs.zip.required ? '<span class="required-indicator">*</span>' : ''}</label><input type="text" class="custom-input" name="${field.name}[zip]" placeholder="${addrSubs.zip.placeholder || ''}" data-field-id="${fieldId}__zip"${addrSubs.zip.required ? ' required' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"></div>`;
        }
        if (addrSubs.country && (addrSubs.country.enabled === true || addrSubs.country.enabled === 'true' || addrSubs.country.enabled === undefined)) {
          const countryDefault = addrSubs.country.default || 'United States';
          addrHtml += `<div class="sf-addr-subfield"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${addrSubs.country.label || 'Country'}${addrSubs.country.required ? '<span class="required-indicator">*</span>' : ''}</label><select class="custom-input sf-country-select" name="${field.name}[country]" data-field-id="${fieldId}__country"${addrSubs.country.required ? ' required' : ''} data-default="${countryDefault}" style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"><option value="">Select country...</option></select></div>`;
        }
        addrHtml += `</div>`;
        fieldHtml += `
          <div class="text-fields sf-address-wrap ${conditionalClass}" ${conditionalAttributes} data-field-type="address">
            ${getLabel(field)}
            <div class="sf-address-row">
              ${addrHtml}
            </div>
          </div>
        `;
        break;

      case 'country':
        const ctryDefault = field.defaultCountry || 'United States';
        const ctryShowFlags = field.showFlags !== false;
        const ctryPlaceholder = field.placeholder || 'Select a country...';
        fieldHtml += `
          <div class="text-fields sf-country-wrap ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <select
              id="${field.id}"
              name="${field.name}"
              class="sf-country-dropdown ${field.className}"
              data-field-id="${fieldId}"
              data-default="${ctryDefault}"
              data-show-flags="${ctryShowFlags ? 'true' : 'false'}"
              placeholder="${ctryPlaceholder}"${fieldAttributes}>
              <option value="">${ctryPlaceholder}</option>
            </select>
          </div>
        `;
        break;

      case 'richtext':
        const rteToolbar = field.toolbarButtons || ['bold', 'italic', 'underline', 'link', 'bulletList', 'numberList'];
        const rteMediaUpload = field.mediaUpload !== false;
        let toolbarHtml = '';
        const toolbarBtns = {
          bold: { cmd: 'bold', icon: 'B', title: 'Bold', style: 'font-weight:bold;' },
          italic: { cmd: 'italic', icon: 'I', title: 'Italic', style: 'font-style:italic;' },
          underline: { cmd: 'underline', icon: 'U', title: 'Underline', style: 'text-decoration:underline;' },
          link: { cmd: 'createLink', icon: '🔗', title: 'Link' },
          bulletList: { cmd: 'insertUnorderedList', icon: '•', title: 'Bullet List' },
          numberList: { cmd: 'insertOrderedList', icon: '1.', title: 'Numbered List' },
        };
        rteToolbar.forEach(function (btn) {
          if (toolbarBtns[btn]) {
            toolbarHtml += `<button type="button" class="sf-rte-btn sf-richtext-btn" data-cmd="${toolbarBtns[btn].cmd}" title="${toolbarBtns[btn].title}" style="padding:4px 10px;border:1px solid #e5e7eb;background:#fff;cursor:pointer;border-radius:4px;font-size:13px;${toolbarBtns[btn].style || ''}min-width:30px;">${toolbarBtns[btn].icon}</button>`;
          }
        });
        fieldHtml += `
          <div class="text-fields sf-richtext-wrap sf-richtext-editor ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <div class="sf-richtext-container" style="border:1px solid #d1d5db;border-radius:6px;overflow:hidden;background:#fff;" data-field-id="${fieldId}">
              <div class="sf-rte-toolbar" style="padding:6px 8px;background:#f9fafb;border-bottom:1px solid #e5e7eb;display:flex;flex-wrap:wrap;gap:4px;">
                ${toolbarHtml}
                ${rteMediaUpload ? `<button type="button" class="sf-rte-btn sf-richtext-btn sf-rte-media" title="Add media" style="padding:4px 10px;border:1px solid #e5e7eb;background:#fff;cursor:pointer;border-radius:4px;font-size:13px;">🖼️</button>` : ''}
              </div>
              <div
                id="${field.id}"
                name="${field.name}"
                class="sf-rte-content sf-richtext-content ${field.className}"
                contenteditable="true"
                data-field-id="${fieldId}"
                data-placeholder="${field.placeholder || 'Type your content here...'}"${fieldAttributes}
                style="min-height:120px;padding:12px;outline:none;font-size:14px;line-height:1.6;color:#374151;">
                ${field.value || ''}
              </div>
              <input type="hidden" name="${field.name}" class="sf-rte-hidden sf-richtext-value" value="${field.value || ''}">
            </div>
          </div>
        `;
        break;

      case 'star-rating':
        const srMaxStars = parseInt(field.maxStars) || 5;
        const srActiveColor = field.activeColor || '#FFD700';
        const srInactiveColor = field.inactiveColor || '#d1d5db';
        const srHoverColor = field.hoverColor || '#fbbf24';
        const srShowLabels = field.showLabelInline === true;
        const srLabels = field.labels || [];
        let srStarsHtml = '';
        for (let i = 1; i <= srMaxStars; i++) {
          srStarsHtml += `
            <span class="sf-star-item sf-star" data-value="${i}"
              style="font-size:28px;cursor:pointer;transition:color 0.15s;color:${srInactiveColor};user-select:none;line-height:1;display:inline-block;"
              data-active-color="${srActiveColor}"
              data-inactive-color="${srInactiveColor}"
              data-hover-color="${srHoverColor}"
              data-label="${srLabels[i] || ''}">★</span>
          `;
        }
        fieldHtml += `
          <div class="text-fields sf-star-rating-wrap sf-star-rating-group ${conditionalClass}" ${conditionalAttributes} data-field-type="star-rating">
            ${getLabel(field)}
            <div class="sf-star-rating-container" style="display:flex;align-items:center;gap:4px;" data-field-id="${fieldId}" data-max="${srMaxStars}">
              ${srStarsHtml}
            </div>
            <input type="hidden" name="${field.name}" class="sf-star-rating-value" value="${field.value || ''}" data-field-id="${fieldId}">
            ${srShowLabels ? `<div class="sf-star-rating-label" style="font-size:12px;color:#6b7280;margin-top:4px;min-height:16px;"></div>` : ''}
          </div>
        `;
        break;

      case 'nps':
        const npsMin = parseInt(field.minScore) || 0;
        const npsMax = parseInt(field.maxScore) || 10;
        const npsMinLabel = field.minLabel || 'Not likely';
        const npsMaxLabel = field.maxLabel || 'Very likely';
        const npsQuestion = field.question || '';
        const npsShowLabels = field.showLikelyLabels !== false;
        let npsButtonsHtml = '';
        for (let n = npsMin; n <= npsMax; n++) {
          const colorGroup = n <= 6 ? '#ef4444' : (n <= 8 ? '#f59e0b' : '#22c55e');
          npsButtonsHtml += `<button type="button" class="sf-nps-btn" data-value="${n}" style="min-width:40px;height:40px;border:2px solid #e5e7eb;background:#fff;border-radius:6px;cursor:pointer;font-size:14px;font-weight:600;color:#374151;transition:all 0.15s;" data-active-bg="${colorGroup}">${n}</button>`;
        }
        fieldHtml += `
          <div class="text-fields sf-nps-wrap sf-nps-group ${conditionalClass}" ${conditionalAttributes} data-field-type="nps">
            ${getLabel(field)}
            ${npsQuestion ? `<div class="sf-nps-question" style="font-size:14px;color:#374151;margin-bottom:10px;font-weight:500;">${npsQuestion}</div>` : ''}
            <div class="sf-nps-buttons" style="display:flex;flex-wrap:wrap;gap:6px;" data-field-id="${fieldId}" data-min="${npsMin}" data-max="${npsMax}">
              ${npsButtonsHtml}
            </div>
            ${npsShowLabels ? `<div style="display:flex;justify-content:space-between;font-size:11px;color:#6b7280;margin-top:6px;"><span>${npsMinLabel}</span><span>${npsMaxLabel}</span></div>` : ''}
            <input type="hidden" name="${field.name}" class="sf-nps-value" value="${field.value || ''}" data-field-id="${fieldId}">
          </div>
        `;
        break;

      case 'likert':
        const lkRows = field.rows || [];
        const lkCols = field.columns || [];
        let lkHeaderHtml = `<th style="padding:8px 10px;background:#f9fafb;font-size:12px;color:#374151;text-align:center;border:1px solid #e5e7eb;font-weight:600;"></th>`;
        lkCols.forEach(function (col) {
          lkHeaderHtml += `<th style="padding:8px 10px;background:#f9fafb;font-size:12px;color:#374151;text-align:center;border:1px solid #e5e7eb;font-weight:600;min-width:70px;">${col.label}</th>`;
        });
        let lkBodyHtml = '';
        lkRows.forEach(function (row, rIdx) {
          lkBodyHtml += `<tr class="sf-likert-row" data-row-id="${row.id}"><td style="padding:8px 10px;border:1px solid #e5e7eb;font-size:13px;color:#374151;font-weight:500;">${row.label}</td>`;
          lkCols.forEach(function (col, cIdx) {
            lkBodyHtml += `<td style="padding:8px 10px;border:1px solid #e5e7eb;text-align:center;">
              <input type="radio" name="${field.name}[${row.id}]" value="${col.value}" data-field-id="${fieldId}__${rIdx}_${cIdx}" style="cursor:pointer;width:16px;height:16px;">
            </td>`;
          });
          lkBodyHtml += `</tr>`;
        });
        fieldHtml += `
          <div class="text-fields sf-likert-wrap ${conditionalClass}" ${conditionalAttributes} data-field-type="likert">
            ${getLabel(field)}
            <div style="overflow-x:auto;">
              <table class="sf-likert-table" data-field-id="${fieldId}" data-base-name="${field.name}" style="border-collapse:collapse;width:100%;background:#fff;">
                <thead><tr>${lkHeaderHtml}</tr></thead>
                <tbody>${lkBodyHtml}</tbody>
              </table>
            </div>
          </div>
        `;
        break;

      case 'image-select':
        const isChoiceSingle = field.choiceType !== 'multiple';
        const imgW = field.imageWidth || 120;
        const imgH = field.imageHeight || 90;
        const imgShowLabels = field.showLabels !== false;
        const imgOptions = field.options || [];
        let imgChoicesHtml = '';
        imgOptions.forEach(function (opt, idx) {
          const inputType = isChoiceSingle ? 'radio' : 'checkbox';
          const inputName = isChoiceSingle ? field.name : `${field.name}[]`;
          // Validate image URL - if invalid, use data URI placeholder
          const imgUrl = (opt.image && opt.image.startsWith('http')) ? opt.image : `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iOTAiIGZpbGw9IiNlNWU3ZWIiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzZiNzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuKAuTwvdGV4dD48L3N2Zz4=`;
          imgChoicesHtml += `
            <label class="sf-image-choice-item" data-option-value="${opt.value}" style="display:inline-flex;flex-direction:column;align-items:center;gap:6px;cursor:pointer;padding:8px;border:2px solid #e5e7eb;border-radius:8px;background:#fff;transition:all 0.15s;min-width:${+imgW + 20}px;">
              <input type="${inputType}" name="${inputName}" value="${opt.value}" class="sf-image-choice-input" data-field-id="${fieldId}__${idx}" style="display:none;">
              <img src="${imgUrl}" alt="${opt.label}" style="width:${imgW}px;height:${imgH}px;object-fit:cover;border-radius:6px;display:block;background:#f3f4f6;">
              ${imgShowLabels ? `<span style="font-size:12px;color:#374151;font-weight:500;line-height:1.3;">${opt.label}</span>` : ''}
            </label>
          `;
        });
        fieldHtml += `
          <div class="text-fields sf-image-select-wrap sf-image-select-group ${conditionalClass}" ${conditionalAttributes} data-field-type="image-select">
            ${getLabel(field)}
            <div class="sf-image-choice-grid" style="display:flex;flex-wrap:wrap;gap:10px;" data-field-id="${fieldId}">
              ${imgChoicesHtml}
            </div>
          </div>
        `;
        break;

      case 'password_confirm':
        const pcShowStrength = field.showStrengthMeter !== false;
        const pcMinLen = field.minLength || 8;
        const pcPlaceholder = field.placeholder || 'Enter password...';
        const pcConfirmPlaceholder = field.confirmPlaceholder || 'Confirm password...';
        const pcPwdLabel = field.passwordLabel || 'Password';
        const pcConfLabel = field.confirmLabel || 'Confirm Password';
        const pcReqUpper = field.requireUppercase !== false;
        const pcReqLower = field.requireLowercase !== false;
        const pcReqNum = field.requireNumbers !== false;
        const pcReqSym = field.requireSymbols === true;
        fieldHtml += `
          <div class="text-fields sf-password-confirm-wrap sf-password-confirm-group ${conditionalClass}" ${conditionalAttributes} data-field-type="password_confirm">
            <div style="margin-bottom:10px;">
              <label style="font-size:13px;color:#374151;font-weight:500;margin-bottom:4px;display:block;">${pcPwdLabel}${field.required === true || field.required === "true" ? '<span class="required-indicator">*</span>' : ''}</label>
              <input
                type="password"
                class="sf-pwd-input sf-password-input custom-input"
                id="${field.id}"
                name="${field.name}[password]"
                placeholder="${pcPlaceholder}"
                data-field-id="${fieldId}__pwd"
                data-min-length="${pcMinLen}"
                data-require-upper="${pcReqUpper}"
                data-require-lower="${pcReqLower}"
                data-require-number="${pcReqNum}"
                data-require-symbol="${pcReqSym}"${fieldAttributes}
                style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">
            </div>
            ${pcShowStrength ? `
              <div style="margin-bottom:10px;">
                <div class="sf-pwd-strength-bar sf-password-strength-bar" style="height:4px;background:#e5e7eb;border-radius:2px;overflow:hidden;">
                  <div class="sf-pwd-strength-fill" style="height:100%;width:0%;background:#ef4444;transition:width 0.2s, background-color 0.2s;"></div>
                </div>
                <div class="sf-pwd-strength-text sf-password-strength-label" style="font-size:11px;color:#6b7280;margin-top:3px;min-height:14px;"></div>
              </div>
            ` : ''}
            <div>
              <label style="font-size:13px;color:#374151;font-weight:500;margin-bottom:4px;display:block;">${pcConfLabel}${field.required === true || field.required === "true" ? '<span class="required-indicator">*</span>' : ''}</label>
              <input
                type="password"
                class="sf-pwd-confirm-input sf-password-confirm-input custom-input"
                name="${field.name}[confirm]"
                placeholder="${pcConfirmPlaceholder}"
                data-field-id="${fieldId}__confirm"
                data-match-id="${field.id}"
                style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">
              <div class="sf-pwd-match-text sf-password-match-label" style="font-size:11px;color:#6b7280;margin-top:3px;min-height:14px;"></div>
            </div>
            <input type="hidden" name="${field.name}" class="sf-password-combined-value" value="">
          </div>
        `;
        break;

      case 'repeater':
        const rpMinRows = parseInt(field.minRows) || 1;
        const rpMaxRows = parseInt(field.maxRows) || 10;
        const rpAddLabel = field.addButtonLabel || 'Add Row';
        const rpRemoveLabel = field.removeButtonLabel || 'Remove';
        const rpShowNums = field.showRowNumbers !== false;
        const rpRowLayout = field.rowLayout || 'horizontal';
        const rpSubfields = field.subfields || [];
        
        // Determine row layout style
        let rpRowStyle = '';
        if (rpRowLayout === 'vertical') {
          rpRowStyle = 'display:flex;flex-direction:column;gap:10px;';
        } else if (rpRowLayout === 'grid') {
          rpRowStyle = 'display:grid;grid-template-columns:1fr 1fr;gap:10px;';
        } else if (rpRowLayout === 'grid-3') {
          rpRowStyle = 'display:grid;grid-template-columns:repeat(3,1fr);gap:10px;';
        } else {
          rpRowStyle = 'display:flex;flex-wrap:wrap;gap:10px;';
        }
        
        let rpRowTemplateHtml = '';
        rpSubfields.forEach(function (sf, sfIdx) {
          const sfRequired = sf.required === true || sf.required === 'true';
          const sfId = sf.id || 'field-' + sfIdx;
          const sfName = sf.name || 'field-' + sfIdx;
          
          // Render different input types based on subfield type
          let sfInputHtml = '';
          if (sf.type === 'textarea') {
            sfInputHtml = `<textarea class="custom-input sf-rep-subinput" placeholder="${sf.placeholder || ''}"${sfRequired ? ' required' : ''} data-sf-id="${sfId}" data-sf-name="${sfName}" style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;min-height:60px;"></textarea>`;
          } else if (sf.type === 'select') {
            let sfOptionsHtml = '';
            if (sf.options && Array.isArray(sf.options)) {
              sf.options.forEach(function (opt) {
                sfOptionsHtml += `<option value="${opt}">${opt}</option>`;
              });
            }
            sfInputHtml = `<select class="custom-input sf-rep-subinput"${sfRequired ? ' required' : ''} data-sf-id="${sfId}" data-sf-name="${sfName}" style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;"><option value="">Select...</option>${sfOptionsHtml}</select>`;
          } else if (sf.type === 'number') {
            sfInputHtml = `<input type="number" class="custom-input sf-rep-subinput" placeholder="${sf.placeholder || ''}"${sfRequired ? ' required' : ''} data-sf-id="${sfId}" data-sf-name="${sfName}" style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">`;
          } else if (sf.type === 'email') {
            sfInputHtml = `<input type="email" class="custom-input sf-rep-subinput" placeholder="${sf.placeholder || ''}"${sfRequired ? ' required' : ''} data-sf-id="${sfId}" data-sf-name="${sfName}" style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">`;
          } else if (sf.type === 'tel') {
            sfInputHtml = `<input type="tel" class="custom-input sf-rep-subinput" placeholder="${sf.placeholder || ''}"${sfRequired ? ' required' : ''} data-sf-id="${sfId}" data-sf-name="${sfName}" style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">`;
          } else if (sf.type === 'date') {
            sfInputHtml = `<input type="date" class="custom-input sf-rep-subinput"${sfRequired ? ' required' : ''} data-sf-id="${sfId}" data-sf-name="${sfName}" style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">`;
          } else {
            sfInputHtml = `<input type="text" class="custom-input sf-rep-subinput" placeholder="${sf.placeholder || ''}"${sfRequired ? ' required' : ''} data-sf-id="${sfId}" data-sf-name="${sfName}" style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">`;
          }
          
          rpRowTemplateHtml += `<div style="flex:1;min-width:140px;"><label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${sf.label || 'Field ' + (sfIdx + 1)}${sfRequired ? '<span class="required-indicator">*</span>' : ''}</label>${sfInputHtml}</div>`;
        });
        
        fieldHtml += `
          <div class="text-fields sf-repeater-wrap sf-repeater-group ${conditionalClass}" ${conditionalAttributes} data-field-type="repeater" data-field-id="${fieldId}" data-name="${field.name}" data-min="${rpMinRows}" data-max="${rpMaxRows}" data-row-layout="${rpRowLayout}">
            ${getLabel(field)}
            <div class="sf-repeater-rows" style="display:flex;flex-direction:column;gap:10px;margin-bottom:10px;">
            </div>
            <template class="sf-repeater-row-template">
              <div class="sf-repeater-row" style="${rpRowStyle}padding:10px;border:1px solid #e5e7eb;border-radius:8px;background:#fafafa;position:relative;">
                ${rpShowNums ? `<div class="sf-repeater-row-num" style="position:absolute;top:-8px;left:8px;background:#3b82f6;color:#fff;font-size:10px;font-weight:600;padding:2px 6px;border-radius:8px;">1</div>` : ''}
                ${rpRowTemplateHtml}
                <button type="button" class="sf-repeater-remove-btn sf-repeater-remove" style="padding:4px 10px;background:#fee2e2;color:#ef4444;border:1px solid #fecaca;border-radius:4px;cursor:pointer;font-size:12px;font-weight:500;align-self:flex-end;margin-left:auto;">${rpRemoveLabel}</button>
              </div>
            </template>
            <button type="button" class="sf-repeater-add-btn" style="padding:8px 14px;background:#3b82f6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:500;">+ ${rpAddLabel}</button>
          </div>
        `;
        break;

      case 'range_output':
        const roMin = field.min !== undefined ? field.min : 0;
        const roMax = field.max !== undefined ? field.max : 100;
        const roStep = field.step || 1;
        const roValue = field.value !== undefined && field.value !== '' ? field.value : Math.round((+roMax - +roMin) / 2);
        const roPrefix = field.prefix || '';
        const roSuffix = field.suffix || '';
        const roOutPos = field.outputPosition || 'right';
        const roFillColor = field.fillColor || '#3b82f6';
        const roTrackColor = field.trackColor || '#e5e7eb';
        const roPct = ((roValue - roMin) / (roMax - roMin)) * 100;
        const outputSpan = `<span class="sf-range-output-value sf-range-output-badge" style="display:inline-flex;align-items:center;justify-content:center;min-width:50px;padding:4px 10px;background:${roFillColor};color:#fff;border-radius:4px;font-size:12px;font-weight:600;">${roPrefix}${roValue}${roSuffix}</span>`;
        fieldHtml += `
          <div class="text-fields sf-range-slider-wrap sf-range-output-group ${conditionalClass}" ${conditionalAttributes} data-field-type="range_output">
            ${getLabel(field)}
            <div style="display:flex;align-items:center;gap:12px;">
              ${roOutPos === 'left' ? outputSpan : ''}
              <div style="flex:1;position:relative;">
                <div class="sf-range-fill" style="position:absolute;left:0;top:50%;width:${roPct}%;height:6px;background:${roFillColor};border-radius:3px;transform:translateY(-50%);pointer-events:none;z-index:1;"></div>
                <input
                  id="${field.id}"
                  type="range"
                  name="${field.name}"
                  class="sf-range-slider-input sf-range-input ${field.className}"
                  min="${roMin}" max="${roMax}" step="${roStep}" value="${roValue}"
                  data-field-id="${fieldId}"
                  data-prefix="${roPrefix}"
                  data-suffix="${roSuffix}"
                  data-fill-color="${roFillColor}"
                  data-track-color="${roTrackColor}"${fieldAttributes}
                  style="width:100%;height:6px;background:${roTrackColor};border-radius:3px;-webkit-appearance:none;appearance:none;outline:none;position:relative;z-index:2;">
              </div>
              ${roOutPos !== 'left' ? outputSpan : ''}
            </div>
          </div>
        `;
        break;

      case 'email_otp':
        const otpLen = parseInt(field.otpLength) || 6;
        const otpExpiry = parseInt(field.expirySeconds) || 180;
        const otpSendLabel = field.sendButtonLabel || 'Send OTP';
        const otpVerifyLabel = field.verifyButtonLabel || 'Verify';
        const otpResendLabel = field.resendButtonLabel || 'Resend OTP';
        const otpPlaceholder = field.otpPlaceholder || 'Enter OTP';
        let otpDigitsHtml = '';
        for (let o = 0; o < otpLen; o++) {
          otpDigitsHtml += `<input type="text" class="sf-otp-digit sf-otp-digit-input" maxlength="1" inputmode="numeric" data-index="${o}" style="width:40px;height:44px;text-align:center;font-size:18px;font-weight:700;border:1px solid #d1d5db;border-radius:6px;padding:0;">`;
        }
        fieldHtml += `
          <div class="text-fields sf-email-otp-wrap sf-email-otp-group ${conditionalClass}" ${conditionalAttributes} data-field-type="email_otp" data-field-id="${fieldId}" data-otp-length="${otpLen}" data-expiry="${otpExpiry}" data-email-field-id="${field.emailFieldId || ''}">
            ${getLabel(field)}
            <div style="display:flex;flex-direction:column;gap:10px;">
              <div style="display:flex;gap:8px;align-items:center;">
                <input type="email" class="sf-otp-email-input custom-input" placeholder="Enter your email address" data-field-id="${fieldId}__email" style="flex:1;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">
                <button type="button" class="sf-otp-send-btn" style="padding:8px 14px;background:#3b82f6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:500;white-space:nowrap;">${otpSendLabel}</button>
              </div>
              <div class="sf-otp-timer" style="font-size:11px;color:#6b7280;display:none;">⏱️ OTP expires in <span class="sf-otp-countdown">${otpExpiry}</span>s</div>
              <div class="sf-otp-digits-wrap" style="display:flex;gap:6px;display:none;">
                ${otpDigitsHtml}
              </div>
              <div style="display:flex;gap:8px;align-items:center;">
                <button type="button" class="sf-otp-verify-btn" style="padding:8px 14px;background:#22c55e;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:500;display:none;">${otpVerifyLabel}</button>
                <button type="button" class="sf-otp-resend-btn" style="padding:8px 14px;background:#fff;color:#374151;border:1px solid #d1d5db;border-radius:6px;cursor:pointer;font-size:13px;font-weight:500;display:none;">${otpResendLabel}</button>
              </div>
              <div class="sf-otp-status" style="font-size:12px;min-height:16px;"></div>
            </div>
            <input type="hidden" name="${field.name}" class="sf-otp-hidden-value sf-otp-value" value="" data-field-id="${fieldId}">
          </div>
        `;
        break;

      case 'coupon':
        const cpPlaceholder = field.placeholder || 'Enter coupon code...';
        const cpApplyLabel = field.applyButtonLabel || 'Apply';
        const cpCoupons = Array.isArray(field.couponList) ? field.couponList : [];
        let cpCouponsJson = '';
        try { cpCouponsJson = JSON.stringify(cpCoupons).replace(/"/g, '&quot;'); } catch (e) { cpCouponsJson = '[]'; }
        const showBadges = field.showAvailableCoupons !== false && cpCoupons.length > 0;
        let badgesHtml = '';
        if (showBadges) {
          badgesHtml = `
            <div class="sf-available-coupons" style="margin-top:6px;display:flex;flex-wrap:wrap;gap:6px;align-items:center;">
              <span style="font-size:11px;color:#6b7280;font-weight:500;">Available codes:</span>
              ${cpCoupons.map(function(c) {
                const code = (c.code || '').trim();
                const label = c.label ? ` (${c.label})` : (c.type === 'percent' ? ` (${c.value}% Off)` : ` ($${c.value} Off)`);
                return `<button type="button" class="sf-coupon-badge" data-coupon-code="${code}" style="background:#f0fdf4;color:#166534;border:1px dashed #86efac;border-radius:4px;padding:3px 8px;font-size:11px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:3px;transition:all 0.15s ease;" title="Click to apply ${code}">🏷️ ${code}${label}</button>`;
              }).join('')}
            </div>
          `;
        }
        fieldHtml += `
          <div class="text-fields sf-coupon-wrap sf-coupon-group ${conditionalClass}" ${conditionalAttributes} data-field-type="coupon" data-field-id="${fieldId}" data-coupons='${cpCouponsJson}' data-coupon-list='${cpCouponsJson}' data-amount-field-id="${field.amountFieldId || ''}">
            ${getLabel(field)}
            <div style="display:flex;gap:8px;margin-bottom:4px;">
              <input
                type="text"
                class="sf-coupon-input custom-input"
                placeholder="${cpPlaceholder}"
                data-field-id="${fieldId}__input"
                style="flex:1;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;text-transform:uppercase;font-weight:600;letter-spacing:0.5px;">
              <button type="button" class="sf-coupon-apply-btn" style="padding:8px 16px;background:#3b82f6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:600;">${cpApplyLabel}</button>
            </div>
            ${badgesHtml}
            <div class="sf-coupon-status" style="font-size:12px;min-height:16px;margin-top:4px;"></div>
            <input type="hidden" name="${field.name}[code]" class="sf-coupon-code-value" value="" data-field-id="${fieldId}__code">
            <input type="hidden" name="${field.name}[discount]" class="sf-coupon-discount-value" value="" data-field-id="${fieldId}__discount">
          </div>
        `;
        break;

      case 'calculation':
        const calcFormula = field.formula || '';
        const calcDecimals = field.decimalPlaces !== undefined ? field.decimalPlaces : 2;
        const calcPrefix = field.prefix || '';
        const calcSuffix = field.suffix || '';
        const calcReadOnly = field.readOnly !== false;
        const calcIsCurrency = field.showAsCurrency === true;
        const calcCurrencySym = field.currencySymbol || '$';
        let calcDisplayValue = field.value || '0';
        if (calcIsCurrency) calcDisplayValue = calcCurrencySym + calcDisplayValue;
        fieldHtml += `
          <div class="text-fields sf-calculation-wrap sf-calculation-group ${conditionalClass}" ${conditionalAttributes} data-field-type="calculation" data-field-id="${fieldId}">
            ${getLabel(field)}
            <div style="display:flex;align-items:center;gap:8px;">
              ${calcPrefix && !calcIsCurrency ? `<span style="font-size:14px;color:#374151;font-weight:600;">${calcPrefix}</span>` : ''}
              ${calcIsCurrency ? `<span style="padding:0 10px;background:#f3f4f6;display:flex;align-items:center;font-size:14px;color:#374151;border:1px solid #e5e7eb;border-right:none;border-radius:6px 0 0 6px;height:38px;font-weight:500;">${calcCurrencySym}</span>` : ''}
              <input
                id="${field.id}"
                type="text"
                name="${field.name}"
                class="sf-calculation-input ${field.className}"
                value="${field.value || ''}"
                data-formula="${calcFormula}"
                data-decimals="${calcDecimals}"
                data-prefix="${calcPrefix}"
                data-suffix="${calcSuffix}"
                data-is-currency="${calcIsCurrency}"
                data-currency-symbol="${calcCurrencySym}"
                data-field-id="${fieldId}"
                ${calcReadOnly ? 'readonly' : ''}${fieldAttributes}
                style="flex:1;padding:8px 10px;border:1px solid #d1d5db;border-radius:${calcIsCurrency ? '0 6px 6px 0' : '6px'};font-size:14px;font-weight:600;background-color:${calcReadOnly ? '#f9fafb' : '#fff'};color:#111827;">
              ${calcSuffix ? `<span style="font-size:14px;color:#374151;font-weight:600;">${calcSuffix}</span>` : ''}
            </div>
          </div>
        `;
        break;

      case 'chained':
        const chLevels = field.levels || [];
        const chLayout = field.layout || 'vertical';
        const chLevelsJson = JSON.stringify(chLevels).replace(/"/g, '&quot;');
        let chSelectsHtml = '';
        chLevels.forEach(function (lv, lvIdx) {
          const isFirst = lvIdx === 0;
          chSelectsHtml += `<div class="sf-chained-level" style="flex:1;min-width:160px;">
            <label style="font-size:12px;color:#6b7280;margin-bottom:4px;display:block;">${lv.label || 'Level ' + (lvIdx + 1)}</label>
            <select class="sf-chained-select custom-input" data-level-index="${lvIdx}" data-level-id="${lv.id}" data-parent-key="${lv.parentKey || ''}" ${!isFirst ? 'disabled' : ''} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">
              <option value="">${lv.placeholder || 'Select...'}</option>
              ${isFirst && lv.options ? lv.options.map(function (o) { return `<option value="${o.value}">${o.label}</option>`; }).join('') : ''}
            </select>
          </div>`;
        });
        fieldHtml += `
          <div class="text-fields sf-chained-wrap sf-chained-select-group ${conditionalClass}" ${conditionalAttributes} data-field-type="chained" data-field-id="${fieldId}" data-levels='${chLevelsJson}'>
            ${getLabel(field)}
            <div class="sf-chained-selects" style="display:${chLayout === 'horizontal' ? 'flex' : 'flex'};flex-wrap:wrap;gap:10px;flex-direction:${chLayout === 'vertical' ? 'column' : 'row'};">
              ${chSelectsHtml}
            </div>
            <input type="hidden" name="${field.name}" class="sf-chained-hidden-value" value="" data-field-id="${fieldId}">
          </div>
        `;
        break;

      case 'multiselect':
        const msPlaceholder = field.placeholder || 'Select options...';
        const msMaxSel = parseInt(field.maxSelections) || 0;
        const msShowSelAll = field.showSelectAll !== false && field.showSelectAll !== 'false';
        const msShowSearch = field.showSearch !== false && field.showSearch !== 'false';
        const msShowCount = field.showSelectedCount !== false && field.showSelectedCount !== 'false';
        let msOptions = [];
        if (Array.isArray(field.options)) {
          msOptions = field.options;
        } else if (field.options && typeof field.options === 'object') {
          msOptions = Object.values(field.options);
        } else if (typeof field.options === 'string') {
          try {
            const parsed = JSON.parse(field.options);
            if (Array.isArray(parsed)) msOptions = parsed;
            else if (parsed && typeof parsed === 'object') msOptions = Object.values(parsed);
            else msOptions = field.options.split(',').map(s => ({ label: s.trim(), value: s.trim() })).filter(o => o.label);
          } catch (e) {
            msOptions = field.options.split(',').map(s => ({ label: s.trim(), value: s.trim() })).filter(o => o.label);
          }
        }
        let msOptionsHtml = '';
        if (msOptions.length === 0) {
          msOptionsHtml = `<div style="padding:10px;font-size:12px;color:#9ca3af;text-align:center;">No options configured</div>`;
        } else {
          msOptions.forEach(function (opt) {
            const optVal = typeof opt === 'object' ? (opt.value !== undefined ? opt.value : (opt.label || '')) : String(opt);
            const optLbl = typeof opt === 'object' ? (opt.label !== undefined ? opt.label : (opt.value || '')) : String(opt);
            msOptionsHtml += `<label class="sf-ms-option-item sf-multiselect-option" style="display:flex;align-items:center;gap:8px;padding:7px 10px;cursor:pointer;font-size:13px;color:#374151;border-bottom:1px solid #f9fafb;">
              <input type="checkbox" value="${optVal}" data-label="${optLbl}" class="sf-ms-option-input" style="width:16px;height:16px;cursor:pointer;accent-color:#3b82f6;">
              <span>${optLbl}</span>
            </label>`;
          });
        }
        fieldHtml += `
          <div class="text-fields sf-multiselect-wrap sf-multiselect-group ${conditionalClass}" ${conditionalAttributes} data-field-type="multiselect" data-field-id="${fieldId}" data-max-selections="${msMaxSel}" data-placeholder="${msPlaceholder}" style="overflow:visible;position:relative;">
            ${getLabel(field)}
            <div class="sf-multiselect-container" style="position:relative;z-index:30;">
              <div class="sf-multiselect-header" style="display:flex;align-items:center;gap:8px;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;background:#fff;cursor:pointer;min-height:38px;user-select:none;">
                <span class="sf-ms-placeholder" style="color:#9ca3af;font-size:13px;flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${msPlaceholder}</span>
                ${msShowCount ? `<span class="sf-ms-count-badge sf-multiselect-count" style="display:none;background:#3b82f6;color:#fff;font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;">0 selected</span>` : ''}
                <span class="sf-ms-chevron" style="font-size:10px;color:#6b7280;margin-left:4px;">▼</span>
              </div>
              <div class="sf-multiselect-dropdown" style="display:none;position:absolute;top:calc(100% + 4px);left:0;right:0;background:#fff;border:1px solid #e5e7eb;border-radius:6px;box-shadow:0 8px 24px rgba(0,0,0,0.12);z-index:99999;max-height:280px;overflow:hidden;">
                ${msShowSearch ? `<div style="padding:8px;border-bottom:1px solid #e5e7eb;"><input type="text" class="sf-ms-search-input sf-multiselect-search custom-input" placeholder="Search options..." style="width:100%;padding:6px 8px;border:1px solid #d1d5db;border-radius:4px;font-size:13px;"></div>` : ''}
                ${msShowSelAll ? `<label class="sf-ms-select-all-label" style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-bottom:1px solid #f3f4f6;background:#fafafa;font-size:13px;font-weight:600;color:#374151;cursor:pointer;">
                  <input type="checkbox" class="sf-ms-select-all sf-multiselect-select-all" style="width:16px;height:16px;accent-color:#3b82f6;">
                  <span>Select All</span>
                </label>` : ''}
                <div class="sf-multiselect-options-list sf-multiselect-options" style="max-height:190px;overflow-y:auto;">
                  ${msOptionsHtml}
                </div>
              </div>
            </div>
            <input type="hidden" name="${field.name}" class="sf-multiselect-hidden-value sf-multiselect-value" value="" data-field-id="${fieldId}">
          </div>
        `;
        break;

      case 'mask':
        const maskPattern = field.maskPattern || field.customMask || '';
        const maskPreset = field.presetMask || '';
        const maskChar = field.maskChar || '_';
        let effectivePattern = maskPattern;
        if (!effectivePattern) {
          const presets = {
            'phone-us': '(___) ___-____',
            'phone-intl': '+__ ___ ___ ____',
            'date-us': '__/__/____',
            'date-eu': '__.__.____',
            'time': '__:__',
            'zip-us': '_____-____',
            'credit-card': '____ ____ ____ ____',
            'ssn': '___-__-____',
            'ip-address': '___.___.___.___',
          };
          effectivePattern = presets[maskPreset] || '';
        }
        fieldHtml += `
          <div class="text-fields sf-mask-wrap sf-mask-group ${conditionalClass}" ${conditionalAttributes} data-field-type="mask">
            ${getLabel(field)}
            <input
              id="${field.id}"
              type="text"
              name="${field.name}"
              placeholder="${field.placeholder || 'Enter value...'}"
              class="sf-mask-input sf-input-mask-field ${field.className}"
              value="${field.value || ''}"
              data-field-id="${fieldId}"
              data-mask-pattern="${effectivePattern}"
              data-mask-char="${maskChar}"
              data-allow-optional="${field.allowOptional ? 'true' : 'false'}"${fieldAttributes}
              style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">
            ${effectivePattern ? `<div style="font-size:11px;color:#6b7280;margin-top:3px;">Format: <code style="background:#f3f4f6;padding:1px 4px;border-radius:3px;">${effectivePattern}</code></div>` : ''}
          </div>
        `;
        break;

      case 'color_enhanced':
        const ceDefault = field.defaultColor || '#3b82f6';
        const ceShowHex = field.showHexInput !== false;
        const ceShowRgb = field.showRgbInputs === true;
        const ceShowOpacity = field.showOpacity === true;
        const ceShowPalette = field.showPalette !== false;
        const ceSwatches = field.paletteSwatches || [];
        let cePaletteHtml = '';
        if (ceShowPalette) {
          ceSwatches.forEach(function (c) {
            cePaletteHtml += `<button type="button" class="sf-ce-swatch" data-color="${c}" style="width:22px;height:22px;border:2px solid #fff;box-shadow:0 0 0 1px #e5e7eb;border-radius:4px;background-color:${c};cursor:pointer;padding:0;"></button>`;
          });
        }
        const hexValue = field.value || ceDefault;
        fieldHtml += `
          <div class="text-fields sf-color-enhanced-wrap sf-enhanced-color-group ${conditionalClass}" ${conditionalAttributes} data-field-type="color_enhanced">
            ${getLabel(field)}
            <div style="display:flex;flex-direction:column;gap:10px;">
              <div style="display:flex;align-items:center;gap:10px;">
                <div class="sf-ce-preview sf-color-preview" style="position:relative;width:44px;height:44px;border:2px solid #e5e7eb;border-radius:6px;background-color:${hexValue};overflow:hidden;">
                  <input type="color" class="sf-ce-native-picker sf-color-native-picker" value="${hexValue}" style="position:absolute;inset:0;width:100%;height:100%;opacity:0;cursor:pointer;padding:0;border:none;">
                </div>
                ${ceShowHex ? `<div style="flex:1;"><label style="font-size:11px;color:#6b7280;display:block;margin-bottom:2px;">HEX</label>
                  <input type="text" class="sf-ce-hex-input sf-color-hex-input custom-input" value="${hexValue}" data-field-id="${fieldId}__hex" maxlength="7" placeholder="#RRGGBB" style="width:100%;padding:6px 8px;border:1px solid #d1d5db;border-radius:4px;font-size:13px;font-family:monospace;letter-spacing:0.5px;">
                </div>` : ''}
              </div>
              ${ceShowRgb ? `<div style="display:grid;grid-template-columns:repeat(${ceShowOpacity ? '4' : '3'},1fr);gap:6px;">
                <div><label style="font-size:11px;color:#ef4444;display:block;margin-bottom:2px;font-weight:600;">R</label><input type="number" class="sf-ce-rgb-r sf-color-r-input custom-input" min="0" max="255" style="width:100%;padding:6px 8px;border:1px solid #d1d5db;border-radius:4px;font-size:13px;"></div>
                <div><label style="font-size:11px;color:#22c55e;display:block;margin-bottom:2px;font-weight:600;">G</label><input type="number" class="sf-ce-rgb-g custom-input" min="0" max="255" style="width:100%;padding:6px 8px;border:1px solid #d1d5db;border-radius:4px;font-size:13px;"></div>
                <div><label style="font-size:11px;color:#3b82f6;display:block;margin-bottom:2px;font-weight:600;">B</label><input type="number" class="sf-ce-rgb-b custom-input" min="0" max="255" style="width:100%;padding:6px 8px;border:1px solid #d1d5db;border-radius:4px;font-size:13px;"></div>
                ${ceShowOpacity ? `<div><label style="font-size:11px;color:#6b7280;display:block;margin-bottom:2px;font-weight:600;">A</label><input type="number" class="sf-ce-rgb-a custom-input" min="0" max="1" step="0.01" style="width:100%;padding:6px 8px;border:1px solid #d1d5db;border-radius:4px;font-size:13px;"></div>` : ''}
              </div>` : ''}
              ${ceShowPalette ? `<div><label style="font-size:11px;color:#6b7280;display:block;margin-bottom:4px;">Palette</label>
                <div style="display:flex;flex-wrap:wrap;gap:5px;">${cePaletteHtml}</div>
              </div>` : ''}
            </div>
            <input type="hidden" name="${field.name}" class="sf-ce-hidden-value" value="${hexValue}" data-field-id="${fieldId}">
          </div>
        `;
        break;

      case 'username':
        const unPlaceholder = field.placeholder || 'Choose a username...';
        const unMin = field.minLength || 4;
        const unMax = field.maxLength || 20;
        const unPattern = field.allowedPattern || '^[a-zA-Z0-9_-]+$';
        const unPatternErr = field.patternError || 'Invalid characters';
        const unCheckAvail = field.checkAvailability !== false;
        const unDelayMs = parseInt(field.availabilityDelayMs) || 400;
        const unForceLower = field.forceLowercase !== false;
        fieldHtml += `
          <div class="text-fields sf-username-wrap sf-username-group ${conditionalClass}" ${conditionalAttributes} data-field-type="username" data-field-id="${fieldId}">
            ${getLabel(field)}
            <div style="position:relative;">
              <input
                id="${field.id}"
                type="text"
                name="${field.name}"
                placeholder="${unPlaceholder}"
                class="sf-username-input ${field.className}"
                value="${field.value || ''}"
                data-field-id="${fieldId}"
                data-min-length="${unMin}"
                data-max-length="${unMax}"
                data-pattern="${unPattern}"
                data-pattern-error="${unPatternErr}"
                data-check-availability="${unCheckAvail ? 'true' : 'false'}"
                data-delay-ms="${unDelayMs}"
                data-force-lowercase="${unForceLower ? 'true' : 'false'}"${fieldAttributes}
                style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;padding-right:34px;">
              <span class="sf-un-status-icon" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);font-size:14px;display:none;">⏳</span>
            </div>
            <div class="sf-un-status-text" style="font-size:11px;color:#6b7280;margin-top:3px;min-height:14px;">${unMinLength ? '' : ''}Min ${unMin} - Max ${unMax} characters</div>
          </div>
        `;
        break;

      case 'quiz_score':
        const qsFormat = field.displayFormat || 'percentage_and_points';
        const qsPassing = parseInt(field.passingScore) || 70;
        const qsShowPassFail = field.showPassFail !== false;
        const qsShowMessage = field.showMessage !== false;
        const qsPassMsg = field.passMessage || '🎉 Congratulations! You passed!';
        const qsFailMsg = field.failMessage || 'Keep practicing and try again!';
        const qsTotalPoints = parseInt(field.totalPoints) || 0;
        fieldHtml += `
          <div class="text-fields sf-quiz-score-wrap ${conditionalClass}" ${conditionalAttributes} data-field-type="quiz_score" data-field-id="${fieldId}" data-passing-score="${qsPassing}" data-total-points="${qsTotalPoints}">
            ${getLabel(field)}
            <div class="sf-quiz-score-card" style="padding:20px;border:1px solid #e5e7eb;border-radius:10px;background:linear-gradient(135deg,#f0f9ff 0%,#eff6ff 100%);text-align:center;">
              <div class="sf-quiz-score-number" style="font-size:48px;font-weight:800;color:#1e3a8a;line-height:1;margin-bottom:4px;">0%</div>
              ${qsFormat !== 'percentage' ? `<div class="sf-quiz-score-points" style="font-size:14px;color:#6b7280;margin-bottom:10px;">0 / ${qsTotalPoints} points</div>` : ''}
              ${qsShowPassFail ? `<div class="sf-quiz-pass-fail-badge" style="display:inline-block;padding:4px 12px;border-radius:100px;font-size:12px;font-weight:600;background:#f3f4f6;color:#6b7280;margin-bottom:10px;">Awaiting submission</div>` : ''}
              ${qsShowMessage ? `<div class="sf-quiz-score-message" style="font-size:13px;color:#374151;">Fill out answers to see your score.</div>` : ''}
            </div>
            <input type="hidden" name="${field.name}[percentage]" class="sf-quiz-percentage" value="0" data-field-id="${fieldId}__pct">
            <input type="hidden" name="${field.name}[points]" class="sf-quiz-points" value="0" data-field-id="${fieldId}__pts">
            <input type="hidden" name="${field.name}[passed]" class="sf-quiz-passed" value="false" data-field-id="${fieldId}__passed">
          </div>
        `;
        break;

      case 'post_select':
        const psPostType = field.postType || 'post';
        const psStyle = field.displayStyle || 'dropdown';
        const psMaxItems = parseInt(field.maxItems) || 50;
        const psShowSearch = field.showSearch !== false;
        const psMultiple = field.multiple === true;
        const psSortBy = field.sortBy || 'date';
        const psSortOrder = field.sortOrder || 'desc';
        const psTaxonomy = field.taxonomy || '';
        const psTermId = field.termId || '';
        const psSpecificIds = field.specificPostIds || '';
        const psPlaceholder = field.placeholder || 'Select a post...';
        fieldHtml += `
          <div class="text-fields sf-post-select-wrap sf-post-select-group ${conditionalClass}" ${conditionalAttributes} data-field-type="post_select" data-field-id="${fieldId}" data-post-type="${psPostType}" data-max-items="${psMaxItems}" data-sort-by="${psSortBy}" data-sort-order="${psSortOrder}" data-multiple="${psMultiple}" data-taxonomy="${psTaxonomy}" data-term-id="${psTermId}" data-specific-post-ids="${psSpecificIds}" data-placeholder="${psPlaceholder}">
            ${getLabel(field)}
            <div style="display:flex;flex-direction:column;gap:6px;">
              ${psShowSearch ? `<input type="text" class="sf-post-search-input custom-input" placeholder="Search ${psPostType}s..." style="width:100%;padding:7px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:13px;">` : ''}
              <select class="sf-post-select sf-post-select-dropdown custom-input" name="${psMultiple ? field.name + '[]' : field.name}"${psMultiple ? ' multiple size="6"' : ''} data-field-id="${fieldId}"${fieldAttributes} style="width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;background:#fff;">
                <option value="">${psPlaceholder}</option>
              </select>
              <div class="sf-post-select-loading" style="font-size:11px;color:#6b7280;display:none;">Loading ${psPostType}s...</div>
            </div>
          </div>
        `;
        break;

      case 'draft':
        const draftLabel = field.buttonLabel || 'Save Draft';
        const draftColor = field.buttonColor || '#ffffff';
        const draftBgColor = field.buttonBgColor || '#6b7280';
        const draftShowMsg = field.showMessage !== false;
        const draftSuccessMsg = field.successMessage || '✅ Draft saved successfully!';
        fieldHtml += `
          <div class="text-fields sf-draft-button-wrap sf-draft-button-group ${conditionalClass}" ${conditionalAttributes} data-field-type="draft" data-field-id="${fieldId}" data-success-message="${encodeURIComponent(draftSuccessMsg)}">
            <button
              type="button"
              id="${field.id}"
              name="${field.name}"
              class="sf-draft-save-btn"
              data-field-id="${fieldId}"
              style="padding:10px 18px;background-color:${draftBgColor};color:${draftColor};border:none;border-radius:6px;cursor:pointer;font-size:14px;font-weight:500;">
              💾 ${draftLabel}
            </button>
            ${draftShowMsg ? `<div class="sf-draft-status-msg" style="font-size:12px;color:#6b7280;margin-top:6px;min-height:16px;"></div>` : ''}
          </div>
        `;
        break;

      case 'entry_preview':
        const epTitle = field.previewTitle || 'Please review your answers';
        const epShowEdit = field.showEditLink !== false;
        const epShowDividers = field.showSectionDividers !== false;
        const epStyle = field.summaryStyle || 'card';
        const epExclude = field.excludeFields || ['submit', 'button', 'page_break', 'section_break', 'spacer'];
        const epExcludeJson = JSON.stringify(epExclude).replace(/"/g, '&quot;');
        const cardStyle = epStyle === 'card' ? 'border:1px solid #e5e7eb;border-radius:10px;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,0.04);' : 'border:none;background:transparent;';
        fieldHtml += `
          <div class="text-fields sf-entry-preview-wrap ${conditionalClass}" ${conditionalAttributes} data-field-type="entry_preview" data-field-id="${fieldId}" data-exclude='${epExcludeJson}' data-show-dividers="${epShowDividers}">
            <div class="sf-entry-preview-card" style="${cardStyle}padding:16px;">
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
                <span style="font-size:18px;">📋</span>
                <span style="font-size:15px;font-weight:700;color:#111827;">${epTitle}</span>
              </div>
              <div class="sf-entry-preview-list" style="display:flex;flex-direction:column;gap:0;">
                <div style="font-size:12px;color:#6b7280;text-align:center;padding:16px 0px;font-style:italic;">Fill out the form to see a summary of your answers here.</div>
              </div>
            </div>
          </div>
        `;
        break;

      case 'content_html':
        let rawContent = field.htmlContent !== undefined ? field.htmlContent : (field.content !== undefined ? field.content : (field.value || ''));
        if (typeof rawContent === 'string' && (rawContent.indexOf('&lt;') !== -1 || rawContent.indexOf('&gt;') !== -1 || rawContent.indexOf('&amp;') !== -1)) {
          try {
            var tempDiv = document.createElement('div');
            tempDiv.innerHTML = rawContent;
            rawContent = tempDiv.textContent || tempDiv.innerText || rawContent;
          } catch (e) {}
        }
        const htmlBgImg = field.backgroundImage || '';
        const htmlBgColor = field.backgroundColor || '';
        const htmlPad = field.padding || '16';
        const htmlRadius = field.borderRadius || '8';
        const htmlShowBorder = field.showBorder === true || field.showBorder === 'true';
        const htmlBorderColor = field.borderColor || '#e5e7eb';
        const bgStyles = [];
        if (htmlBgImg) bgStyles.push(`background-image:url('${htmlBgImg}');background-size:cover;background-position:center;`);
        if (htmlBgColor) bgStyles.push(`background-color:${htmlBgColor};`);
        fieldHtml += `
          <div class="text-fields sf-content-html-wrap sf-content-html-block ${conditionalClass}" ${conditionalAttributes} data-field-type="content_html" style="${bgStyles.join(' ')}padding:${htmlPad}px;border-radius:${htmlRadius}px;${htmlShowBorder ? `border:1px solid ${htmlBorderColor};` : ''}">
            <div class="sf-content-html-body" style="font-size:14px;line-height:1.6;color:#374151;">
              ${rawContent}
            </div>
          </div>
        `;
        break;

      default:
        break;
    }

    if (field.type !== 'section_break') {
      const w = field.columnWidth || '100%';
      let calcWidth = '100%';
      
      // For horizontal rows, if no explicit columnWidth is set, default to auto-sizing
      if (rowLayout === 'horizontal' && !field.columnWidth) {
        // Don't force 100% width in horizontal rows - let flex handle it
        // Fields will share space equally
      } else {
        // Apply column width for explicit widths or vertical rows
        if (w === '50%') calcWidth = 'calc(50% - 6px)';
        else if (w === '33.33%' || w === '33.3%') calcWidth = 'calc(33.333% - 8px)';
        else if (w === '25%') calcWidth = 'calc(25% - 9px)';
        else if (w === '66.66%' || w === '66.6%') calcWidth = 'calc(66.666% - 4px)';
        else if (w === '75%') calcWidth = 'calc(75% - 3px)';

        const flexStyle = `flex:0 0 ${calcWidth} !important;max-width:${calcWidth} !important;width:${calcWidth} !important;box-sizing:border-box;`;

        fieldHtml = fieldHtml.replace(/^\s*<([a-zA-Z0-9_-]+)([^>]*)>/, function (match, tag, attrs) {
          if (attrs.indexOf('style="') !== -1) {
            return `<${tag}${attrs.replace('style="', `style="${flexStyle} `)}>`;
          } else {
            return `<${tag} style="${flexStyle}"${attrs}>`;
          }
        });
      }
    }

    return fieldHtml;
  }

  // Function to get theme-specific styles - mainly its used here but maxium style maintain from frontend.scss 
  function getThemeStyles(theme) {
    // Check if it's a custom theme
    if (theme && theme.startsWith('custom-')) {
      return getCustomThemeStyles(theme);
    }

    switch (theme) {
      case 'theme-1':
        return `
          <style>
            .simple_form_content.theme-1 {
              background-color: #ffffff;
              color: #333333;
            }
          </style>
        `;

      case 'theme-2':
        return `
          <style>
            .simple_form_content.theme-2 {
              background-color: #f8f9fa;
              color: #212529;
              padding: 25px;
              border-radius: 12px;
            }

            .submit-button{
              border: 1px solid #333333;
            }

          </style>
        `;
      case 'theme-3':
        return `
          <style>
            .simple_form_content.theme-3 {
              background-color:#2c3e50;
              color: #ecf0f1;
              padding: 30px;
              border-radius: 8px;
            }

            .submit-button{
              border: 1px solid  #ecf0f1;
            }
          </style>
        `;
      case 'theme-4':
        return `
          <style>
            .simple_form_content.theme-4 {
              background-color: #f5f6fa;
              color: #2c3e50;
              padding: 30px;
              border-radius: 12px;
            }

            .submit-button{
              border: 1px solid #2c3e50;
            }
          </style>
        `;
      case 'theme-5':
        return `
          <style>
            .simple_form_content.theme-5 {
              background-color: #1e1e2f;
              color: #ecf0f1;
               padding: 30px;
              border-radius: 12px;
           
            }

            .submit-button{
              border: 1px solid #ecf0f1;
            }
          </style>
        `;
      case 'theme-6':
        return `
          <style>
            .simple_form_content.theme-6 {
              background-color: #e6eef9;
              color: #2d3436;
              border-radius: 12px;
              font-weight: 600;
              border: none;
              padding: 12px 24px;
             
              transition: all 0.3s ease;
            }

            .submit-button{
              border: 1px solid #2d3436;
            }
          </style>
        `;
      case 'theme-7':
        return `
          <style>
            .simple_form_content.theme-7 {
              background-color: #121212;
              color: #ffffff;
              padding: 30px;
              border-radius: 16px;
              backdrop-filter: blur(10px);
             
              border: 1px solid rgba(255, 255, 255, 0.05);
            }

            .submit-button{
              border: 1px solid #ffffff;
            }
          </style>
        `;

      default:
        return '';
    }
  }

  // Function to get custom theme styles
  function getCustomThemeStyles(themeId) {
    // If already injected into <head>, nothing to do
    if (document.querySelector(`style[data-theme-id="${themeId}"]`)) {
      return '';
    }

    // Try to get custom theme from global variable (set by PHP via wp_add_inline_script)
    if (window.simpleform_custom_themes && window.simpleform_custom_themes[themeId]) {
      const customTheme = window.simpleform_custom_themes[themeId];
      const styleEl = document.createElement('style');
      styleEl.setAttribute('data-theme-id', themeId);
      styleEl.textContent = customTheme.css_styles;
      document.head.appendChild(styleEl);
      return ''; // Don't return inline — injected into head above
    }

    // Fallback: Load custom theme via AJAX (for backward compatibility)
    loadCustomThemeAsync(themeId);
    return '';
  }

  // Async function to load custom theme styles
  function loadCustomThemeAsync(themeId) {
    fetch(front_end_data.admin_ajax, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        action: 'simpleform_get_custom_theme_styles',
        theme_id: themeId,
        nonce: front_end_data.nonce
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success && data.data.css_styles) {
          // Inject the custom theme styles into the page
          const styleElement = document.createElement('style');
          styleElement.setAttribute('data-theme-id', themeId);
          styleElement.textContent = data.data.css_styles;
          document.head.appendChild(styleElement);
        }
      })
      .catch(error => {
        console.error('Error loading custom theme:', error);
      });
  }
}



/**
 * Load Form and new page break add.
 * Load Form with page break
 */

function sfInitAllForms() {
  var formContainers = document.querySelectorAll('.simple_form_container');

  formContainers.forEach(function (container) {
    var formId = container.getAttribute('data-form-id');
    var formTheme = container.getAttribute('data-form-theme');
    var formPosition = container.getAttribute('data-position') || 'inline';
    // console.log(formTheme)
    var nonce = container.getAttribute('data-nonce');
    var cf7 = container.getAttribute('data-cf7');
    // var markupId = container.querySelector('.simple_form_loader').id;
    var markupId = '';
    var simpleFormLoader = container.querySelector('.simple_form_loader');
    if (simpleFormLoader) {
      var markupId = simpleFormLoader.id;
    }

    var isFloatingForm = formPosition !== 'inline';

    var submitBtn = container.querySelector('.submit-button');

    var isThemeActiveInForm = container.querySelector('.floating-form-container') !== null;
    // console.log(isThemeActiveInForm);

    var theme = isThemeActiveInForm ? 'theme-off' : formTheme;  // 1 to 7
    // var theme = isThemeActiveInForm ? 'theme-off' : 'theme-7';  // 1 to 7

    // console.log('Form Theme:', theme);

    if (isFloatingForm) {
      var toggleBtn = container.querySelector('.simple_form_toggle');
      var floatingContainer = container.querySelector('.simple_form_floating_container');
      var closeBtn = container.querySelector('.simple_form_close');
      var expandBtn = container.querySelector('.simple_form_expand_btn');
      var ctaBadge = container.querySelector('.simple_form_cta_badge');
      var ctaClose = container.querySelector('.simple_form_cta_close');
      var isExpanded = false;

      // Handle display delay if configured
      var delaySeconds = parseInt(container.getAttribute('data-delay') || '0', 10);
      if (delaySeconds > 0) {
        setTimeout(function () {
          container.style.visibility = 'visible';
          container.style.opacity = '1';
          container.style.transition = 'opacity 0.4s ease, visibility 0.4s ease';
        }, delaySeconds * 1000);
      } else {
        container.style.visibility = 'visible';
        container.style.opacity = '1';
      }

      // Handle State Persistence & Initial State
      var rememberState = container.getAttribute('data-remember-state') === 'true';
      var initialState = container.getAttribute('data-initial-state') || 'collapsed';
      var instanceId = container.getAttribute('data-instance-id') || '';
      var isGlobal = container.getAttribute('data-is-global') === 'true';
      var stateKey = 'sf_floating_state_' + formId + (isGlobal ? '_global' : (instanceId ? '_' + instanceId : ''));
      var savedState = null;
      if (rememberState) {
        try {
          savedState = localStorage.getItem(stateKey);
        } catch (e) {}
      }

      function openFloatingForm() {
        if (!floatingContainer) return;
        floatingContainer.style.display = 'block';
        if (toggleBtn) toggleBtn.style.display = 'none';
        if (ctaBadge) ctaBadge.style.display = 'none';

        void floatingContainer.offsetWidth;
        setTimeout(function () {
          floatingContainer.classList.add('visible');
        }, 10);

        if (rememberState) {
          try { localStorage.setItem(stateKey, 'open'); } catch (e) {}
        }
        sfLayoutFloatingWidgets();
      }

      function closeFloatingForm() {
        if (!floatingContainer) return;
        floatingContainer.classList.remove('visible');
        setTimeout(function () {
          floatingContainer.style.display = 'none';
          if (toggleBtn) toggleBtn.style.display = 'flex';
          if (ctaBadge) ctaBadge.style.display = 'inline-flex';
          sfLayoutFloatingWidgets();
        }, 300);

        var errorMessages = container.querySelectorAll('.error-message');
        errorMessages.forEach(function (errorMsg) {
          errorMsg.style.display = 'none';
        });

        if (rememberState) {
          try { localStorage.setItem(stateKey, 'closed'); } catch (e) {}
        }
      }

      // Auto-open if initial state is open or remembered as open
      var shouldOpenInitial = savedState ? (savedState === 'open') : (initialState === 'open');
      if (shouldOpenInitial && floatingContainer) {
        openFloatingForm();
      }

      if (toggleBtn) {
        toggleBtn.addEventListener('click', function () {
          applySpinAnimation(toggleBtn);
          openFloatingForm();
          setTimeout(function () {
            toggleBtn.classList.remove('simple_form_spin');
          }, 500);
        });
      }

      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          closeFloatingForm();
        });
      }

      if (ctaBadge) {
        ctaBadge.addEventListener('click', function (e) {
          if (e.target.closest('.simple_form_cta_close')) return;
          openFloatingForm();
        });
      }

      if (ctaClose && ctaBadge) {
        ctaClose.addEventListener('click', function (e) {
          e.stopPropagation();
          ctaBadge.style.opacity = '0';
          setTimeout(function () {
            ctaBadge.style.display = 'none';
          }, 250);
        });
      }

      if (expandBtn && floatingContainer) {
        // Set the initial expand icon
        expandBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"></path><path d="M9 21H3v-6"></path><path d="M21 3l-7 7"></path><path d="M3 21l7-7"></path></svg>';

        expandBtn.addEventListener('click', function () {
          isExpanded = !isExpanded;

          if (isExpanded) {
            // Expand the form
            floatingContainer.classList.add('expanded');
            // Change to minimize icon
            expandBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3"></path><path d="M21 8h-3a2 2 0 0 1-2-2V3"></path><path d="M3 16h3a2 2 0 0 1 2 2v3"></path><path d="M16 21v-3a2 2 0 0 1 2-2h3"></path></svg>';
          } else {
            // Restore original size
            floatingContainer.classList.remove('expanded');
            // Restore the expand icon
            expandBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"></path><path d="M9 21H3v-6"></path><path d="M21 3l-7 7"></path><path d="M3 21l7-7"></path></svg>';
          }
        });
      }
    }


    fetch(front_end_data.admin_ajax + '?action=simpleform_table_html&nonce=' + nonce + '&id=' + formId + '&iscf7=' + cf7)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(function (data) {
        // // console.log('[SimpleForm] AJAX response received, cf7:', cf7);
        // console.log(data.data.cf7_render)
        if (cf7 === 'false') {
          const code = container.querySelector('.simple_form_loader') || document.getElementById(markupId);
          if (!code) {
            return;
          }
          const formData = data.data.table_settings;
          const isPro = front_end_data.isProActive;
          const currentInstanceId = container.getAttribute('data-instance-id') || '';
          const renderedFormHTML = generateRenderedForm(formData, isPro, theme, currentInstanceId);
          code.innerHTML = renderedFormHTML;

          // Immediately hide loader and show submit button for this container
          const loader = container.querySelector('.simple_form_page_loader');
          if (loader) {
            loader.style.display = 'none';
          }
          if (submitBtn) {
            submitBtn.style.display = 'block';
          }

          // console.log('[SimpleForm] Form HTML inserted into DOM');

          // Add navigation functionality
          try {
            addNavigationFunctionality(container);
            // console.log('[SimpleForm] Navigation functionality added');
          } catch (e) {
            console.error('[SimpleForm] Error in addNavigationFunctionality:', e);
          }

          // Add conditional logic functionality
          try {
            addConditionalLogicFunctionality(container);
            // console.log('[SimpleForm] Conditional logic functionality added');
          } catch (e) {
            console.error('[SimpleForm] Error in addConditionalLogicFunctionality:', e);
          }

          // Add progress indicator functionality
          try {
            addProgressIndicatorFunctionality(container);
            // console.log('[SimpleForm] Progress indicator functionality added');
          } catch (e) {
            console.error('[SimpleForm] Error in addProgressIndicatorFunctionality:', e);
          }

          /**
          * Initilize all required function for fields 
          */

          // console.log('[SimpleForm] Starting field initializations...');

          //Signature 
          try {
            canvasSignature(code);
            // console.log('[SimpleForm] Signature initialized');
          } catch (e) {
            console.error('[SimpleForm] Error in canvasSignature:', e);
          }

          // File Upload Dropzone
          try {
            initFileUploadHandlers(code);
            // console.log('[SimpleForm] File upload handlers initialized');
          } catch (e) {
            console.error('[SimpleForm] Error in initFileUploadHandlers:', e);
          }

          // Set up reset button click handlers
          try {
            setupResetButtons(code);
            // console.log('[SimpleForm] Reset buttons initialized');
          } catch (e) {
            console.error('[SimpleForm] Error in setupResetButtons:', e);
          }

          // Initialize the form slider : Keep it below of all 
          try {
            initializeFormSlider(code, submitBtn);
            // console.log('[SimpleForm] Form slider initialized');
          } catch (e) {
            console.error('[SimpleForm] Error in initializeFormSlider:', e);
          }

          // Initialize new field types - pass the form container
          try {
            var formContainer = code.querySelector('.simple_form_container') || code;
            // console.log('[SimpleForm] About to call initNewFieldTypes, container:', formContainer);
            // console.log('[SimpleForm] Container HTML:', formContainer ? formContainer.innerHTML.substring(0, 200) : 'null');
            initNewFieldTypes(formContainer);
            // console.log('[SimpleForm] initNewFieldTypes completed');
          } catch (e) {
            console.error('[SimpleForm] Error in initNewFieldTypes:', e);
          }

        }
        // No need to render through JS as direct shortcode used to render 
        /* else{
          const code = document.getElementById(markupId);
          const formData = data.data.cf7_render;
          code.innerHTML = formData;
        } */

      })
      .catch(function (error) {
        // // console.log('SForm', formId, 'Error:', error.message);
      });
  });

  sfLayoutFloatingWidgets();
}

function sfLayoutFloatingWidgets() {
  ['bottom-right', 'bottom-left'].forEach(function (pos) {
    var widgets = document.querySelectorAll('.simple_form_container.' + pos);
    var offset = 20;
    widgets.forEach(function (widget) {
      widget.style.bottom = offset + 'px';
      var toggle = widget.querySelector('.simple_form_toggle');
      var h = (toggle && toggle.offsetHeight > 0) ? toggle.offsetHeight : 60;
      offset += h + 14;
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', sfInitAllForms);
} else {
  sfInitAllForms();
}



// Animention task ------------

// Function to apply attention-grabbing animation
function applyAttentionAnimation(toggleBtn) {
  // Add animation class
  toggleBtn.classList.add('simple_form_attention');

  // Remove animation class after animation completes
  setTimeout(() => {
    toggleBtn.classList.remove('simple_form_attention');
  }, 2000); // Animation duration + a bit extra
}

// Function to apply spinning animation on click
function applySpinAnimation(toggleBtn) {
  toggleBtn.classList.add('simple_form_spin');
}

// Add the animation logic to your existing code
function sfInitAttentionAnimations() {
  var formContainers = document.querySelectorAll('.simple_form_container');

  formContainers.forEach(function (container) {
    var formPosition = container.getAttribute('data-position') || 'inline';
    var isFloatingForm = formPosition !== 'inline';

    if (isFloatingForm) {
      var toggleBtn = container.querySelector('.simple_form_toggle');
      var floatingContainer = container.querySelector('.simple_form_floating_container');

      if (toggleBtn && floatingContainer) {
        // Set up attention animation interval
        setInterval(function () {
          applyAttentionAnimation(toggleBtn);
        }, 10000); // Run every 10 seconds

        // Run it once after 3 seconds on page load
        setTimeout(function () {
          applyAttentionAnimation(toggleBtn);
        }, 3000);
      }
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', sfInitAttentionAnimations);
} else {
  sfInitAttentionAnimations();
}

// Animention task Emd


/**
 * Signature 
 */

function canvasSignature(formContainer) {
  var pages = formContainer.querySelectorAll('.page');

  pages.forEach(function (page) {
    const signatureCanvases = page.querySelectorAll('.signature-canvas');
    signatureCanvases.forEach(function (canvas) {
      const context = canvas.getContext('2d');
      let isDrawing = false;

      // Handle mouse events
      canvas.addEventListener('mousedown', function (e) {
        isDrawing = true;
        drawStart(e, context);
      });

      canvas.addEventListener('mousemove', function (e) {
        if (isDrawing) {
          draw(e, context);
        }
      });

      canvas.addEventListener('mouseup', function () {
        if (isDrawing) {
          isDrawing = false;
          drawEnd(context);
        }
      });

      canvas.addEventListener('mouseout', function () {
        if (isDrawing) {
          isDrawing = false;
          drawEnd(context);
        }
      });

      // Handle touch events
      canvas.addEventListener('touchstart', function (e) {
        e.preventDefault();
        isDrawing = true;
        const touch = e.touches[0];
        const simulatedEvent = new MouseEvent('mousedown', {
          clientX: touch.clientX,
          clientY: touch.clientY
        });
        canvas.dispatchEvent(simulatedEvent);
      });

      canvas.addEventListener('touchmove', function (e) {
        e.preventDefault();
        if (isDrawing) {
          const touch = e.touches[0];
          const simulatedEvent = new MouseEvent('mousemove', {
            clientX: touch.clientX,
            clientY: touch.clientY
          });
          canvas.dispatchEvent(simulatedEvent);
        }
      });

      canvas.addEventListener('touchend', function () {
        e.preventDefault();
        if (isDrawing) {
          isDrawing = false;
          drawEnd(context);
        }
      });

      canvas.addEventListener('touchcancel', function () {
        e.preventDefault();
        if (isDrawing) {
          isDrawing = false;
          drawEnd(context);
        }
      });
    });
  });
}


function drawStart(e, context) {
  context.beginPath();
  draw(e, context);
}

function drawEnd(context) {
  context.closePath();
}

function draw(e, context) {
  const rect = e.target.getBoundingClientRect();
  const mouseX = e.clientX - rect.left;
  const mouseY = e.clientY - rect.top;

  context.lineWidth = 2;
  context.lineCap = 'round';

  context.lineTo(mouseX, mouseY);
  context.stroke();
  context.moveTo(mouseX, mouseY);
}


/**
 * 
 * Reset Canvas
 */

function setupResetButtons(formContainer) {
  const resetButtons = formContainer.querySelectorAll('.reset-signature');
  // console.log(resetButtons)
  resetButtons.forEach(function (button) {
    button.addEventListener('click', function () {
      const canvasId = button.getAttribute('data-canvas-id');
      resetSignatureCanvas(canvasId);
    });
  });
}

function resetSignatureCanvas(canvasId) {

  const canvas = document.getElementById(canvasId);
  const context = canvas.getContext('2d');

  // Clear the canvas
  context.clearRect(0, 0, canvas.width, canvas.height);

  // Additional code for resetting any other related state or variables
}

/**
 * 
 Slider form for next and prev page
 */
function initializeFormSlider(formContainer, submitBtn) {

  var pages = formContainer.querySelectorAll('.page');
  var currentPage = 1;

  // console.log(formContainer)
  // console.log("initializeFormSlider is running!");

  function showPage(pageNumber) {
    pages.forEach(function (page) {
      page.style.display = 'none';
    });

    var currentPageElement = formContainer.querySelector('.page[data-page="' + pageNumber + '"]');
    if (currentPageElement) {
      currentPageElement.style.display = 'block';
    }

    // Toggle the visibility of the submit button based on the presence of the next button.
    var isNext = currentPageElement.querySelector('.next-btn') !== null;

    if (submitBtn) {
      // submitBtn.style.display = isNext ? 'none' : 'inline-block';
      submitBtn.style.display = isNext ? 'none' : 'flex';
    } else {
      // console.log("Button not found")
    }
  }

  /**
   * Page next and prev button visibility
   */
  function updateButtonsVisibility() {
    var prevButton = formContainer.querySelector('.prev-btn');
    var nextButton = formContainer.querySelector('.next-btn');

    if (prevButton) {
      if (currentPage === 1) {
        prevButton.style.display = 'none';
      } else {
        prevButton.style.display = 'inline-block';
      }
    }

    if (nextButton) {
      if (currentPage === pages.length) {
        nextButton.style.display = 'none';
      } else {
        nextButton.style.display = 'inline-block';
      }
    }
  }

  // Initial setup
  showPage(currentPage);
  updateButtonsVisibility();

  // Event listeners for next and previous buttons
  formContainer.addEventListener('click', function (event) {
    var target = event.target;

    if (target.classList.contains('next-btn')) {
      // Do not proceed if required fields are not filled
      if (!validateRequiredFields(currentPage)) {
        return;
      }
      //IF all ok move next 
      currentPage++;

    } else if (target.classList.contains('prev-btn')) {
      currentPage--;
    }
    showPage(currentPage);
    updateButtonsVisibility();
  });


  /**
   * Validation of next button move
   */
  function validateRequiredFields(pageNumber) {
    var currentPageElement = formContainer.querySelector('.page[data-page="' + pageNumber + '"]');
    var requiredFields = currentPageElement.querySelectorAll('[required]');
    var isValid = true;

    requiredFields.forEach(function (field) {
      if (field.value.trim() === '') {
        field.style.border = '1px solid red';
        isValid = false;
      } else {
        field.style.border = '';
      }
    });

    var errorMessage = currentPageElement.querySelector('.blank-filled-error-message');

    if (!isValid) {
      // Display an error message below the "text-fields" div
      if (!errorMessage) {
        errorMessage = document.createElement('div');
        errorMessage.className = 'blank-filled-error-message';
        errorMessage.style.color = 'red';
        errorMessage.textContent = 'Please fill out all required fields.';
        currentPageElement.querySelector('.text-fields').appendChild(errorMessage);
      }
    } else {
      // Remove the error message if fields are valid
      if (errorMessage) {
        errorMessage.remove();
      }
    }

    return isValid;
  }

}

// Modal --------------------
// Custom modal implementation to replace SweetAlert
// Add this to your existing JavaScript file

function showCustomModal(title, message, duration = 3000) {
  // Create modal container
  const modalOverlay = document.createElement('div');
  modalOverlay.className = 'custom-modal-overlay';

  // Create modal content
  const modalContent = document.createElement('div');
  modalContent.className = 'custom-modal';

  // Create header with title
  const modalHeader = document.createElement('div');
  modalHeader.className = 'custom-modal-header';

  const modalTitle = document.createElement('h3');
  modalTitle.textContent = title;
  modalHeader.appendChild(modalTitle);

  // Create message content
  const modalBody = document.createElement('div');
  modalBody.className = 'custom-modal-body';
  modalBody.innerHTML = message;

  // Create progress bar
  const progressBar = document.createElement('div');
  progressBar.className = 'custom-modal-progress';

  const progressIndicator = document.createElement('div');
  progressIndicator.className = 'custom-modal-progress-indicator';
  progressBar.appendChild(progressIndicator);

  // Assemble the modal
  modalContent.appendChild(modalHeader);
  modalContent.appendChild(modalBody);
  modalContent.appendChild(progressBar);
  modalOverlay.appendChild(modalContent);

  // Add to document
  document.body.appendChild(modalOverlay);

  // Show modal with animation
  setTimeout(() => {
    modalOverlay.classList.add('visible');
    progressIndicator.style.transition = `width ${duration}ms linear`;
    progressIndicator.style.width = '100%';
  }, 10);

  // Auto close after duration
  setTimeout(() => {
    modalOverlay.classList.remove('visible');
    setTimeout(() => {
      document.body.removeChild(modalOverlay);
    }, 300); // Wait for fade out animation
  }, duration);

  // Allow clicking outside to close
  modalOverlay.addEventListener('click', (e) => {
    if (e.target === modalOverlay) {
      modalOverlay.classList.remove('visible');
      setTimeout(() => {
        document.body.removeChild(modalOverlay);
      }, 300);
    }
  });
}


/**
 * Submit Form data get and store in DB and other form submission task here
 */

window.addEventListener('load', function () {
  var formContainers = document.querySelectorAll('.simple_form_container');
  const formContent = document.querySelector('.form-content');

  formContainers.forEach(function (formContainer) {
    var formId = formContainer.dataset.formId;
    var nonce = formContainer.dataset.nonce || (typeof front_end_data !== 'undefined' && front_end_data.nonce) || '';

    // Find the submit button within the current form container
    var submitButton = formContainer.querySelector('.submit-button');


    if (!submitButton) {
      return;
    }


    // Store the original button text
    var originalButtonText = submitButton.innerText;

    // Create or locate a div for displaying error messages INSIDE the form
    var errorDiv = formContainer.querySelector('.error-message.sf-form-error-msg');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'error-message sf-form-error-msg';
    }
    errorDiv.style.color = '#ef4444';
    errorDiv.style.fontSize = '13px';
    errorDiv.style.lineHeight = '1.5';
    errorDiv.style.margin = '10px 0';
    errorDiv.style.whiteSpace = 'pre-line';
    errorDiv.style.wordBreak = 'break-word';
    errorDiv.style.overflowWrap = 'break-word';
    errorDiv.style.maxWidth = '100%';
    errorDiv.style.boxSizing = 'border-box';
    errorDiv.style.display = 'none';

    // Insert inside the form, right above the submit button so it is never outside widgets or the form container
    if (submitButton && submitButton.parentNode) {
      submitButton.parentNode.insertBefore(errorDiv, submitButton);
    } else {
      var formContent = formContainer.querySelector('.simple_form, form, .simple_form_content') || formContainer;
      formContent.appendChild(errorDiv);
    }


    submitButton.addEventListener('click', function (event) {

      // Check if this is an auth form (login/registration/comment)
      if (typeof front_end_data !== 'undefined' && typeof front_end_data.auth_form_ids !== 'undefined') {
        const authFormIds = front_end_data.auth_form_ids;
        const currentFormId = parseInt(formId, 10);
        
        // Check if this form is configured as an auth form
        if (currentFormId && (
            currentFormId === parseInt(authFormIds.wp_login, 10) || 
            currentFormId === parseInt(authFormIds.wp_registration, 10) || 
            currentFormId === parseInt(authFormIds.wc_login, 10) || 
            currentFormId === parseInt(authFormIds.wc_registration, 10) || 
            currentFormId === parseInt(authFormIds.comment, 10))) {
          event.preventDefault();
          if (typeof window.sfHandleAuthSubmit === 'function') {
            window.sfHandleAuthSubmit(formContainer, currentFormId);
          }
          return;
        }
      }

      // Prevent the default form submission behavior
      event.preventDefault();

      // Change button text to "Sending..."
      submitButton.innerText = 'Sending...';

      // Sync all rich text editors so HTML formatting (bold, italics, lists, links) is captured in hidden inputs
      formContainer.querySelectorAll('.sf-richtext-editor').forEach(function (ed) {
        var editor = ed.querySelector('.sf-richtext-content');
        var hidden = ed.querySelector('.sf-richtext-value');
        if (editor && hidden) {
          hidden.value = editor.innerHTML;
        }
      });

      // Get all form elements within the form
      var formElements = formContainer.querySelectorAll('input, select, textarea');

      // Create an object to store the form data
      var formDataObject = {};

      // Create a flag to track if there are any required fields that are empty
      var hasEmptyRequiredFields = false;
      var errorMessage = '';

      formElements.forEach(function (element) {
        // Skip elements with no name or empty name attribute
        if (!element.name || element.name.trim() === '') {
          return;
        }
        // Skip buttons
        if (element.type === 'button' || element.type === 'submit' || element.type === 'reset') {
          return;
        }

        // Check if the element has the "simple-form-checkbox-toggle" class (checkbox field's toggle mode)
        var isToggleCheckbox = element.closest('.simple-form-checkbox-toggle') !== null;
        // Check if it's a standalone Toggle/Switch field type
        var isStandaloneToggle = element.classList && element.classList.contains('sf-toggle-input');
        // Check GDPR and Terms checkboxes for explicit handling
        var isGdprCheckbox = element.classList && element.classList.contains('sf-gdpr-input');
        var isTermsCheckbox = element.classList && element.classList.contains('sf-terms-input');

        // Handle checkboxes with the "simple-form-checkbox-toggle" class (checkbox field toggle mode)
        if (isToggleCheckbox && element.type === 'checkbox') {
          formDataObject[element.name] = element.checked ? 'on' : 'off';
        }
        // Handle standalone Toggle/Switch field type - ALWAYS store value (both on and off states)
        else if (isStandaloneToggle && element.type === 'checkbox') {
          var tglTrue = element.getAttribute('data-true-value') || 'true';
          var tglFalse = element.getAttribute('data-false-value') || 'false';
          formDataObject[element.name] = element.checked ? tglTrue : tglFalse;
          // Required validation for standalone toggle
          if (element.hasAttribute('required') && !element.checked) {
            var tglWrap = element.closest('.sf-toggle-wrap');
            if (tglWrap) tglWrap.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
          } else {
            var tglWrap2 = element.closest('.sf-toggle-wrap');
            if (tglWrap2) tglWrap2.style.outline = '';
          }
        }
        // Handle GDPR checkbox - explicit on/off value + required validation
        else if (isGdprCheckbox && element.type === 'checkbox') {
          formDataObject[element.name] = element.checked ? 'agreed' : 'disagreed';
          if (element.hasAttribute('required') && !element.checked) {
            element.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
            errorMessage = errorMessage || 'Please agree to the privacy policy.';
          } else {
            element.style.outline = '';
          }
        }
        // Handle Terms & Conditions checkbox - explicit on/off value + required validation
        else if (isTermsCheckbox && element.type === 'checkbox') {
          formDataObject[element.name] = element.checked ? 'agreed' : 'disagreed';
          if (element.hasAttribute('required') && !element.checked) {
            element.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
            errorMessage = errorMessage || 'Please agree to the Terms & Conditions.';
          } else {
            element.style.outline = '';
          }
        }
        else if (element.type === 'radio') {
          // Only store radio button value if it's checked
          if (element.checked) {
            formDataObject[element.name] = element.value;
          }
        } else if (element.type === 'checkbox') {
          // Handle regular checkboxes - only store if checked
          if (element.checked) {
            // If multiple checkboxes have the same name, store as array
            if (formDataObject[element.name]) {
              // Convert to array if not already
              if (!Array.isArray(formDataObject[element.name])) {
                formDataObject[element.name] = [formDataObject[element.name]];
              }
              formDataObject[element.name].push(element.value);
            } else {
              formDataObject[element.name] = element.value;
            }
          }
        } else if (element.type === 'file') {
          // File fields are uploaded via binary payload; do not store C:\fakepath\ string
          if (element.hasAttribute('required') && (!element.files || element.files.length === 0)) {
            element.style.border = '1px solid red';
            hasEmptyRequiredFields = true;
            errorMessage = 'Please select a file to upload.';
          } else {
            element.style.border = '';
          }
        } else if (element.classList && element.classList.contains('sf-currency-input')) {
          // Currency field: store raw numeric value (without thousand separators), not formatted display value
          var rawValue = element.getAttribute('data-raw-value');
          if (rawValue === null || rawValue === undefined) {
            rawValue = unformatCurrencyNumber(element.value);
          }
          formDataObject[element.name] = rawValue;

          // Store display-formatted value too for convenience in emails? No - raw is safer
          // Validate min / max for currency
          var currMin = element.getAttribute('data-min');
          var currMax = element.getAttribute('data-max');
          var numVal = parseFloat(rawValue);
          if (element.hasAttribute('required') && (rawValue === '' || rawValue === null || isNaN(numVal))) {
            element.closest('.sf-currency-input-group').style.border = '1px solid red';
            hasEmptyRequiredFields = true;
          } else if (!isNaN(numVal)) {
            if (currMin !== '' && currMin !== null && currMin !== undefined && numVal < parseFloat(currMin)) {
              element.closest('.sf-currency-input-group').style.border = '1px solid red';
              hasEmptyRequiredFields = true;
              errorMessage = element.name + ' must be at least ' + currMin + '.';
            } else if (currMax !== '' && currMax !== null && currMax !== undefined && numVal > parseFloat(currMax)) {
              element.closest('.sf-currency-input-group').style.border = '1px solid red';
              hasEmptyRequiredFields = true;
              errorMessage = element.name + ' must not exceed ' + currMax + '.';
            } else {
              element.closest('.sf-currency-input-group').style.border = '';
            }
          } else {
            element.closest('.sf-currency-input-group').style.border = '';
          }
        } else if (element.classList && element.classList.contains('sf-calculation-input')) {
          var calcRaw = element.getAttribute('data-raw-value');
          formDataObject[element.name] = (calcRaw !== null && calcRaw !== undefined) ? calcRaw : element.value;
          if (element.hasAttribute('required') && (calcRaw === '' || calcRaw === null || calcRaw === undefined)) {
            element.style.border = '1px solid red';
            hasEmptyRequiredFields = true;
          } else {
            element.style.border = '';
          }
        } else if (element.classList && element.classList.contains('sf-phone-combined-value')) {
          if (element.name) formDataObject[element.name] = element.value || '';
          var parentPhoneGrp = element.closest('.sf-phone-group');
          var actualPhoneInput = parentPhoneGrp ? parentPhoneGrp.querySelector('.sf-phone-input') : null;
          if (actualPhoneInput && actualPhoneInput.hasAttribute('required') && (!element.value || element.value.trim() === '')) {
            if (parentPhoneGrp) parentPhoneGrp.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
          } else if (parentPhoneGrp) {
            parentPhoneGrp.style.outline = '';
          }
        } else if (element.classList && element.classList.contains('sf-password-combined-value')) {
          if (element.name) formDataObject[element.name] = element.value || '';
        } else if (element.classList && element.classList.contains('sf-star-rating-value')) {
          if (element.name) formDataObject[element.name] = element.value || '';
          var parentStarGrp = element.closest('.sf-star-rating-group');
          if (element.hasAttribute('required') && (!element.value || element.value.trim() === '' || element.value === '0')) {
            if (parentStarGrp) parentStarGrp.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
          } else if (parentStarGrp) {
            parentStarGrp.style.outline = '';
          }
        } else if (element.classList && element.classList.contains('sf-nps-value')) {
          if (element.name) formDataObject[element.name] = element.value || '';
          var parentNpsGrp = element.closest('.sf-nps-group');
          if (element.hasAttribute('required') && (!element.value || element.value.trim() === '')) {
            if (parentNpsGrp) parentNpsGrp.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
          } else if (parentNpsGrp) {
            parentNpsGrp.style.outline = '';
          }
        } else if (element.classList && element.classList.contains('sf-multiselect-value')) {
          if (element.name) formDataObject[element.name] = element.value || '';
          var parentMultiGrp = element.closest('.sf-multiselect-group');
          if (element.hasAttribute('required') && (!element.value || element.value.trim() === '')) {
            if (parentMultiGrp) parentMultiGrp.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
          } else if (parentMultiGrp) {
            parentMultiGrp.style.outline = '';
          }
        } else if (element.classList && element.classList.contains('sf-richtext-value')) {
          if (element.name) formDataObject[element.name] = element.value || '';
          var parentRtGrp = element.closest('.sf-richtext-editor');
          if (element.hasAttribute('required') && (!element.value || element.value.trim() === '')) {
            if (parentRtGrp) parentRtGrp.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
          } else if (parentRtGrp) {
            parentRtGrp.style.outline = '';
          }
        } else if (element.classList && element.classList.contains('sf-otp-value')) {
          if (element.name) formDataObject[element.name] = element.value || '';
          var parentOtpGrp = element.closest('.sf-email-otp-group');
          if (element.hasAttribute('required') && (!element.value || element.value.trim() === '')) {
            if (parentOtpGrp) parentOtpGrp.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
          } else if (parentOtpGrp) {
            parentOtpGrp.style.outline = '';
          }
        } else if (element.classList && element.classList.contains('sf-chained-aggregate-value')) {
          if (element.name) formDataObject[element.name] = element.value || '';
          var parentChainGrp = element.closest('.sf-chained-select-group');
          var firstLevelSel = parentChainGrp ? parentChainGrp.querySelector('select.sf-chained-level') : null;
          if (firstLevelSel && firstLevelSel.hasAttribute('required') && (!element.value || element.value.trim() === '')) {
            if (parentChainGrp) parentChainGrp.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
          } else if (parentChainGrp) {
            parentChainGrp.style.outline = '';
          }
        } else if (element.classList && element.classList.contains('sf-color-value')) {
          if (element.name) formDataObject[element.name] = element.value || '';
        } else if (element.classList && element.classList.contains('sf-currency-input')) {
          var unformatted = unformatCurrencyNumber(element.value);
          formDataObject[element.name] = unformatted !== '' ? unformatted : (element.value || '');
          if (element.hasAttribute('required') && (!formDataObject[element.name] || String(formDataObject[element.name]).trim() === '')) {
            element.style.border = '1px solid red';
            hasEmptyRequiredFields = true;
          } else {
            element.style.border = '';
          }
        } else if (element.classList && element.classList.contains('sf-repeater-row-template')) {
          // Skip the template's internal elements
        } else {
          // Handle all other input types (text, email, hidden syncs for username/quiz/post etc.)
          formDataObject[element.name] = element.value;
        }

        // Check if the field is required and empty
        if (element.type !== 'file' && !element.classList.contains('sf-currency-input') && element.hasAttribute('required') && (!formDataObject[element.name] || String(formDataObject[element.name]).trim() === '')) {
          // Add a red border to indicate the required field is empty
          element.style.border = '1px solid red';
          hasEmptyRequiredFields = true;
        } else if (element.type !== 'file' && !element.classList.contains('sf-currency-input') && !element.hasAttribute('required')) {
          // Remove any red border if the field is not empty
          element.style.border = '';
        }

        // Add email validation
        if (element.type === 'email' && element.value && !isValidEmail(element.value)) {
          // Add a red border to indicate an invalid email
          element.style.border = '1px solid red';
          errorMessage = 'Email is not correct.';
          hasEmptyRequiredFields = true;
        }

        // Add number validation
        if (element.type === 'number' && element.value !== '') {
          const min = element.getAttribute('min');
          const max = element.getAttribute('max');

          if (!isValidNumber(element.value, min, max)) {
            element.style.border = '1px solid red';
            hasEmptyRequiredFields = true;

            if (!(/^-?\d+(\.\d+)?$/.test(element.value))) {
              errorMessage = 'Please enter a valid number.';
            } else if (min !== '' || max !== '') {
              if (min !== '' && max !== '') {
                errorMessage = `Number must be between ${min} and ${max}.`;
              } else if (min !== '') {
                errorMessage = `Number must be at least ${min}.`;
              } else {
                errorMessage = `Number must not exceed ${max}.`;
              }
            }
          }
        }

        //custom captch
        if (element.name.endsWith('_correct')) {
          const captchaFieldName = element.name.replace('_correct', '');
          const captchaUserAnswer = formDataObject[captchaFieldName];
          const captchaCorrectAnswer = element.value;

          if (captchaUserAnswer !== captchaCorrectAnswer) {
            // Display an error message for the custom captcha
            errorMessage = 'Wrong captcha, please fill in the correct answer.';
            hasEmptyRequiredFields = true;
          }
        }

        //Canvas to image create to store the image in databse as a link
        if (element.classList.contains('signature-data')) {
          var canvasId = element.dataset.canvasId;
          var signatureCanvas = document.getElementById(canvasId);
          // console.log(signatureCanvas);
          // Capture signature data as a base64-encoded PNG image
          var signatureData = signatureCanvas.toDataURL('image/png');
          // console.log(signatureData)
          // Don't URL encode - FormData handles it correctly and keeps data:image/png;base64, prefix intact
          formDataObject[element.name] = signatureData;
          // console.log(formDataObject[element.name]);
        }

      });


      // ============================================================================
      // POST-PROCESS: Complex field aggregates (Likert, Repeater) + validations
      // ============================================================================

      // --- Likert: Aggregate per-row radios into an object under the base field name ---
      (function () {
        var likeTables = formContainer.querySelectorAll('.sf-likert-table');
        likeTables.forEach(function (tbl) {
          var baseName = tbl.getAttribute('data-base-name');
          if (!baseName) return;
          var result = {};
          var hasAny = false;
          var isRequired = tbl.getAttribute('data-required') === 'true';
          var rows = tbl.querySelectorAll('tbody .sf-likert-row');
          rows.forEach(function (row) {
            var rowId = row.getAttribute('data-row-id');
            if (!rowId) return;
            var selRadio = row.querySelector('input[type="radio"]:checked');
            if (selRadio) { result[rowId] = selRadio.value; hasAny = true; }
            else result[rowId] = '';
          });
          if (Object.keys(result).length > 0) {
            formDataObject[baseName] = JSON.stringify(result);
          }
          if (isRequired && !hasAny) {
            tbl.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
            errorMessage = errorMessage || 'Please answer all Likert scale questions.';
          } else {
            tbl.style.outline = '';
          }
        });
      })();

      // --- Repeater: Restructure flat indexed keys into array of row objects + validation ---
      (function () {
        var repeaters = formContainer.querySelectorAll('.sf-repeater-group');
        repeaters.forEach(function (grp) {
          var baseName = grp.getAttribute('data-name');
          var minRows = parseInt(grp.getAttribute('data-min') || '1', 10);
          var isRequired = grp.getAttribute('data-required') === 'true';
          if (!baseName) return;
          var rowsMap = {};
          // Collect keys matching baseName[i][sub] from the existing form data
          Object.keys(formDataObject).forEach(function (key) {
            var re = new RegExp('^' + baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\[(\\d+)\\]\\[([^\\]]+)\\]$');
            var match = key.match(re);
            if (match) {
              var i = parseInt(match[1], 10);
              var sub = match[2];
              if (!rowsMap[i]) rowsMap[i] = {};
              rowsMap[i][sub] = formDataObject[key];
              delete formDataObject[key];
            }
          });
          var ordered = [];
          Object.keys(rowsMap).sort(function (a, b) { return parseInt(a, 10) - parseInt(b, 10); })
            .forEach(function (k) { ordered.push(rowsMap[k]); });
          formDataObject[baseName] = JSON.stringify(ordered);
          // Required / min rows validation - only validate if field is marked as required
          if (isRequired && ordered.length < minRows) {
            grp.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
            errorMessage = errorMessage || ('Please add at least ' + minRows + ' row(s).');
          } else {
            grp.style.outline = '';
          }
        });
      })();

      // --- Password Confirm: match validation + required ---
      (function () {
        var groups = formContainer.querySelectorAll('.sf-password-confirm-group');
        groups.forEach(function (grp) {
          var pwd = grp.querySelector('.sf-password-input');
          var confirm = grp.querySelector('.sf-password-confirm-input');
          var isRequired = pwd && pwd.hasAttribute('required');
          if (isRequired && (!pwd.value || pwd.value.trim() === '')) {
            pwd.style.border = '1px solid red';
            hasEmptyRequiredFields = true;
          }
          if (pwd && confirm && pwd.value && confirm.value && pwd.value !== confirm.value) {
            pwd.style.border = '1px solid red';
            confirm.style.border = '1px solid red';
            hasEmptyRequiredFields = true;
            errorMessage = errorMessage || 'Passwords do not match.';
          }
        });
      })();

      // --- Post Select: required validation on dropdown ---
      (function () {
        var groups = formContainer.querySelectorAll('.sf-post-select-group');
        groups.forEach(function (grp) {
          var sel = grp.querySelector('.sf-post-select-dropdown');
          if (!sel) return;
          var isRequired = sel.hasAttribute('required');
          if (isRequired && (!sel.value || sel.value.trim() === '')) {
            grp.style.outline = '1px solid red';
            hasEmptyRequiredFields = true;
          } else {
            grp.style.outline = '';
          }
        });
      })();

      // --- File Upload: error validation + required check ---
      (function () {
        var dropzones = formContainer.querySelectorAll('.sf-file-dropzone');
        dropzones.forEach(function (dropzone) {
          var input = dropzone.querySelector('input[type="file"]');
          var inputId = dropzone.getAttribute('data-input-id');
          var errorMsg = formContainer.querySelector('#error-' + inputId) || (dropzone.parentElement ? dropzone.parentElement.querySelector('.sf-file-error-msg') : null);
          var hasError = dropzone.getAttribute('data-has-error') === 'true';
          var customErrorMsg = dropzone.getAttribute('data-error-msg');

          if (hasError) {
            dropzone.style.borderColor = '#ef4444';
            if (errorMsg) {
              if (customErrorMsg) errorMsg.textContent = customErrorMsg;
              errorMsg.style.display = 'block';
            }
            hasEmptyRequiredFields = true;
            errorMessage = errorMessage || customErrorMsg || (errorMsg ? errorMsg.textContent : 'File type not allowed.');
            return;
          }

          var isRequired = (input && input.hasAttribute('required')) ||
                           dropzone.getAttribute('data-required') === 'true' ||
                           (dropzone.hasAttribute('data-required') && dropzone.getAttribute('data-required') !== 'false');

          var wrapper = dropzone.closest('.sf-file-upload-wrapper');
          if (!isRequired && wrapper) {
            var label = wrapper.querySelector('label');
            if (label && label.querySelector('.required, .sf-required-star')) {
              isRequired = true;
            }
          }

          var fileCount = (input && input.files) ? input.files.length : 0;
          if (isRequired && fileCount === 0) {
            dropzone.style.borderColor = '#ef4444';
            if (errorMsg) {
              errorMsg.textContent = 'Please upload a file.';
              errorMsg.style.display = 'block';
            }
            hasEmptyRequiredFields = true;
            errorMessage = errorMessage || 'Please upload the required file.';
          } else if (!hasError) {
            dropzone.style.borderColor = '';
            if (errorMsg && errorMsg.textContent === 'Please upload a file.') {
              errorMsg.style.display = 'none';
              errorMsg.textContent = '';
            }
          }
        });
      })();

      // If there are empty required fields, prevent form submission
      if (hasEmptyRequiredFields || errorMessage) {
        submitButton.innerText = originalButtonText;
        // alert('Please fill out all required fields.');
        errorDiv.textContent = errorMessage || 'Please fill out all required fields.';
        errorDiv.style.display = 'block';
        return;
      } else {
        // Clear the error message if there are no errors
        errorDiv.textContent = '';
        errorDiv.style.display = 'none';
      }

      // Create a FormData object for form submission (supports text & binary files)
      var xhr = new XMLHttpRequest();
      xhr.open('POST', front_end_data.admin_ajax, true);

      var formDataPayload = new FormData();
      formDataPayload.append('action', 'simpleform_get_submit_data');
      formDataPayload.append('nonce', nonce);
      formDataPayload.append('id', formId);
      formDataPayload.append('form_data', JSON.stringify(formDataObject));
      formDataPayload.append('current_page_url', window.location.href);

      // Append selected file(s) from all file inputs in formContainer
      var fileInputs = formContainer.querySelectorAll('input[type="file"]');
      fileInputs.forEach(function (fileInput) {
        if (fileInput.name && fileInput.files && fileInput.files.length > 0) {
          for (var f = 0; f < fileInput.files.length; f++) {
            formDataPayload.append(fileInput.name + '[]', fileInput.files[f]);
          }
        }
      });

      xhr.onreadystatechange = function () {
        if (xhr.readyState === 4) {

          // Set button text back to the original text
          submitButton.innerText = originalButtonText;

          if (xhr.status === 200) {
            var responseData = JSON.parse(xhr.responseText);

            // // console.log('Response:', responseData);

            // Check if the form should be closed after submission

            var shouldCloseForm = formContainer.dataset.close === "true";

            if (responseData.success) {
              errorDiv.textContent = '';
              errorDiv.style.display = 'none';

              // Clear the form fields
              var form = formContainer.querySelector('.simple_form');
              if (form) {
                form.reset();

                // Clear any stored local draft for this form
                try {
                  localStorage.removeItem('simpleform_draft_' + formId);
                  var draftStatusMsg = formContainer.querySelector('.sf-draft-status-msg, .sf-draft-message');
                  if (draftStatusMsg) draftStatusMsg.textContent = '';
                } catch (err) {}

                // form.reset() doesn't clear canvas elements — reset all signature canvases manually
                form.querySelectorAll('.signature-canvas').forEach(function(canvas) {
                  var ctx = canvas.getContext('2d');
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                  // Also clear the associated hidden input
                  var hiddenInput = form.querySelector('.signature-data[data-canvas-id="' + canvas.id + '"]');
                  if (hiddenInput) hiddenInput.value = '';
                });
              }


              // Close the form if required
              if (shouldCloseForm) {
                formContent.classList.remove('active');
                formContent.style.height = '0';
              }


              //Rest captch and add new value 
              var captchaField = form.querySelector('[name^="custom-captcha-input"]');
              var captchaCorrectField = form.querySelector('[name^="custom-captcha-input-"][name$="_correct"]');
              // Generate a random operation and operands
              var captchaOperation = generateRandomOperation();
              // console.log(captchaOperation);
              // Update the UI with the new captcha operation
              var captchaSpan = form.querySelector('[class^="question-"]');

              if (captchaSpan) {
                captchaSpan.textContent = `${captchaOperation.operand1} ${captchaOperation.operation} ${captchaOperation.operand2} =`;
                // Set the correct value
                captchaCorrectField.value = eval(`${captchaOperation.operand1} ${captchaOperation.operation} ${captchaOperation.operand2}`);
                // Clear the user's input
                captchaField.value = '';
              }


              /**
               * WhatsApp redirection
               */
              if (responseData.data && responseData.data.whatsapp_settings) {
                handleWhatsAppRedirection(responseData.data.whatsapp_settings, formDataObject, formId);
              }

              // Track form success analytics
              trackFormEvent(formId, 'form_success', {
                has_redirect: responseData.data && responseData.data.redirect_url && responseData.data.redirect_url !== '',
                submission_time: new Date().toISOString()
              });

              // Check for thank you page redirect
              if (responseData.data && responseData.data.redirect_url && responseData.data.redirect_url !== '') {
                // Track redirect analytics
                trackFormEvent(formId, 'form_redirect', {
                  redirect_url: responseData.data.redirect_url,
                  open_new_tab: responseData.data.open_new_tab || false
                });

                // Check if redirect should open in new tab
                if (responseData.data.open_new_tab) {
                  // Open in new tab
                  window.open(responseData.data.redirect_url, '_blank');

                  // Show success modal since we're not redirecting the current page
                  showCustomModal(
                    'Thanks for your submission',
                    'We will contact you soon <br> Have a nice day.',
                    3000 // Duration in milliseconds (3 seconds)
                  );
                } else {
                  // Redirect in current tab
                  window.location.href = responseData.data.redirect_url;
                  return; // Exit early to prevent showing the modal
                }
              }

              // Clear file upload dropzones and file inputs
              var fileDropzones = formContainer.querySelectorAll('.sf-file-dropzone');
              fileDropzones.forEach(function (dz) {
                var input = dz.querySelector('input[type="file"]');
                var previewList = dz.querySelector('.sf-file-preview-list');
                if (input) input.value = '';
                if (previewList) previewList.innerHTML = '';
              });

              showCustomModal(
                'Thanks for your submission',
                'We will contact you soon <br> Have a nice day.',
                3000 // Duration in milliseconds (3 seconds)
              );

            } else {
              // Track form error analytics
              trackFormEvent(formId, 'form_error', {
                error_message: responseData.data ? responseData.data.message : 'Unknown error',
                error_time: new Date().toISOString()
              });

              var errorMsg = (responseData.data && responseData.data.message) ? responseData.data.message : (responseData.message || 'Submission failed. Please check the fields and try again.');
              errorDiv.textContent = errorMsg;
              errorDiv.style.display = 'block';
            }

          } else {
            // Handle the error for this form
            // // console.log('Form', formId, 'Error:', xhr.statusText);
          }
        }
      };

      // Collect Honeypot value (field has no name attr — queried by data attribute)
      var hpInput = formContainer.querySelector('input[data-sf-hp="1"]');
      if (hpInput) {
        formDataPayload.append('sf_hp', hpInput.value);
        formDataObject['sf_hp'] = hpInput.value;
      }

      // Collect captcha response tokens from DOM if present
      var gResp = formContainer.querySelector('[name="g-recaptcha-response"]');
      if (gResp && gResp.value) {
        formDataPayload.append('g-recaptcha-response', gResp.value);
        formDataObject['g-recaptcha-response'] = gResp.value;
      }
      var hResp = formContainer.querySelector('[name="h-captcha-response"]');
      if (hResp && hResp.value) {
        formDataPayload.append('h-captcha-response', hResp.value);
        formDataObject['h-captcha-response'] = hResp.value;
      }
      var cfResp = formContainer.querySelector('[name="cf-turnstile-response"]');
      if (cfResp && cfResp.value) {
        formDataPayload.append('cf-turnstile-response', cfResp.value);
        formDataObject['cf-turnstile-response'] = cfResp.value;
      }

      formDataPayload.set('form_data', JSON.stringify(formDataObject));

      // Handle reCAPTCHA v3 async execution if present
      var v3El = formContainer.querySelector('.sf-recaptcha-v3-token');
      var v3Key = v3El ? (v3El.getAttribute('data-sitekey') || (window.front_end_data && window.front_end_data.captcha_keys ? window.front_end_data.captcha_keys.recaptcha_v3_site_key : '')) : '';

      if (v3El && v3Key && window.grecaptcha && typeof window.grecaptcha.execute === 'function') {
        try {
          window.grecaptcha.execute(v3Key, { action: 'submit' }).then(function(token) {
            v3El.value = token;
            formDataPayload.set('g-recaptcha-response', token);
            formDataObject['g-recaptcha-response'] = token;
            formDataPayload.set('form_data', JSON.stringify(formDataObject));
            xhr.send(formDataPayload);
          }).catch(function() {
            xhr.send(formDataPayload);
          });
        } catch(e) {
          xhr.send(formDataPayload);
        }
      } else {
        xhr.send(formDataPayload);
      }
    });
  });
});


//after submit rest and add new vlaue to captch
function generateRandomOperation() {
  const operations = ['+', '-', '*'];
  // const operations = ['+', '-', '*', '/'];
  const operation = operations[Math.floor(Math.random() * operations.length)];

  let operand1, operand2;
  if (operation === '/') {
    operand2 = Math.floor(Math.random() * 9) + 1; // Ensure operand2 is not zero
    operand1 = operand2 * (Math.floor(Math.random() * 9) + 1);
  } else {
    operand1 = Math.floor(Math.random() * 10);
    operand2 = Math.floor(Math.random() * 10);
  }

  return { operation, operand1, operand2 };
}


// Helper function to validate email
function isValidEmail(email) {
  var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email);
}

// Helper function to validate number
function isValidNumber(value, min, max) {
  if (value === '' || value === undefined || value === null) {
    return true;
  }
  // Allow integers, negative numbers, and decimals
  var numberPattern = /^-?\d+(\.\d+)?$/;
  if (!numberPattern.test(String(value).trim())) {
    return false;
  }

  // Convert to number for min/max comparison
  const numValue = parseFloat(value);

  // Check min constraint if present
  if (min !== undefined && min !== '' && numValue < parseFloat(min)) {
    return false;
  }

  // Check max constraint if present
  if (max !== undefined && max !== '' && numValue > parseFloat(max)) {
    return false;
  }

  return true;
}


function setCookie(name, value, days) {
  let expires = "";

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${name}=${value || ""}${expires}; path=/`;
}

function getCookie(name) {
  const cookieStr = decodeURIComponent(document.cookie);
  const cookies = cookieStr.split("; ");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];

    if (cookie.startsWith(`${name}=`)) {
      return cookie.substring(name.length + 1);
    }
  }
  return "";
}

function eraseCookie(name) {
  setCookie(name, "", -1);
}

// END ---------------------------------------------------


/**
 *  Floating form action 
 */
window.addEventListener('load', function () {
  // Get references to the WhatsApp icon and the form content
  const whatsappIcon = document.querySelector('.whatsapp-icon');
  const formContent = document.querySelector('.form-content');
  const closeButton = document.querySelector('.sf-close');
  const containerFloat = document.querySelector('.simple_form_container_floating');

  // Toggle the "active" class and adjust the height of the form content when the icon is clicked
  if (whatsappIcon) {
    whatsappIcon.addEventListener('click', function () {
      formContent.classList.toggle('active');
      adjustFormHeight(formContent);
    });
  }

  // Close the form when the close button is clicked
  if (closeButton) {
    closeButton.addEventListener('click', function () {
      formContent.classList.remove('active');
      formContent.style.height = '0';
    });
  }

  // Check if the form should be initially open

  if (containerFloat) {
    var OpenForm = containerFloat.dataset.open === "true";
    // console.log(containerFloat.dataset)
    if (OpenForm) {
      formContent.classList.add('active');
      adjustFormHeight(formContent);
    }
  }



  function adjustFormHeight(element) {
    const isActive = element.classList.contains('active');
    if (isActive) {
      // Calculate the full height of the form content
      // element.style.height = element.scrollHeight + 'px'; // prev used
      element.style.height = 'auto';
      element.style.maxHeight = '520px';

      if (containerFloat) {
        containerFloat.style.overflowY = 'auto';
        containerFloat.style.maxHeight = '465px';
      }

    } else {
      // Set the height back to 0 for collapsing effect
      element.style.height = '0';
    }
  }

});


// Function to restart the animation
function restartAnimation() {
  const whatsappIcon = document.getElementById('jumping-whatsapp');
  if (whatsappIcon) {
    whatsappIcon.style.animation = 'none';
    // Trigger reflow
    void whatsappIcon.offsetWidth;
    // Set animation duration to 5 seconds
    whatsappIcon.style.animation = 'jumpAnimation 0.9s ease-in-out';
  }

}

// Initially, trigger the animation 4-5 times with a delay
let initialAnimationCount = 0;
// Random value between 4 and 5
const maxInitialAnimationCount = Math.floor(Math.random() * 2) + 1;

function initialAnimations() {
  if (initialAnimationCount < maxInitialAnimationCount) {
    restartAnimation();
    initialAnimationCount++;
    setTimeout(initialAnimations, 200); // Adjust the delay as needed
  }
}

// Start initial animations
initialAnimations();

// After the initial animations, continue with the 10-second animation cycle
setInterval(restartAnimation, 3000); // Restart every

// END 

//Analytics tracking function — only runs when Pro is active AND analytics tracking is enabled in Settings
function trackFormEvent(formId, eventType, eventData = {}) {
  // Guard: Pro must be active AND admin must have enabled tracking in General Settings
  if ( ! front_end_data.analyticsEnabled ) {
    return;
  }

  // Get user information
  const userAgent = navigator.userAgent;
  const pageUrl = window.location.href;
  
  // Now simply get the page URL and user agent only 
  const data = new FormData();
  data.append('action', 'simpleform_track_event');
  data.append('nonce', front_end_data.nonce);
  data.append('form_id', formId);
  data.append('event_type', eventType);
  data.append('event_data', JSON.stringify(eventData));
  data.append('user_agent', userAgent);
  data.append('page_url', pageUrl);

  // Send analytics data
  fetch(front_end_data.admin_ajax, {
    method: 'POST',
    body: data
  }).catch(function (error) {
    // console.log('Analytics tracking error:', error);
  });
}

// Track form view when form is loaded
document.addEventListener('DOMContentLoaded', function () {
  const formContainers = document.querySelectorAll('.simple_form_container');

  formContainers.forEach(function (container) {
    const formId = container.getAttribute('data-form-id');
    if (formId) {
      // Track form view
      trackFormEvent(formId, 'form_view', {
        form_theme: container.getAttribute('data-form-theme'),
        form_position: container.getAttribute('data-position') || 'inline'
      });
    }
  });
});

// Function to add navigation functionality for multistep forms
function addNavigationFunctionality(container) {
  const pages = container.querySelectorAll('.page');
  const nextButtons = container.querySelectorAll('.next-btn');
  const prevButtons = container.querySelectorAll('.prev-btn');

  if (pages.length <= 1) return; // No need for navigation if only one page

  // Show first page initially
  pages.forEach((page, index) => {
    page.style.display = index === 0 ? 'block' : 'none';
  });

  // Add click handlers for next buttons
  nextButtons.forEach(button => {
    button.addEventListener('click', function () {
      const targetPage = parseInt(this.getAttribute('data-page'));
      showPage(targetPage, pages, container);
    });
  });

  // Add click handlers for prev buttons
  prevButtons.forEach(button => {
    button.addEventListener('click', function () {
      const targetPage = parseInt(this.getAttribute('data-page'));
      showPage(targetPage, pages, container);
    });
  });
}

// Function to show a specific page
function showPage(pageNumber, pages, container) {
  pages.forEach((page, index) => {
    page.style.display = (index + 1) === pageNumber ? 'block' : 'none';
  });

  // Update progress indicator
  updateProgressIndicator(container, pageNumber);
}

// Function to add conditional logic functionality
function addConditionalLogicFunctionality(container) {
  const conditionalFields = container.querySelectorAll('[data-conditional-logic]');

  if (conditionalFields.length === 0) return;

  // // console.log('Setting up conditional logic for', conditionalFields.length, 'fields');

  // Debug: List all fields with data-field-id
  const allFieldsWithId = container.querySelectorAll('[data-field-id]');
  /* // console.log('All fields with data-field-id:', Array.from(allFieldsWithId).map(f => ({
    id: f.getAttribute('data-field-id'),
    tag: f.tagName,
    type: f.type || 'N/A',
    name: f.name || 'N/A'
  }))); */

  // Add change listeners to all form inputs
  const allInputs = container.querySelectorAll('input, select, textarea');
  // // console.log('Found', allInputs.length, 'input elements');

  allInputs.forEach(input => {
    // Use both 'input' and 'change' events for real-time updates
    input.addEventListener('input', function () {
      // // console.log('Input event triggered on:', input.getAttribute('data-field-id'), 'value:', input.value);
      evaluateConditionalLogic(container);
    });
    input.addEventListener('change', function () {
      // // console.log('Change event triggered on:', input.getAttribute('data-field-id'), 'value:', input.value);
      evaluateConditionalLogic(container);
    });
    // Also listen for keyup for immediate feedback
    input.addEventListener('keyup', function () {
      evaluateConditionalLogic(container);
    });
  });

  // Initial evaluation
  setTimeout(() => {
    evaluateConditionalLogic(container);
  }, 100);
}

// Function to evaluate conditional logic for all fields
function evaluateConditionalLogic(container) {
  const conditionalFields = container.querySelectorAll('[data-conditional-logic]');

  conditionalFields.forEach(field => {
    try {
      const logicData = JSON.parse(field.getAttribute('data-conditional-logic'));

      // Skip if conditional logic is not enabled
      if (!logicData.enabled || logicData.enabled === 'false' || logicData.enabled === false) {
        return;
      }

      const shouldShow = evaluateConditions(
        logicData.conditions,
        logicData.action,
        container,
        logicData.conditionType || 'all'
      );

    
      if (shouldShow) {
        field.classList.remove('hidden');
        field.style.display = '';
      } else {
        field.classList.add('hidden');
        field.style.display = 'none';
      }
    } catch (error) {
      console.error('Error evaluating conditional logic:', error, field);
    }
  });
}

// Function to evaluate individual conditions
function evaluateConditions(conditions, action, container, conditionType = 'all') {
  if (!conditions || conditions.length === 0) return true;

  const results = [];

  for (const condition of conditions) {
    // Try multiple selectors to find the target field
    let targetField = null;

    // First try to find the input/select/textarea directly
    targetField = container.querySelector(`input[data-field-id="${condition.field}"], select[data-field-id="${condition.field}"], textarea[data-field-id="${condition.field}"]`);

    // If not found, try to find the container div and then the input inside
    if (!targetField) {
      const containerDiv = container.querySelector(`[data-field-id="${condition.field}"]`);
      if (containerDiv) {
        targetField = containerDiv.querySelector('input, select, textarea');
      }
    }

    // Last resort - try by ID
    if (!targetField) {
      targetField = container.querySelector(`#${condition.field}`);
    }

    if (!targetField) {
      // Target field not found
      results.push(false);
      continue;
    }

    const fieldValue = getFieldValue(targetField);
    const conditionMet = evaluateSingleCondition(fieldValue, condition.operator, condition.value);

    results.push(conditionMet === true);
  }

  // Apply AND or OR based on conditionType
  const conditionsMet = (conditionType === 'any')
    ? results.some(r => r === true)   // OR: at least one condition is true
    : results.every(r => r === true); // AND: all conditions must be true (default)

  // Return true if we should show the field
  return action === 'show' ? conditionsMet : !conditionsMet;
}

// Function to get field value based on field type
function getFieldValue(field) {
  if (!field) return '';

  // Helper: find the closest known value-carrying wrapper and return its value directly
  function resolveFromWrapper(wrapper) {
    if (!wrapper || wrapper.nodeType !== 1) return null;

    // Star Rating
    if (wrapper.classList && wrapper.classList.contains('sf-star-rating-group')) {
      var h = wrapper.querySelector('.sf-star-rating-value');
      return (h ? h.value : '') || '';
    }
    // NPS
    if (wrapper.classList && wrapper.classList.contains('sf-nps-group')) {
      var npsH = wrapper.querySelector('.sf-nps-value');
      return (npsH ? npsH.value : '') || '';
    }
    // Rich Text
    if (wrapper.classList && wrapper.classList.contains('sf-richtext-editor')) {
      var rtH = wrapper.querySelector('.sf-richtext-value');
      return (rtH ? rtH.value : '') || '';
    }
    // Multiselect
    if (wrapper.classList && wrapper.classList.contains('sf-multiselect-group')) {
      var msH = wrapper.querySelector('.sf-multiselect-value');
      return (msH ? msH.value : '') || '';
    }
    // Email OTP
    if (wrapper.classList && wrapper.classList.contains('sf-email-otp-group')) {
      var otpH = wrapper.querySelector('.sf-otp-value');
      return (otpH ? otpH.value : '') || '';
    }
    // Coupon (return code, if applied)
    if (wrapper.classList && wrapper.classList.contains('sf-coupon-group')) {
      var couponH = wrapper.querySelector('.sf-coupon-code-value');
      return (couponH ? couponH.value : '') || '';
    }
    // Calculation (return raw numeric)
    if (wrapper.classList && wrapper.classList.contains('sf-calculation-group')) {
      var inp = wrapper.querySelector('.sf-calculation-input');
      if (inp) {
        var raw = inp.getAttribute('data-raw-value');
        return (raw !== null && raw !== undefined) ? raw : (inp.value || '');
      }
      return '';
    }
    // Chained select (return aggregate pipe string)
    if (wrapper.classList && wrapper.classList.contains('sf-chained-select-group')) {
      var csH = wrapper.querySelector('.sf-chained-aggregate-value');
      return (csH ? csH.value : '') || '';
    }
    // Enhanced color
    if (wrapper.classList && wrapper.classList.contains('sf-enhanced-color-group')) {
      var colH = wrapper.querySelector('.sf-color-value');
      return (colH ? colH.value : '') || '';
    }
    // Phone (return combined)
    if (wrapper.classList && wrapper.classList.contains('sf-phone-group')) {
      var phH = wrapper.querySelector('.sf-phone-combined-value');
      if (phH && phH.value) return phH.value;
      var telIn = wrapper.querySelector('.sf-phone-input');
      return telIn ? (telIn.value || '') : '';
    }
    // Password Confirm (return pwd only if matching, else '')
    if (wrapper.classList && wrapper.classList.contains('sf-password-confirm-group')) {
      var pwIn = wrapper.querySelector('.sf-password-input');
      var confIn = wrapper.querySelector('.sf-password-confirm-input');
      if (pwIn && confIn && pwIn.value && confIn.value && pwIn.value === confIn.value) return pwIn.value;
      if (pwIn) return pwIn.value || '';
      return '';
    }
    // Username (return input text + status for logic)
    if (wrapper.classList && wrapper.classList.contains('sf-username-group')) {
      var unIn = wrapper.querySelector('.sf-username-input');
      return unIn ? (unIn.value || '') : '';
    }
    // Quiz Score (return e.g. "85%")
    if (wrapper.classList && wrapper.classList.contains('sf-quiz-score-card')) {
      var qsH = wrapper.querySelector('.sf-quiz-score-value');
      var qrH = wrapper.querySelector('.sf-quiz-result-value');
      var parts = [];
      if (qsH && qsH.value) parts.push(qsH.value);
      if (qrH && qrH.value) parts.push(qrH.value);
      return parts.join('|');
    }
    // Post Select
    if (wrapper.classList && wrapper.classList.contains('sf-post-select-group')) {
      var postSel = wrapper.querySelector('.sf-post-select-dropdown');
      return postSel ? (postSel.value || '') : '';
    }
    // Likert Table (return JSON of row answers - same as submission)
    if (wrapper.classList && wrapper.classList.contains('sf-likert-table')) {
      var baseName = wrapper.getAttribute('data-base-name');
      var result = {};
      var hasAny = false;
      var rows = wrapper.querySelectorAll('tbody .sf-likert-row');
      rows.forEach(function (row) {
        var rowId = row.getAttribute('data-row-id');
        if (!rowId) return;
        var selRadio = row.querySelector('input[type="radio"]:checked');
        if (selRadio) { result[rowId] = selRadio.value; hasAny = true; }
        else result[rowId] = '';
      });
      return hasAny ? JSON.stringify(result) : '';
    }
    // Repeater (return JSON array of rows - same as submission)
    if (wrapper.classList && wrapper.classList.contains('sf-repeater-group')) {
      var baseName2 = wrapper.getAttribute('data-name');
      var form = wrapper.closest('.simple_form_container, form');
      if (!form || !baseName2) return '';
      var allInputs = wrapper.querySelectorAll('input, select, textarea');
      var rm = {};
      allInputs.forEach(function (el) {
        if (!el.name) return;
        var re = new RegExp('^' + baseName2.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\[(\\d+)\\]\\[([^\\]]+)\\]$');
        var m = el.name.match(re);
        if (!m) return;
        var idx = parseInt(m[1], 10);
        var sub = m[2];
        var useVal = el.value;
        if (el.type === 'radio' || el.type === 'checkbox') {
          if (!el.checked) return;
          useVal = el.value;
        }
        if (!rm[idx]) rm[idx] = {};
        rm[idx][sub] = useVal;
      });
      var ordered = [];
      Object.keys(rm).sort(function (a, b) { return parseInt(a, 10) - parseInt(b, 10); })
        .forEach(function (k) { ordered.push(rm[k]); });
      return ordered.length > 0 ? JSON.stringify(ordered) : '';
    }
    // Image Choice (return selected values)
    if (wrapper.classList && wrapper.classList.contains('sf-image-select-group')) {
      var radios = wrapper.querySelectorAll('input[type="radio"]:checked');
      var checks = wrapper.querySelectorAll('input[type="checkbox"]:checked');
      if (radios.length > 0) return radios[0].value;
      return Array.from(checks).map(function (c) { return c.value; }).join(',');
    }
    // Range Output (return native input value)
    if (wrapper.classList && wrapper.classList.contains('.sf-range-output-group')) {
      var rIn = wrapper.querySelector('.sf-range-input');
      return rIn ? (rIn.value || '') : '';
    }
    // Entry Preview / Draft Button / HTML content are display only - return ''
    if (wrapper.classList && (
      wrapper.classList.contains('sf-entry-preview-card') ||
      wrapper.classList.contains('sf-draft-button-group') ||
      wrapper.classList.contains('sf-content-html-block')
    )) {
      return '';
    }
    return null;
  }

  // Try wrapper resolution on the input itself first, or any ancestor that is a known group
  if (field.nodeType === 1) {
    var knownClasses = [
      'sf-star-rating-group','sf-nps-group','sf-richtext-editor','sf-multiselect-group',
      'sf-email-otp-group','sf-coupon-group','sf-calculation-group','sf-chained-select-group',
      'sf-enhanced-color-group','sf-phone-group','sf-password-confirm-group','sf-username-group',
      'sf-quiz-score-card','sf-post-select-group','sf-likert-table','sf-repeater-group',
      'sf-image-select-group','sf-range-output-group','sf-entry-preview-card','sf-draft-button-group',
      'sf-content-html-block'
    ];
    // Check if field itself is a wrapper
    var selfWrap = resolveFromWrapper(field);
    if (selfWrap !== null) return selfWrap;
    // Check closest ancestor wrapper
    for (var c = 0; c < knownClasses.length; c++) {
      var w = field.closest('.' + knownClasses[c]);
      if (w) {
        var wr = resolveFromWrapper(w);
        if (wr !== null) return wr;
      }
    }
  }

  // If it's a container div, find the actual input inside it
  if (field.tagName === 'DIV') {
    const input = field.querySelector('input, select, textarea');
    if (input) {
      field = input;
    } else {
      return '';
    }
  }

  if (field.type === 'checkbox') {
    if (field.classList && field.classList.contains('switch-input')) {
      return field.checked ? (field.value || 'on') : '';
    } else if (field.classList && field.classList.contains('sf-toggle-input')) {
      var tglTrue = field.getAttribute('data-true-value') || 'true';
      var tglFalse = field.getAttribute('data-false-value') || 'false';
      return field.checked ? tglTrue : tglFalse;
    } else if (field.classList && (field.classList.contains('sf-gdpr-input') || field.classList.contains('sf-terms-input'))) {
      return field.checked ? 'agreed' : 'disagreed';
    } else {
      const checkboxes = document.querySelectorAll(`input[name="${field.name}"]:checked`);
      const values = Array.from(checkboxes).map(cb => cb.value);
      return values.join(',');
    }
  } else if (field.type === 'radio') {
    const radioGroup = document.querySelectorAll(`input[name="${field.name}"]:checked`);
    return radioGroup.length > 0 ? radioGroup[0].value : '';
  } else if (field.type === 'range' && field.classList && field.classList.contains('sf-range-input')) {
    return field.value || '';
  } else if (field.classList && field.classList.contains('sf-currency-input')) {
    var rawCur = field.getAttribute('data-raw-value');
    if (rawCur !== null && rawCur !== undefined) return rawCur;
    return field.value || '';
  } else if (field.classList && field.classList.contains('sf-calculation-input')) {
    var rawCalc = field.getAttribute('data-raw-value');
    if (rawCalc !== null && rawCalc !== undefined) return rawCalc;
    return field.value || '';
  } else if (field.tagName === 'SELECT') {
    return field.value || '';
  } else if (field.tagName === 'TEXTAREA') {
    return field.value || '';
  } else if (field.tagName === 'INPUT') {
    return field.value || '';
  } else {
    return '';
  }
}

// Function to evaluate a single condition
function evaluateSingleCondition(fieldValue, operator, conditionValue) {
  switch (operator) {
    case 'equals':
      return fieldValue === conditionValue;
    case 'not_equals':
      return fieldValue !== conditionValue;
    case 'contains':
      return fieldValue.toLowerCase().includes(conditionValue.toLowerCase());
    case 'not_contains':
      return !fieldValue.toLowerCase().includes(conditionValue.toLowerCase());
    case 'is_empty':
      return fieldValue === '' || fieldValue === null || fieldValue === undefined;
    case 'is_not_empty':
      return fieldValue !== '' && fieldValue !== null && fieldValue !== undefined;
    default:
      return true;
  }
}

// Function to add progress indicator functionality
function addProgressIndicatorFunctionality(container) {
  const progressIndicator = container.querySelector('#progress-indicator');
  if (!progressIndicator) return;

  // Initial update
  updateProgressIndicator(container, 1);
}

// Function to update progress indicator
function updateProgressIndicator(container, currentPage) {
  const progressIndicator = container.querySelector('#progress-indicator');
  if (!progressIndicator) return;

  const pages = container.querySelectorAll('.page');
  const totalPages = pages.length;

  // Update label
  const label = progressIndicator.querySelector('.progress-label');
  if (label) {
    const labelText = label.textContent.split(' ')[0]; // Get the first word (e.g., "Step")
    label.textContent = `${labelText} ${currentPage} of ${totalPages}`;
  }

  // Update dots
  const dots = progressIndicator.querySelectorAll('.progress-dot');
  dots.forEach((dot, index) => {
    const pageNum = index + 1;
    dot.className = 'progress-dot';
    if (pageNum < currentPage) {
      dot.classList.add('completed');
    } else if (pageNum === currentPage) {
      dot.classList.add('active');
    }
  });

  // Update numbers
  const numbers = progressIndicator.querySelectorAll('.progress-number');
  numbers.forEach((number, index) => {
    const pageNum = index + 1;
    number.className = 'progress-number';
    if (pageNum < currentPage) {
      number.classList.add('completed');
    } else if (pageNum === currentPage) {
      number.classList.add('active');
    }
  });

  // Update progress bar
  const progressBar = progressIndicator.querySelector('.progress-bar');
  if (progressBar) {
    const percentage = totalPages > 1 ? ((currentPage - 1) / (totalPages - 1)) * 100 : 100;
    progressBar.style.width = `${percentage}%`;
  }
}

/**
 * Build WhatsApp URL with form data and settings
 * 
 * @param {object} settings - WhatsApp settings from form
 * @param {object} formData - Form submission data
 * @param {string} formId - Form ID
 * @returns {string} - WhatsApp URL
 */
function buildWhatsAppUrl(settings, formData, formId) {
  let message = settings.whatsapp_message || 'New form submission';
  
  // Replace placeholders
  const replacements = {
    '{FormName}': settings.form_name || '',
    '{PageURL}': window.location.href,
    '{Date}': new Date().toLocaleDateString(),
    '{Time}': new Date().toLocaleTimeString(),
    '{FormID}': formId || ''
  };
  
  for (const [placeholder, value] of Object.entries(replacements)) {
    const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
    message = message.replace(regex, value);
  }
  
  // Use PHP-prepared form_fields (captcha stripped, esignatures as WP URLs) when available
  const fieldsToUse = (settings.form_fields && typeof settings.form_fields === 'object')
    ? settings.form_fields
    : formData;

  // Include form data if enabled
  if (settings.whatsapp_include_leads && fieldsToUse) {
    message += '\n\n--- Form Data ---\n';
    for (const [key, value] of Object.entries(fieldsToUse)) {
      // When falling back to raw formData, apply client-side filtering
      if (!settings.form_fields) {
        if (key === 'action' || key === 'nonce' || key === 'form_id') continue;
        if (
          key === 'cf-turnstile-response' ||
          key === 'g-recaptcha-response' ||
          key === 'h-captcha-response' ||
          key.toLowerCase().includes('custom-captcha-input') ||
          key.endsWith('_correct')
        ) continue;
        if (key.startsWith('esignature-')) {
          message += `${key}: [Signature — view in email]\n`;
          continue;
        }
      }
      message += `${key}: ${value}\n`;
    }
  }
  
  // Include metadata if enabled
  if (settings.whatsapp_include_metadata) {
    message += '\n\n--- Metadata ---\n';
    message += `Page: ${window.location.href}\n`;
    message += `Date: ${new Date().toLocaleDateString()}\n`;
    message += `Time: ${new Date().toLocaleTimeString()}\n`;
  }
  
  // Clean phone number (remove spaces, dashes, parentheses, etc.)
  const cleanNumber = settings.whatsapp_number.replace(/[^0-9+]/g, '');
  
  // Encode message for URL
  const encodedMessage = encodeURIComponent(message);
  
  // Detect mobile vs desktop and build appropriate URL
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // Mobile: Use wa.me format
    return `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
  } else {
    // Desktop: Use web.whatsapp.com format
    return `https://web.whatsapp.com/send?phone=${cleanNumber}&text=${encodedMessage}`;
  }
}

/**
 * Handle WhatsApp redirection after form submission
 * 
 * @param {object} settings - WhatsApp settings from form
 * @param {object} formData - Form submission data
 * @param {string} formId - Form ID
 */
function handleWhatsAppRedirection(settings, formData, formId) {
  // Check if WhatsApp redirection is enabled and number is provided
  if (!settings || !settings.whatsapp_redirect_enabled || !settings.whatsapp_number) {
    return;
  }
  
  try {
    const whatsappUrl = buildWhatsAppUrl(settings, formData, formId);
    
    // Small delay to ensure form submission completes
    setTimeout(() => {
      if (settings.whatsapp_open_new_tab) {
        window.open(whatsappUrl, '_blank');
      } else {
        window.location.href = whatsappUrl;
      }
    }, 500);
  } catch (error) {
    console.error('WhatsApp redirection error:', error);
  }
}

/**
 * Initialize drag-and-drop file upload handlers and client-side validations
 * 
 * @param {HTMLElement} formContainer 
 */
function initFileUploadHandlers(formContainer) {
  if (!formContainer) return;
  var dropzones = formContainer.querySelectorAll('.sf-file-dropzone');

  dropzones.forEach(function (dropzone) {
    var inputId = dropzone.getAttribute('data-input-id');
    var input = dropzone.querySelector('input[type="file"]');
    var previewList = dropzone.querySelector('.sf-file-preview-list');
    var errorMsg = formContainer.querySelector('#error-' + inputId) || (dropzone.parentElement ? dropzone.parentElement.querySelector('.sf-file-error-msg') : null);
    var maxMB = parseFloat(dropzone.getAttribute('data-max-size')) || 5;
    var maxSizeBytes = maxMB * 1024 * 1024;
    var preset = dropzone.getAttribute('data-preset') || 'all';
    var customTypes = dropzone.getAttribute('data-custom-types') || '';
    var isMultiple = dropzone.getAttribute('data-upload-mode') === 'multiple';
    var maxFilesLimit = parseInt(dropzone.getAttribute('data-max-files')) || 5;

    // Track active file items: array of { file: File, error: string|null }
    var currentTrackedFiles = [];

    // Strict list of blocked executable / hazardous file extensions
    var dangerousExts = ['php', 'phtml', 'php3', 'php4', 'php5', 'phps', 'exe', 'pl', 'py', 'cgi', 'asp', 'aspx', 'js', 'sh', 'bat', 'cmd', 'vbs', 'htm', 'html', 'htaccess'];

    function getPresetAllowedExts(presetType, customStr) {
      if (presetType === 'images') return ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      if (presetType === 'documents') return ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'md'];
      if (presetType === 'audio') return ['mp3', 'wav', 'ogg'];
      if (presetType === 'video') return ['mp4', 'avi', 'mov', 'mkv'];
      if (presetType === 'archive') return ['zip', 'rar', '7z', 'tar', 'gz'];
      if (presetType === 'custom' && customStr) {
        return customStr.split(',').map(function (s) { return s.trim().toLowerCase().replace(/^\./, ''); }).filter(Boolean);
      }
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'md', 'mp3', 'wav', 'ogg', 'mp4', 'avi', 'mov', 'mkv', 'zip', 'rar', '7z', 'tar', 'gz'];
    }

    var allowedExts = getPresetAllowedExts(preset, customTypes);

    function formatBytes(bytes) {
      if (bytes === 0) return '0 Bytes';
      var k = 1024;
      var sizes = ['Bytes', 'KB', 'MB', 'GB'];
      var i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    dropzone.addEventListener('click', function (e) {
      if (e.target.classList.contains('sf-file-remove') || e.target.closest('.sf-file-remove')) {
        return;
      }
      input.click();
    });

    ['dragenter', 'dragover'].forEach(function (eventName) {
      dropzone.addEventListener(eventName, function (e) {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.add('sf-drag-active');
      }, false);
    });

    ['dragleave', 'drop'].forEach(function (eventName) {
      dropzone.addEventListener(eventName, function (e) {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.remove('sf-drag-active');
      }, false);
    });

    dropzone.addEventListener('drop', function (e) {
      var dt = e.dataTransfer;
      var files = dt ? dt.files : null;
      if (files && files.length > 0) {
        processIncomingFiles(files, isMultiple);
      }
    });

    input.addEventListener('change', function () {
      processIncomingFiles(input.files, isMultiple);
    });

    function processIncomingFiles(newFileList, isAppend) {
      var newFiles = Array.from(newFileList || []);
      if (newFiles.length === 0) return;

      var workingFiles = isAppend && isMultiple ? currentTrackedFiles.map(function(item) { return item.file; }) : [];

      if (!isMultiple) {
        workingFiles = [newFiles[0]];
      } else {
        newFiles.forEach(function (nf) {
          var alreadyExists = workingFiles.some(function (wf) {
            return wf.name === nf.name && wf.size === nf.size && wf.lastModified === nf.lastModified;
          });
          if (!alreadyExists) {
            workingFiles.push(nf);
          }
        });
      }

      if (isMultiple && workingFiles.length > maxFilesLimit) {
        workingFiles = workingFiles.slice(0, maxFilesLimit);
      }

      validateAndRenderTrackedFiles(workingFiles);
    }

    function syncInputFiles() {
      if (typeof DataTransfer !== 'undefined') {
        var dt = new DataTransfer();
        currentTrackedFiles.forEach(function (item) {
          if (!item.error && item.file) {
            dt.items.add(item.file);
          }
        });
        input.files = dt.files;
      }
    }

    function validateAndRenderTrackedFiles(fileList) {
      currentTrackedFiles = [];
      var hasError = false;
      var firstError = '';

      if (fileList.length === 0) {
        dropzone.removeAttribute('data-has-error');
        dropzone.removeAttribute('data-error-msg');
        dropzone.style.borderColor = '';
        if (errorMsg) {
          errorMsg.textContent = '';
          errorMsg.style.display = 'none';
        }
        if (previewList) previewList.innerHTML = '';
        syncInputFiles();
        return;
      }

      if (isMultiple && fileList.length > maxFilesLimit) {
        hasError = true;
        firstError = 'Maximum ' + maxFilesLimit + ' files allowed.';
      }

      fileList.forEach(function (file) {
        var ext = file.name.split('.').pop().toLowerCase();
        var itemErr = '';

        if (dangerousExts.indexOf(ext) !== -1) {
          itemErr = 'File type .' + ext + ' is not allowed for security reasons.';
        } else if (allowedExts.length > 0 && allowedExts.indexOf(ext) === -1) {
          var extUpper = allowedExts.map(function(e) { return e.toUpperCase(); });
          var allowedFormatted = '';
          if (extUpper.length > 7) {
            var chunks = [];
            for (var c = 0; c < extUpper.length; c += 7) {
              chunks.push(extUpper.slice(c, c + 7).join(', '));
            }
            allowedFormatted = chunks.join(',\n');
          } else {
            allowedFormatted = extUpper.join(', ');
          }
          itemErr = 'File type .' + ext + ' is not allowed.\nAllowed:\n' + allowedFormatted;
        } else if (file.size > maxSizeBytes) {
          itemErr = 'File "' + file.name + '" exceeds maximum size of ' + maxMB + 'MB.';
        }

        if (itemErr) {
          hasError = true;
          if (!firstError) firstError = itemErr;
        }

        currentTrackedFiles.push({
          file: file,
          error: itemErr || null
        });
      });

      if (hasError) {
        dropzone.setAttribute('data-has-error', 'true');
        dropzone.setAttribute('data-error-msg', firstError);
        dropzone.style.borderColor = '#ef4444';
        if (errorMsg) {
          errorMsg.textContent = firstError;
          errorMsg.style.whiteSpace = 'pre-line';
          errorMsg.style.wordBreak = 'break-word';
          errorMsg.style.overflowWrap = 'break-word';
          errorMsg.style.lineHeight = '1.4';
          errorMsg.style.display = 'block';
        }
      } else {
        dropzone.removeAttribute('data-has-error');
        dropzone.removeAttribute('data-error-msg');
        dropzone.style.borderColor = '';
        if (errorMsg) {
          errorMsg.textContent = '';
          errorMsg.style.display = 'none';
        }
      }

      syncInputFiles();
      renderPreviews(currentTrackedFiles);
    }

    function renderPreviews(items) {
      if (!previewList) return;
      previewList.innerHTML = '';
      items.forEach(function (itemData, index) {
        var file = itemData.file || itemData;
        var hasItemError = Boolean(itemData.error);
        var item = document.createElement('div');
        item.className = 'sf-file-item' + (hasItemError ? ' sf-file-item-error' : '');
        if (hasItemError) {
          item.style.border = '1px solid #ef4444';
          item.style.backgroundColor = '#fef2f2';
        }

        var isImage = !hasItemError && (file.type.indexOf('image/') === 0 || /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name));

        var fileVisual = '<span class="sf-file-icon" style="font-size: 20px;">' + (hasItemError ? '⚠️' : '📄') + '</span>';
        if (isImage) {
          try {
            var objectUrl = URL.createObjectURL(file);
            fileVisual = `<img src="${objectUrl}" alt="${file.name}" class="sf-file-thumb-img" style="width: 38px; height: 38px; object-fit: cover; border-radius: 6px; border: 1px solid #cbd5e1; flex-shrink: 0;" />`;
          } catch (e) {
            fileVisual = '<span class="sf-file-icon" style="font-size: 20px;">🖼️</span>';
          }
        }

        var errorNote = hasItemError ? `<span class="sf-file-item-err-text" style="font-size: 11px; color: #ef4444; font-weight: 500; white-space: pre-line; word-break: break-word; overflow-wrap: break-word; line-height: 1.3;">${itemData.error}</span>` : '';

        item.innerHTML = `
          <div class="sf-file-info" style="display: flex; align-items: center; gap: 10px;">
            ${fileVisual}
            <div style="display: flex; flex-direction: column; overflow: hidden;">
              <span class="sf-file-name" title="${file.name}" style="font-weight: 600; color: ${hasItemError ? '#b91c1c' : '#1e293b'}; font-size: 13px; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; max-width: 180px;">${file.name}</span>
              <span class="sf-file-size" style="font-size: 11px; color: #64748b;">${formatBytes(file.size)}</span>
              ${errorNote}
            </div>
          </div>
          <span class="sf-file-remove" data-index="${index}" title="Remove file" style="color: #ef4444; cursor: pointer; font-weight: bold; font-size: 16px; padding: 2px 6px; border-radius: 4px;">✕</span>
        `;

        item.querySelector('.sf-file-remove').addEventListener('click', function (e) {
          e.stopPropagation();
          removeFileAt(index);
        });

        previewList.appendChild(item);
      });
    }

    function removeFileAt(indexToRemove) {
      if (indexToRemove >= 0 && indexToRemove < currentTrackedFiles.length) {
        currentTrackedFiles.splice(indexToRemove, 1);
      }
      var remainingFiles = currentTrackedFiles.map(function (item) { return item.file; });
      input.value = '';
      validateAndRenderTrackedFiles(remainingFiles);
    }
  });
}

// ============================================================================
// NEW FIELD TYPES INITIALIZATION
// ============================================================================

/**
 * Universal helper to reliably retrieve the form ID from any child element or container
 */
function getContainerFormId(container, element) {
  if (element && element.getAttribute) {
    var elemFid = parseInt(element.getAttribute('data-form-id') || '0', 10);
    if (elemFid > 0) return elemFid;
  }
  var sfContainer = (element && element.closest && element.closest('.simple_form_container')) ||
                    (container && container.closest && container.closest('.simple_form_container')) ||
                    (container && container.classList && container.classList.contains('simple_form_container') ? container : null) ||
                    document.querySelector('.simple_form_container');
  if (sfContainer && sfContainer.dataset && sfContainer.dataset.formId) {
    var fid = parseInt(sfContainer.dataset.formId, 10);
    if (fid > 0) return fid;
  }
  var hiddenFormId = document.querySelector('input[name="id"], input[name="form_id"]');
  if (hiddenFormId && hiddenFormId.value) {
    var hfid = parseInt(hiddenFormId.value, 10);
    if (hfid > 0) return hfid;
  }
  return 0;
}

/**
 * Initialize Toggle / Switch fields
 */
function initToggleFields(formContainer) {
  if (!formContainer) return;
  var toggleInputs = formContainer.querySelectorAll('.sf-toggle-input');
  // console.log('[SimpleForm] initToggleFields: Found', toggleInputs.length, 'toggle inputs');
  toggleInputs.forEach(function (input) {
    var label = input.closest('label');
    var slider = input.parentElement.querySelector('.sf-toggle-slider');
    var knob = slider ? slider.querySelector('.sf-toggle-knob') : null;
    var statusLabel = label ? label.querySelector('.sf-toggle-status') : null;
    var checkedLabel = input.getAttribute('data-checked-label') || 'On';
    var uncheckedLabel = input.getAttribute('data-unchecked-label') || 'Off';

    // console.log('[SimpleForm] initToggleFields: Setting up toggle', input.id, 'label:', label ? 'found' : 'not found');

    function updateVisual() {
      var isChecked = input.checked;
      if (slider) {
        slider.style.backgroundColor = isChecked ? '#10b981' : '#d1d5db';
      }
      if (knob) {
        knob.style.left = isChecked ? '24px' : '3px';
      }
      if (statusLabel) {
        statusLabel.textContent = isChecked ? checkedLabel : uncheckedLabel;
      }
    }

    input.addEventListener('change', function () {
      // console.log('[SimpleForm] Toggle changed:', input.id, 'checked:', input.checked);
      updateVisual();
    });

    updateVisual();
  });
}

/**
 * Initialize Terms & Conditions collapse/expand
 */
function initTermsFields(formContainer) {
  if (!formContainer) return;
  var termsWraps = formContainer.querySelectorAll('.sf-terms-wrap');
  termsWraps.forEach(function (wrap) {
    var header = wrap.querySelector('.sf-terms-header');
    var content = wrap.querySelector('.sf-terms-content');
    var hint = wrap.querySelector('.sf-terms-hint');
    var chevron = wrap.querySelector('.sf-terms-chevron');

    if (header && content) {
      header.addEventListener('click', function () {
        var isHidden = content.style.display === 'none' || content.getAttribute('data-collapsed') === 'true';
        if (isHidden) {
          content.style.display = 'block';
          header.style.borderBottom = '1px solid #e5e7eb';
          if (hint) hint.textContent = 'Scrollable';
          if (chevron) chevron.style.transform = 'rotate(180deg)';
          content.setAttribute('data-collapsed', 'false');
        } else {
          content.style.display = 'none';
          header.style.borderBottom = 'none';
          if (hint) hint.textContent = 'Click to expand';
          if (chevron) chevron.style.transform = 'rotate(0deg)';
          content.setAttribute('data-collapsed', 'true');
        }
      });
    }
  });
}

/**
 * Currency formatting utilities
 */
function formatCurrencyNumber(numStr, options) {
  numStr = String(numStr || '').replace(/[^\d.\-,]/g, '');
  if (!numStr || numStr === '-' || numStr === '.') return numStr;

  var decimalPlaces = options.decimalPlaces ? parseInt(options.decimalPlaces) : 2;
  var decSep = options.decimalSep || '.';
  var thouSep = options.thousandSep || ',';

  // Normalize to standard format with . as decimal
  var normalized = numStr.split(thouSep).join('');
  if (decSep !== '.') normalized = normalized.replace(decSep, '.');

  // Split into integer / decimal parts
  var parts = normalized.split('.');
  var intPart = parts[0];
  var decPart = parts.length > 1 ? parts[1] : '';

  // Remove non-digit from integer (keep leading -)
  var negative = intPart.startsWith('-');
  intPart = intPart.replace(/[^\d]/g, '');
  if (negative) intPart = '-' + intPart;

  // Trim decimal to allowed places
  if (decPart.length > decimalPlaces) {
    decPart = decPart.substring(0, decimalPlaces);
  }

  // Add thousand separators to integer part
  var absInt = Math.abs(parseInt(intPart || '0', 10));
  if (!isNaN(absInt)) {
    var formattedInt = absInt.toLocaleString('en-US').replace(/,/g, thouSep);
    if (negative) formattedInt = '-' + formattedInt;
    intPart = formattedInt;
  }

  return decPart !== '' || (normalized.indexOf('.') > -1)
    ? intPart + decSep + decPart
    : intPart;
}

function unformatCurrencyNumber(formattedStr) {
  if (!formattedStr) return '';
  return String(formattedStr).replace(/[^\d.\-]/g, '');
}

/**
 * Initialize Currency fields (input formatting)
 */
function initCurrencyFields(formContainer) {
  if (!formContainer) return;
  var currencyInputs = formContainer.querySelectorAll('.sf-currency-input');
  currencyInputs.forEach(function (input) {
    var options = {
      decimalPlaces: input.getAttribute('data-decimal-places'),
      decimalSep: input.getAttribute('data-decimal-sep'),
      thousandSep: input.getAttribute('data-thousand-sep'),
      min: input.getAttribute('data-min'),
      max: input.getAttribute('data-max')
    };

    // Apply format on input
    input.addEventListener('input', function (e) {
      var caret = input.selectionStart;
      var beforeLen = input.value.length;
      var beforeDot = input.value.substring(0, caret).split(options.decimalSep || '.').length - 1;
      var beforeSepCount = (input.value.substring(0, caret).match(new RegExp((options.thousandSep || ',').replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;

      input.value = formatCurrencyNumber(input.value, options);

      // Try to restore caret position roughly
      var afterLen = input.value.length;
      var afterSepCount = (input.value.substring(0, caret).match(new RegExp((options.thousandSep || ',').replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
      var newCaret = caret + (afterLen - beforeLen) + (afterSepCount - beforeSepCount);
      try { input.setSelectionRange(Math.max(0, newCaret), Math.max(0, newCaret)); } catch (err) {}
    });

    // Store raw numeric value in data attribute on form submit
    input.setAttribute('data-raw-value', unformatCurrencyNumber(input.value));
    input.addEventListener('change', function () {
      input.setAttribute('data-raw-value', unformatCurrencyNumber(input.value));
    });
  });
}

/**
 * Initialize International Phone fields (dial code select + tel input)
 */
function initPhoneCountryFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-phone-group');
  // console.log('[SimpleForm] initPhoneCountryFields: Found', groups.length, 'phone groups');
  groups.forEach(function (grp) {
    var dialSelect = grp.querySelector('.sf-phone-dialcode');
    var telInput = grp.querySelector('.sf-phone-input');
    // console.log('[SimpleForm] Phone group - dialSelect:', dialSelect ? 'found' : 'not found', 'telInput:', telInput ? 'found' : 'not found');
    
    if (dialSelect && dialSelect.options.length <= 1) {
      // console.log('[SimpleForm] Populating phone dial codes for', dialSelect.id);
      var defaultCountries = [
        { code: 'us', dial: '+1', name: 'United States' },
        { code: 'gb', dial: '+44', name: 'United Kingdom' },
        { code: 'ca', dial: '+1', name: 'Canada' },
        { code: 'au', dial: '+61', name: 'Australia' },
        { code: 'in', dial: '+91', name: 'India' },
        { code: 'de', dial: '+49', name: 'Germany' },
        { code: 'fr', dial: '+33', name: 'France' },
        { code: 'br', dial: '+55', name: 'Brazil' },
        { code: 'jp', dial: '+81', name: 'Japan' },
        { code: 'cn', dial: '+86', name: 'China' },
        { code: 'mx', dial: '+52', name: 'Mexico' },
        { code: 'es', dial: '+34', name: 'Spain' },
        { code: 'it', dial: '+39', name: 'Italy' },
        { code: 'nl', dial: '+31', name: 'Netherlands' },
        { code: 'se', dial: '+46', name: 'Sweden' }
      ];
      defaultCountries.forEach(function (c) {
        var opt = document.createElement('option');
        opt.value = c.code;
        opt.setAttribute('data-dial', c.dial);
        opt.textContent = c.dial + ' ' + c.name;
        dialSelect.appendChild(opt);
      });
      var defCountry = dialSelect.getAttribute('data-default-country') || 'us';
      dialSelect.value = defCountry;
      // console.log('[SimpleForm] Phone dial codes populated, default:', defCountry);
    }
    // Sync full phone number to combined value hidden input if present
    var combineInput = grp.querySelector('.sf-phone-combined-value');
    function syncCombined() {
      if (!combineInput) return;
      var dial = '';
      if (dialSelect && dialSelect.selectedOptions && dialSelect.selectedOptions[0]) {
        dial = dialSelect.selectedOptions[0].getAttribute('data-dial') || dialSelect.value;
      }
      var numberPart = (telInput && telInput.value) ? telInput.value : '';
      combineInput.value = (dial + numberPart).trim();
    }
    if (dialSelect) dialSelect.addEventListener('change', syncCombined);
    if (telInput) telInput.addEventListener('input', syncCombined);
    syncCombined();
  });
}

/**
 * Initialize Country dropdown fields - populate with list
 */
function initCountrySelectFields(formContainer) {
  if (!formContainer) return;
  var selects = formContainer.querySelectorAll('.sf-country-select, .sf-country-dropdown');
  // console.log('[SimpleForm] initCountrySelectFields: Found', selects.length, 'country selects');
  var countries = [
    'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia','Austria',
    'Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan','Bolivia',
    'Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi','Cabo Verde','Cambodia',
    'Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo','Costa Rica',
    'Croatia','Cuba','Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic','Ecuador','Egypt',
    'El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini','Ethiopia','Fiji','Finland','France','Gabon','Gambia',
    'Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana','Haiti','Honduras',
    'Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Jamaica','Japan','Jordan',
    'Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan','Laos','Latvia','Lebanon','Lesotho','Liberia','Libya',
    'Liechtenstein','Lithuania','Luxembourg','Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands',
    'Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique',
    'Myanmar','Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Macedonia',
    'Norway','Oman','Pakistan','Palau','Palestine','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland',
    'Portugal','Qatar','Romania','Russia','Rwanda','Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines',
    'Samoa','San Marino','Sao Tome and Principe','Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore',
    'Slovakia','Slovenia','Solomon Islands','Somalia','South Africa','South Korea','South Sudan','Spain','Sri Lanka',
    'Sudan','Suriname','Sweden','Switzerland','Syria','Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo',
    'Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu','Uganda','Ukraine','United Arab Emirates',
    'United Kingdom','United States','Uruguay','Uzbekistan','Vanuatu','Vatican City','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe'
  ];
  selects.forEach(function (sel) {
    if (sel.options.length > 1) {
      // console.log('[SimpleForm] Country select already populated:', sel.id);
      return;
    }
    // console.log('[SimpleForm] Populating country select:', sel.id);
    var ph = sel.getAttribute('data-placeholder') || 'Select a country';
    var phOpt = document.createElement('option');
    phOpt.value = '';
    phOpt.textContent = ph;
    sel.appendChild(phOpt);
    countries.forEach(function (c) {
      var o = document.createElement('option');
      o.value = c;
      o.textContent = c;
      sel.appendChild(o);
    });
    // Set default country
    var defCountry = sel.getAttribute('data-default') || 'United States';
    sel.value = defCountry;
    // console.log('[SimpleForm] Country select populated with', countries.length, 'countries, default:', defCountry);
  });
}

/**
 * Initialize Rich Text / WYSIWYG editor fields
 */
function initRichTextFields(formContainer) {
  if (!formContainer) return;
  var editors = formContainer.querySelectorAll('.sf-richtext-editor');
  editors.forEach(function (ed) {
    var editor = ed.querySelector('.sf-richtext-content');
    var hidden = ed.querySelector('.sf-richtext-value');
    var buttons = ed.querySelectorAll('.sf-richtext-btn');
    if (!editor || !hidden) return;
    
    function sync() { 
      hidden.value = editor.innerHTML; 
    }
    
    editor.addEventListener('input', sync);
    editor.addEventListener('blur', sync);
    
    // Handle toolbar button clicks
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var cmd = btn.getAttribute('data-cmd');
        var val = btn.getAttribute('data-val') || null;
        
        // Focus the editor before executing command
        editor.focus();
        
        // Execute the command
        if (cmd === 'createLink') {
          var url = prompt('Enter URL:', 'https://');
          if (url) {
            try { document.execCommand(cmd, false, url); } catch (err) {}
          }
        } else if (cmd === 'formatBlock') {
          try { document.execCommand(cmd, false, val); } catch (err) {}
        } else {
          try { document.execCommand(cmd, false, val); } catch (err) {}
        }
        
        sync();
        
        // Maintain focus
        editor.focus();
      });
    });
    
    // Initialize content from hidden if present
    if (hidden.value && !editor.innerHTML.trim()) {
      editor.innerHTML = hidden.value;
    } else {
      sync();
    }
    
    // Add placeholder behavior
    var placeholder = editor.getAttribute('data-placeholder');
    if (placeholder) {
      editor.addEventListener('focus', function() {
        if (editor.innerHTML === '' || editor.innerHTML === '<br>') {
          editor.innerHTML = '';
        }
      });
      editor.addEventListener('blur', function() {
        if (editor.innerHTML === '' || editor.innerHTML === '<br>') {
          editor.innerHTML = '';
        }
      });
    }
  });
}

/**
 * Initialize Star Rating fields
 */
function initStarRatingFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-star-rating-group');
  groups.forEach(function (grp) {
    var stars = grp.querySelectorAll('.sf-star');
    var hidden = grp.querySelector('.sf-star-rating-value');
    var labels = (grp.getAttribute('data-labels') || '').split('|').filter(Boolean);
    var labelDisplay = grp.querySelector('.sf-star-rating-label');
    var activeColor = grp.getAttribute('data-active-color') || '#FFD700';
    var hoverColor = grp.getAttribute('data-hover-color') || '#fbbf24';
    var inactiveColor = grp.getAttribute('data-inactive-color') || '#d1d5db';

    function applyVisual(value) {
      stars.forEach(function (s, idx) {
        var starVal = parseInt(s.getAttribute('data-value') || (idx + 1), 10);
        s.style.color = starVal <= value ? activeColor : inactiveColor;
        s.style.cursor = 'pointer';
      });
      if (labelDisplay && labels && labels.length > 0) {
        labelDisplay.textContent = labels[value - 1] || '';
      }
    }
    
    stars.forEach(function (s) {
      s.addEventListener('mouseenter', function () {
        var v = parseInt(s.getAttribute('data-value'), 10);
        stars.forEach(function (ss, idx) {
          var sv = parseInt(ss.getAttribute('data-value') || (idx + 1), 10);
          ss.style.color = sv <= v ? hoverColor : inactiveColor;
        });
      });
      
      s.addEventListener('mouseleave', function () {
        var cur = parseInt(hidden ? hidden.value : '0', 10) || 0;
        applyVisual(cur);
      });
      
      s.addEventListener('click', function () {
        var v = parseInt(s.getAttribute('data-value'), 10);
        if (hidden) { 
          hidden.value = String(v); 
          hidden.dispatchEvent(new Event('change', { bubbles: true })); 
        }
        applyVisual(v);
        
        // Add visual feedback
        s.style.transform = 'scale(1.2)';
        setTimeout(function() {
          s.style.transform = 'scale(1)';
        }, 150);
      });
    });
    
    var init = parseInt(hidden ? hidden.value : '0', 10) || 0;
    applyVisual(init);
  });
}

/**
 * Initialize NPS (Net Promoter Score) fields
 */
function initNpsFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-nps-group');
  // console.log('[SimpleForm] initNpsFields: Found', groups.length, 'NPS groups');
  groups.forEach(function (grp) {
    var btns = grp.querySelectorAll('.sf-nps-btn');
    var hidden = grp.querySelector('.sf-nps-value');
    // console.log('[SimpleForm] NPS group - buttons:', btns.length, 'hidden:', hidden ? 'found' : 'not found');
    
    function applyVisual(value) {
      btns.forEach(function (b) {
        var bv = b.getAttribute('data-value');
        var isActive = String(bv) === String(value);
        var intBv = parseInt(bv, 10);
        var color = '#3b82f6';
        if (intBv >= 0 && intBv <= 6) color = '#ef4444';
        else if (intBv >= 7 && intBv <= 8) color = '#f59e0b';
        else if (intBv >= 9 && intBv <= 10) color = '#10b981';
        b.style.backgroundColor = isActive ? color : '#f3f4f6';
        b.style.color = isActive ? '#ffffff' : '#374151';
        b.style.borderColor = color;
      });
    }
    btns.forEach(function (b) {
      b.addEventListener('click', function () {
        // console.log('[SimpleForm] NPS button clicked, value:', b.getAttribute('data-value'));
        var v = b.getAttribute('data-value');
        if (hidden) { hidden.value = v; hidden.dispatchEvent(new Event('change', { bubbles: true })); }
        applyVisual(v);
      });
    });
    applyVisual(hidden ? hidden.value : '');
  });
}

/**
 * Initialize Likert scale fields (table of radio buttons - visual feedback)
 */
function initLikertFields(formContainer) {
  if (!formContainer) return;
  var tables = formContainer.querySelectorAll('.sf-likert-table');
  tables.forEach(function (tbl) {
    var fieldId = tbl.getAttribute('data-field-id');
    if (fieldId) {
      tbl.setAttribute('data-base-name', fieldId);
    }
    var rows = tbl.querySelectorAll('tbody .sf-likert-row');
    rows.forEach(function (row) {
      var cells = row.querySelectorAll('td');
      var radios = row.querySelectorAll('input[type="radio"]');
      function applyVisual() {
        cells.forEach(function (c) { c.style.backgroundColor = ''; });
        radios.forEach(function (r) {
          if (r.checked) {
            var td = r.closest('td');
            if (td) td.style.backgroundColor = '#dbeafe';
          }
        });
      }
      radios.forEach(function (r) { r.addEventListener('change', applyVisual); });
      applyVisual();
    });
  });
}

/**
 * Initialize Image Choice / Select fields (single or multiple)
 */
function initImageChoiceFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-image-select-group');
  groups.forEach(function (grp) {
    var items = grp.querySelectorAll('.sf-image-choice-item');
    items.forEach(function (item) {
      var input = item.querySelector('input[type="radio"], input[type="checkbox"]');
      function applyVisual() {
        if (input && input.checked) {
          item.style.borderColor = '#3b82f6';
          item.style.backgroundColor = '#eff6ff';
        } else {
          item.style.borderColor = '#e5e7eb';
          item.style.backgroundColor = '#ffffff';
        }
      }
      if (input) input.addEventListener('change', applyVisual);
      applyVisual();
    });
  });
}

/**
 * Initialize Password + Confirm fields (strength meter + match check)
 */
function initPasswordConfirmFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-password-confirm-group');
  groups.forEach(function (grp) {
    var pwd = grp.querySelector('.sf-password-input');
    var confirm = grp.querySelector('.sf-password-confirm-input');
    var meter = grp.querySelector('.sf-password-strength-bar');
    var label = grp.querySelector('.sf-password-strength-label');
    var matchMsg = grp.querySelector('.sf-password-match-label');
    var hidden = grp.querySelector('.sf-password-combined-value');
    if (!pwd) return;

    function calcStrength(pw) {
      if (!pw) return 0;
      var s = 0;
      if (pw.length >= 8) s += 1;
      if (/[a-z]/.test(pw)) s += 1;
      if (/[A-Z]/.test(pw)) s += 1;
      if (/\d/.test(pw)) s += 1;
      if (/[^a-zA-Z0-9]/.test(pw)) s += 1;
      return s;
    }
    function updateStrength() {
      var val = pwd.value || '';
      var st = calcStrength(val);
      if (meter) {
        var pct = (st / 5) * 100;
        meter.style.width = pct + '%';
        var color = '#ef4444';
        if (st >= 5) color = '#10b981';
        else if (st >= 4) color = '#3b82f6';
        else if (st >= 3) color = '#f59e0b';
        meter.style.backgroundColor = color;
      }
      if (label) {
        var txts = ['Too Short', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'];
        label.textContent = txts[st] || '';
      }
      updateMatch();
    }
    function updateMatch() {
      if (!confirm || !matchMsg) return;
      if (pwd.value && confirm.value) {
        if (pwd.value === confirm.value) {
          matchMsg.textContent = 'Passwords match';
          matchMsg.style.color = '#10b981';
        } else {
          matchMsg.textContent = 'Passwords do not match';
          matchMsg.style.color = '#ef4444';
        }
      } else {
        matchMsg.textContent = '';
      }
      if (hidden) hidden.value = pwd.value === confirm.value ? confirm.value : '';
    }
    pwd.addEventListener('input', updateStrength);
    if (confirm) confirm.addEventListener('input', updateMatch);
    updateStrength();
  });
}

/**
 * Initialize Repeater fields (add / remove rows, clone template)
 */
function initRepeaterFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-repeater-group');
  groups.forEach(function (grp) {
    var rowsContainer = grp.querySelector('.sf-repeater-rows');
    var template = grp.querySelector('.sf-repeater-row-template');
    var addBtn = grp.querySelector('.sf-repeater-add-btn');
    var maxRows = parseInt(grp.getAttribute('data-max') || '10', 10);
    var minRows = parseInt(grp.getAttribute('data-min') || '1', 10);
    var fieldId = grp.getAttribute('data-field-id');
    var rowLayout = grp.getAttribute('data-row-layout') || 'horizontal';
    
    if (!rowsContainer || !template) return;

    function indexRow(row, idx) {
      // Update row number display
      var rowNum = row.querySelector('.sf-repeater-row-num');
      if (rowNum) rowNum.textContent = idx + 1;
      
      // Update subfield names with proper indexing
      row.querySelectorAll('.sf-rep-subinput').forEach(function (input) {
        var sfId = input.getAttribute('data-sf-id');
        var sfName = input.getAttribute('data-sf-name');
        if (sfName) {
          input.name = fieldId + '[' + idx + '][' + sfName + ']';
        }
      });
      
      // Handle remove button
      row.querySelectorAll('.sf-repeater-remove-btn').forEach(function (rb) {
        rb.onclick = function (e) {
          e.preventDefault();
          var curRows = rowsContainer.querySelectorAll('.sf-repeater-row');
          if (curRows.length <= minRows) return;
          row.remove();
          reindex();
        };
      });
    }
    
    function reindex() {
      var all = rowsContainer.querySelectorAll('.sf-repeater-row');
      all.forEach(function (r, i) { indexRow(r, i); });
    }
    
    function addRow(populateDefaults) {
      var curRows = rowsContainer.querySelectorAll('.sf-repeater-row');
      if (curRows.length >= maxRows) return;
      var clone = template.content.firstElementChild.cloneNode(true);
      rowsContainer.appendChild(clone);
      reindex();
      return clone;
    }
    
    if (addBtn) addBtn.addEventListener('click', function (e) { 
      e.preventDefault(); 
      addRow(true); 
    });
    
    // Start with min rows
    var existing = rowsContainer.querySelectorAll('.sf-repeater-row');
    var start = existing.length;
    for (var i = start; i < minRows; i++) addRow(true);
    reindex();
  });
}

/**
 * Initialize Range Slider with Output fields (fill bar + output badge position)
 */
function initRangeOutputFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-range-output-group');
  groups.forEach(function (grp) {
    var input = grp.querySelector('.sf-range-input');
    var fill = grp.querySelector('.sf-range-fill');
    var output = grp.querySelector('.sf-range-output-badge');
    var prefix = grp.getAttribute('data-prefix') || '';
    var suffix = grp.getAttribute('data-suffix') || '';
    function update() {
      if (!input) return;
      var min = parseFloat(input.min || '0');
      var max = parseFloat(input.max || '100');
      var val = parseFloat(input.value || '0');
      var pct = max > min ? ((val - min) / (max - min)) * 100 : 0;
      if (fill) fill.style.width = pct + '%';
      if (output) output.textContent = prefix + val + suffix;
    }
    if (input) input.addEventListener('input', update);
    update();
  });
}

/**
 * Initialize Email OTP fields (frontend UI flow - send/verify/resend + timer)
 */
function initEmailOTPFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-email-otp-group');
  groups.forEach(function (grp) {
    var emailInput = grp.querySelector('.sf-otp-email-input');
    var sendBtn = grp.querySelector('.sf-otp-send-btn');
    var timerEl = grp.querySelector('.sf-otp-timer');
    var otpInputs = grp.querySelectorAll('.sf-otp-digit-input');
    var verifyBtn = grp.querySelector('.sf-otp-verify-btn');
    var resendBtn = grp.querySelector('.sf-otp-resend-btn');
    var statusEl = grp.querySelector('.sf-otp-status');
    var hidden = grp.querySelector('.sf-otp-value');
    var expirySeconds = parseInt(grp.getAttribute('data-expiry-seconds') || '180', 10);
    var otpLength = parseInt(grp.getAttribute('data-otp-length') || '6', 10);
    var formId = getContainerFormId(formContainer, grp);
    var timerId = null;

    function startTimer(useSeconds) {
      var secs = useSeconds || expirySeconds;
      if (timerId) clearInterval(timerId);
      if (timerEl) timerEl.style.display = 'inline-block';
      if (resendBtn) resendBtn.style.display = 'none';
      function tick() {
        if (timerEl) {
          var m = Math.floor(secs / 60);
          var s = secs % 60;
          timerEl.textContent = 'Expires in ' + m + ':' + (s < 10 ? '0' : '') + s;
        }
        if (secs <= 0) {
          clearInterval(timerId);
          if (timerEl) timerEl.style.display = 'none';
          if (resendBtn) resendBtn.style.display = 'inline-block';
        }
        secs--;
      }
      tick();
      timerId = setInterval(tick, 1000);
    }

    if (sendBtn) {
      sendBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (!emailInput || !emailInput.value) {
          if (statusEl) { statusEl.textContent = 'Please enter a valid email address.'; statusEl.style.color = '#ef4444'; }
          return;
        }
        if (statusEl) { statusEl.textContent = 'Sending OTP code...'; statusEl.style.color = '#3b82f6'; }
        if (sendBtn) sendBtn.disabled = true;
        sfPostAjax('simpleform_send_otp', {
          email: emailInput.value,
          form_id: formId,
          otp_length: otpLength,
          expiry_seconds: expirySeconds
        }, { message: 'OTP code has been sent to your email.', expires_in: expirySeconds, email_masked: emailInput.value })
          .then(function (res) {
            if (!res.success) {
              if (statusEl) { statusEl.textContent = (res.data && res.data.message) || res.message || 'Failed to send OTP.'; statusEl.style.color = '#ef4444'; }
              if (sendBtn) sendBtn.disabled = false;
              return;
            }
            var d = res.data || {};
            if (statusEl) { statusEl.textContent = d.message || 'OTP code sent.'; statusEl.style.color = '#10b981'; }
            if (sendBtn) sendBtn.style.display = 'none';
            if (sendBtn) sendBtn.disabled = false;
            var digitsWrap = grp.querySelector('.sf-otp-digits-wrap');
            if (digitsWrap) digitsWrap.style.display = 'flex';
            if (verifyBtn) verifyBtn.style.display = 'inline-block';
            if (resendBtn) resendBtn.style.display = 'none';
            if (otpInputs && otpInputs[0]) otpInputs[0].focus();
            startTimer(d.expires_in || expirySeconds);
          });
      });
    }

    otpInputs.forEach(function (inp, idx) {
      inp.addEventListener('input', function () {
        if (inp.value.length > 1) inp.value = inp.value.slice(-1);
        if (inp.value && idx < otpInputs.length - 1) otpInputs[idx + 1].focus();
        var combined = Array.from(otpInputs).map(function (d) { return d.value; }).join('');
        if (hidden) { hidden.value = combined; hidden.dispatchEvent(new Event('change', { bubbles: true })); }
      });
      inp.addEventListener('keydown', function (e) {
        if (e.key === 'Backspace' && !inp.value && idx > 0) otpInputs[idx - 1].focus();
      });
    });

    if (verifyBtn) {
      verifyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var combined = Array.from(otpInputs).map(function (d) { return d.value; }).join('');
        if (combined.length !== otpInputs.length) {
          if (statusEl) { statusEl.textContent = 'Please enter all ' + otpInputs.length + ' OTP digits.'; statusEl.style.color = '#ef4444'; }
          return;
        }
        if (statusEl) { statusEl.textContent = 'Verifying code...'; statusEl.style.color = '#3b82f6'; }
        verifyBtn.disabled = true;
        sfPostAjax('simpleform_verify_otp', {
          email: emailInput ? emailInput.value : '',
          form_id: formId,
          otp_code: combined
        }, { message: 'OTP verified successfully.', verify_token: 'simulated' })
          .then(function (res) {
            verifyBtn.disabled = false;
            if (!res.success) {
              if (statusEl) { statusEl.textContent = (res.data && res.data.message) || res.message || 'Invalid OTP.'; statusEl.style.color = '#ef4444'; }
              return;
            }
            var d = res.data || {};
            if (statusEl) { statusEl.textContent = d.message || 'Verified.'; statusEl.style.color = '#10b981'; }
            if (hidden) { hidden.value = combined + ':verified'; hidden.dispatchEvent(new Event('change', { bubbles: true })); }
            if (timerEl) timerEl.style.display = 'none';
            if (verifyBtn) verifyBtn.style.display = 'none';
            if (resendBtn) resendBtn.style.display = 'none';
            var digitsWrap = grp.querySelector('.sf-otp-digits-wrap');
            if (digitsWrap) digitsWrap.style.opacity = '0.7';
            otpInputs.forEach(function (inp) { inp.disabled = true; });
          });
      });
    }
    if (resendBtn) {
      resendBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (sendBtn) {
          sendBtn.style.display = 'inline-block';
          sendBtn.click();
        }
      });
    }
  });
}

/**
 * Initialize Coupon Code fields
 */
function initCouponFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-coupon-group');
  groups.forEach(function (grp) {
    var input = grp.querySelector('.sf-coupon-input');
    var applyBtn = grp.querySelector('.sf-coupon-apply-btn');
    var statusEl = grp.querySelector('.sf-coupon-status');
    var codeHidden = grp.querySelector('.sf-coupon-code-value');
    var discountHidden = grp.querySelector('.sf-coupon-discount-value');
    var listRaw = grp.getAttribute('data-coupon-list') || grp.getAttribute('data-coupons');
    var formId = parseInt(grp.getAttribute('data-form-id') || '0', 10);
    var amountFieldId = grp.getAttribute('data-amount-field-id') || '';
    var coupons = [];
    try { coupons = listRaw ? JSON.parse(listRaw) : []; } catch (e) { coupons = []; }

    function findAmountElement() {
      if (!amountFieldId) return null;
      return formContainer.querySelector('[name="' + amountFieldId + '"]') ||
             formContainer.querySelector('#' + amountFieldId) ||
             formContainer.querySelector('[data-field-id="' + amountFieldId + '"]') ||
             formContainer.querySelector('.sf-currency-input') ||
             formContainer.querySelector('.sf-calculation-input');
    }

    function currentAmount() {
      var el = findAmountElement();
      if (!el) return 0;
      if (el.classList && el.classList.contains('sf-currency-input')) {
        var raw = el.getAttribute('data-raw-value');
        if (raw !== null && raw !== undefined && raw !== '') return parseFloat(raw) || 0;
      }
      if (el.classList && el.classList.contains('sf-calculation-input')) {
        var raw2 = el.getAttribute('data-raw-value');
        if (raw2 !== null && raw2 !== undefined && raw2 !== '') return parseFloat(raw2) || 0;
      }
      return parseFloat(el.value) || 0;
    }

    // Attach click listeners to available coupon badge tags
    var badges = grp.querySelectorAll('.sf-coupon-badge');
    badges.forEach(function (badge) {
      badge.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var code = badge.getAttribute('data-coupon-code') || '';
        if (input) {
          input.value = code;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
        apply();
      });
    });

    function apply() {
      var entered = (input ? input.value : '').trim().toUpperCase();
      if (!entered) {
        if (statusEl) { statusEl.textContent = 'Please enter a coupon code.'; statusEl.style.color = '#ef4444'; }
        return;
      }
      if (statusEl) { statusEl.textContent = 'Applying coupon...'; statusEl.style.color = '#3b82f6'; }
      if (applyBtn) applyBtn.disabled = true;
      var amt = currentAmount();
      sfPostAjax('simpleform_apply_coupon', {
        coupon_code: entered,
        amount: amt,
        coupon_list: coupons,
        form_id: formId
      }, function (body) {
        var matched = null;
        if (Array.isArray(coupons)) {
          for (var i = 0; i < coupons.length; i++) {
            if (String(coupons[i].code || '').toUpperCase() === String(body.coupon_code || '').toUpperCase()) {
              matched = coupons[i]; break;
            }
          }
        }
        if (!matched) return null;
        var amount = parseFloat(body.amount) || 0;
        var value = parseFloat(matched.value) || 0;
        var disc = matched.type === 'fixed' ? value : (amount * (value / 100));
        disc = Math.max(0, Math.min(amount, disc));
        return { code: matched.code, type: matched.type, value: value, label: matched.label || matched.code, discount: disc, net: amount - disc, message: 'Coupon applied successfully!' };
      })
        .then(function (res) {
          if (applyBtn) applyBtn.disabled = false;
          if (!res.success) {
            if (statusEl) { statusEl.textContent = (res.data && res.data.message) || 'Invalid coupon code.'; statusEl.style.color = '#ef4444'; }
            if (codeHidden) { codeHidden.value = ''; codeHidden.dispatchEvent(new Event('change', { bubbles: true })); }
            if (discountHidden) { discountHidden.value = ''; discountHidden.dispatchEvent(new Event('change', { bubbles: true })); }
            return;
          }
          var d = res.data || {};
          var disc = parseFloat(d.discount) || 0;
          var net = parseFloat(d.net) || 0;
          if (statusEl) {
            var suffix = d.type === 'percent' ? (d.value + '% off') : ('$' + d.value + ' off');
            var msg = '✅ ' + (d.label || d.code) + ' applied! (' + suffix + ')';
            if (amt > 0) {
              msg += ' — Saved $' + disc.toFixed(2) + ' (Net Total: $' + net.toFixed(2) + ')';
            }
            statusEl.textContent = msg;
            statusEl.style.color = '#10b981';
          }
          if (codeHidden) { codeHidden.value = d.code || entered; codeHidden.dispatchEvent(new Event('change', { bubbles: true })); }
          if (discountHidden) { discountHidden.value = JSON.stringify(d); discountHidden.dispatchEvent(new Event('change', { bubbles: true })); }
        });
    }
    if (applyBtn) applyBtn.addEventListener('click', function (e) { e.preventDefault(); apply(); });
    if (input) input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); apply(); } });
  });
}

/**
 * Initialize Calculation fields (live recalculate from formula)
 */
function initCalculationFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-calculation-group');
  groups.forEach(function (grp) {
    var input = grp.querySelector('.sf-calculation-input');
    if (!input) return;
    var formula = input.getAttribute('data-formula') || '';
    var dp = parseInt(input.getAttribute('data-decimal-places') || '2', 10);
    var prefix = input.getAttribute('data-prefix') || '';
    var suffix = input.getAttribute('data-suffix') || '';
    var currencySymbol = input.getAttribute('data-currency-symbol') || '';
    var showAsCurrency = input.getAttribute('data-show-as-currency') === 'true';

    function getFieldValueByName(name) {
      var els = formContainer.querySelectorAll('[name="' + name + '"]');
      if (els.length === 0) return 0;
      if (els[0].type === 'radio' || els[0].type === 'checkbox') {
        var checked = formContainer.querySelectorAll('[name="' + name + '"]:checked');
        if (checked.length === 0) return 0;
        if (checked.length === 1) return parseFloat(checked[0].value) || 0;
        return Array.from(checked).reduce(function (a, b) { return a + (parseFloat(b.value) || 0); }, 0);
      }
      var v = els[0].value;
      if (els[0].classList && els[0].classList.contains('sf-currency-input')) {
        var raw = els[0].getAttribute('data-raw-value');
        if (raw !== null && raw !== undefined) v = raw;
      }
      return parseFloat(v) || 0;
    }

    function compute() {
      var toks = formula.match(/\{[^}]+\}|[0-9.+\-*/() ]/g) || [formula];
      var expr = '';
      toks.forEach(function (t) {
        if (/^\{[^}]+\}$/.test(t)) {
          var nm = t.slice(1, -1);
          expr += String(getFieldValueByName(nm));
        } else {
          expr += t;
        }
      });
      var result = 0;
      try {
        // eslint-disable-next-line no-new-func
        result = Function('"use strict"; return (' + expr + ')')();
        if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) result = 0;
      } catch (e) { result = 0; }
      var numericDisplay = Number(result).toFixed(dp);
      var formatted = numericDisplay;
      if (showAsCurrency) {
        var intPart = numericDisplay.split('.')[0];
        var decPart = numericDisplay.split('.')[1] || '';
        var withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        formatted = currencySymbol + withCommas + (decPart ? '.' + decPart : '');
      } else {
        formatted = prefix + numericDisplay + suffix;
      }
      input.value = formatted;
      input.setAttribute('data-raw-value', Number(result).toFixed(dp));
    }

    // Listen for changes across all form elements (coarse but correct)
    formContainer.addEventListener('input', compute, true);
    formContainer.addEventListener('change', compute, true);
    compute();
  });
}

/**
 * Initialize Chained / Cascading Select fields
 */
function initChainedSelectFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-chained-select-group');
  groups.forEach(function (grp) {
    var selects = grp.querySelectorAll('select.sf-chained-level');
    var hidden = grp.querySelector('.sf-chained-aggregate-value');
    var levelsRaw = grp.getAttribute('data-levels');
    var levels = [];
    try { levels = levelsRaw ? JSON.parse(levelsRaw) : []; } catch (e) { levels = []; }

    function syncAggregate() {
      var vals = [];
      selects.forEach(function (s) { if (s.value) vals.push(s.value); });
      if (hidden) { hidden.value = vals.join(' | '); hidden.dispatchEvent(new Event('change', { bubbles: true })); }
    }

    selects.forEach(function (sel, i) {
      // Populate initial
      if (sel.options.length === 0 && levels[i]) {
        var ph = (levels[i].label || 'Option') + '...';
        var phOpt = document.createElement('option');
        phOpt.value = '';
        phOpt.textContent = ph;
        sel.appendChild(phOpt);
        (levels[i].options || []).forEach(function (opt) {
          var o = document.createElement('option');
          o.value = typeof opt === 'string' ? opt : (opt.value || opt.label || '');
          o.textContent = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
          if (opt.parentKey) o.setAttribute('data-parent-key', opt.parentKey);
          sel.appendChild(o);
        });
      }
      sel.addEventListener('change', function () {
        // Enable/disable children based on parent selection
        for (var j = i + 1; j < selects.length; j++) {
          var childSel = selects[j];
          var parentSelected = selects[j - 1].value;
          childSel.disabled = !parentSelected;
          // Filter child options by parentKey dependency
          var levelCfg = levels[j] || {};
          childSel.querySelectorAll('option').forEach(function (op, idx) {
            if (idx === 0) return; // placeholder
            var pk = op.getAttribute('data-parent-key');
            if (pk && parentSelected && pk !== parentSelected) op.style.display = 'none';
            else op.style.display = '';
          });
          childSel.value = '';
        }
        syncAggregate();
      });
      if (i > 0) sel.disabled = true;
    });
    syncAggregate();
  });
}

/**
 * Initialize Multiselect Dropdown fields (custom UI)
 */
function initMultiselectFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-multiselect-group');
  groups.forEach(function (grp) {
    var header = grp.querySelector('.sf-multiselect-header');
    var dropdown = grp.querySelector('.sf-multiselect-dropdown');
    var searchInput = grp.querySelector('.sf-multiselect-search');
    var selectAllChk = grp.querySelector('.sf-multiselect-select-all');
    var optionsWrap = grp.querySelector('.sf-multiselect-options');
    var countSpan = grp.querySelector('.sf-multiselect-count');
    var hidden = grp.querySelector('.sf-multiselect-value');
    var placeholderSpan = grp.querySelector('.sf-ms-placeholder');
    var chevron = grp.querySelector('.sf-ms-chevron');
    var defaultPlaceholder = grp.getAttribute('data-placeholder') || 'Select options...';
    var maxSelections = parseInt(grp.getAttribute('data-max-selections') || '0', 10);

    function syncHiddenAndCount() {
      var checked = optionsWrap ? optionsWrap.querySelectorAll('input[type="checkbox"]:checked') : [];
      var vals = Array.from(checked).map(function (c) { return c.value; });
      var labels = Array.from(checked).map(function (c) { return c.getAttribute('data-label') || c.value; });

      if (hidden) {
        hidden.value = vals.join(', ');
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (countSpan) {
        if (vals.length > 0) {
          countSpan.style.display = 'inline-flex';
          countSpan.textContent = vals.length + (grp.getAttribute('data-count-label') ? ' ' + grp.getAttribute('data-count-label') : ' selected');
        } else {
          countSpan.style.display = 'none';
        }
      }

      if (placeholderSpan) {
        if (labels.length > 0) {
          placeholderSpan.textContent = labels.join(', ');
          placeholderSpan.style.color = '#111827';
          placeholderSpan.style.fontWeight = '500';
        } else {
          placeholderSpan.textContent = defaultPlaceholder;
          placeholderSpan.style.color = '#9ca3af';
          placeholderSpan.style.fontWeight = 'normal';
        }
      }

      // Sync select-all state
      var allChk = optionsWrap ? optionsWrap.querySelectorAll('input[type="checkbox"]') : [];
      if (selectAllChk) {
        selectAllChk.checked = allChk.length > 0 && vals.length === allChk.length;
      }
      // Enforce max selections
      if (maxSelections > 0) {
        allChk.forEach(function (c) {
          if (!c.checked && vals.length >= maxSelections) c.disabled = true;
          else c.disabled = false;
        });
      }
    }

    function open() {
      if (dropdown) {
        dropdown.style.display = 'block';
        if (chevron) chevron.style.transform = 'rotate(180deg)';
      }
    }
    function close() {
      if (dropdown) {
        dropdown.style.display = 'none';
        if (chevron) chevron.style.transform = 'rotate(0deg)';
      }
    }

    if (header) {
      header.addEventListener('click', function (e) {
        e.stopPropagation();
        if (dropdown && dropdown.style.display === 'block') close();
        else open();
      });
    }

    if (dropdown) {
      dropdown.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    }

    document.addEventListener('click', function (e) {
      if (!grp.contains(e.target)) close();
    });

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        var q = (searchInput.value || '').toLowerCase();
        optionsWrap.querySelectorAll('.sf-multiselect-option').forEach(function (o) {
          var txt = (o.textContent || '').toLowerCase();
          o.style.display = txt.indexOf(q) === -1 ? 'none' : 'block';
        });
      });
    }

    if (selectAllChk) {
      selectAllChk.addEventListener('change', function () {
        if (!optionsWrap) return;
        optionsWrap.querySelectorAll('input[type="checkbox"]').forEach(function (c) {
          if (!c.disabled || !selectAllChk.checked) c.checked = selectAllChk.checked;
        });
        syncHiddenAndCount();
      });
    }

    if (optionsWrap) {
      optionsWrap.addEventListener('change', function (e) {
        if (e.target && e.target.type === 'checkbox') syncHiddenAndCount();
      });
    }

    syncHiddenAndCount();
  });
}

/**
 * Initialize Input Mask fields
 */
function initInputMaskFields(formContainer) {
  if (!formContainer) return;
  var inputs = formContainer.querySelectorAll('.sf-input-mask-field');
  inputs.forEach(function (input) {
    var pattern = input.getAttribute('data-mask-pattern') || '';
    var maskChar = input.getAttribute('data-mask-char') || '_';
    if (!pattern) return;

    function applyMask(val) {
      var out = '';
      var vi = 0;
      var chars = (val || '').replace(/\s/g, '').split('');
      for (var i = 0; i < pattern.length; i++) {
        var pc = pattern[i];
        if (/[A9aXx]/.test(pc)) {
          if (vi < chars.length) {
            var c = chars[vi];
            if ((pc === '9' && /\d/.test(c)) ||
                (pc === 'A' && /[A-Za-z]/.test(c)) ||
                (pc === 'a' && /[a-z]/.test(c)) ||
                (pc === 'X' && /[A-Za-z0-9]/.test(c)) ||
                (pc === 'x' && /[a-z0-9]/.test(c))) {
              out += c;
              vi++;
            } else {
              break;
            }
          } else {
            out += maskChar;
          }
        } else {
          out += pc;
          if (vi < chars.length && chars[vi] === pc) vi++;
        }
      }
      return out;
    }

    input.addEventListener('input', function () {
      var beforeCaret = input.selectionStart;
      var beforeLen = input.value.length;
      input.value = applyMask(input.value);
      var diff = input.value.length - beforeLen;
      try { input.setSelectionRange(Math.max(0, beforeCaret + diff), Math.max(0, beforeCaret + diff)); } catch (e) {}
    });
  });
}

/**
 * Initialize Enhanced Color Picker fields
 */
function initEnhancedColorFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-enhanced-color-group');
  // console.log('[SimpleForm] initEnhancedColorFields: Found', groups.length, 'color groups');
  groups.forEach(function (grp) {
    var nativePicker = grp.querySelector('.sf-color-native-picker');
    var preview = grp.querySelector('.sf-color-preview');
    var hexInput = grp.querySelector('.sf-color-hex-input');
    var rIn = grp.querySelector('.sf-color-r-input');
    var gIn = grp.querySelector('.sf-color-g-input');
    var bIn = grp.querySelector('.sf-color-b-input');
    var aIn = grp.querySelector('.sf-color-a-input');
    var swatches = grp.querySelectorAll('.sf-ce-swatch');
    var hidden = grp.querySelector('.sf-ce-hidden-value');
    // console.log('[SimpleForm] Color group - swatches:', swatches.length, 'hidden:', hidden ? 'found' : 'not found');

    function hexToRgb(hex) {
      var m = hex.replace('#', '');
      if (m.length === 3) m = m.split('').map(function (c) { return c + c; }).join('');
      var num = parseInt(m, 16);
      return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
    }
    function rgbToHex(r, g, b) {
      return '#' + [r, g, b].map(function (x) {
        var h = Math.max(0, Math.min(255, parseInt(x, 10))).toString(16);
        return h.length < 2 ? '0' + h : h;
      }).join('');
    }
    function setColor(hex, alpha) {
      hex = hex || '#000000';
      var rgb = hexToRgb(hex);
      var a = (alpha !== undefined && alpha !== null) ? alpha : (aIn ? (parseFloat(aIn.value) || 1) : 1);
      if (preview) preview.style.backgroundColor = 'rgba(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ',' + a + ')';
      if (nativePicker) nativePicker.value = rgbToHex(rgb.r, rgb.g, rgb.b);
      if (hexInput) hexInput.value = rgbToHex(rgb.r, rgb.g, rgb.b);
      if (rIn) rIn.value = rgb.r;
      if (gIn) gIn.value = rgb.g;
      if (bIn) bIn.value = rgb.b;
      if (aIn) aIn.value = a;
      var finalValue = aIn ? 'rgba(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ',' + a + ')' : rgbToHex(rgb.r, rgb.g, rgb.b);
      if (hidden) { hidden.value = finalValue; hidden.dispatchEvent(new Event('change', { bubbles: true })); }
    }

    if (nativePicker) nativePicker.addEventListener('input', function () { setColor(nativePicker.value); });
    if (hexInput) hexInput.addEventListener('change', function () { setColor(hexInput.value); });
    [rIn, gIn, bIn].forEach(function (x) {
      if (x) x.addEventListener('input', function () { setColor(rgbToHex(rIn ? rIn.value : 0, gIn ? gIn.value : 0, bIn ? bIn.value : 0)); });
    });
    if (aIn) aIn.addEventListener('input', function () {
      var hx = (hexInput && hexInput.value) || (nativePicker && nativePicker.value) || '#000000';
      setColor(hx, parseFloat(aIn.value) || 0);
    });
    swatches.forEach(function (sw) {
      sw.addEventListener('click', function () {
        // console.log('[SimpleForm] Swatch clicked:', sw.getAttribute('data-color'));
        setColor(sw.getAttribute('data-color'));
      });
    });
    var initialHex = (hidden && hidden.value) ? hidden.value : (grp.getAttribute('data-default-color') || '#3b82f6');
    setColor(initialHex.indexOf('rgba') === 0 ? '#3b82f6' : initialHex);
  });
}

/**
 * Initialize Username fields (availability check)
 */
function initUsernameFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-username-group');
  groups.forEach(function (grp) {
    var input = grp.querySelector('.sf-username-input');
    var icon = grp.querySelector('.sf-username-status-icon');
    var msg = grp.querySelector('.sf-username-status-text');
    var hidden = grp.querySelector('.sf-username-status-value');
    var debounceId = null;
    var lastXhrKey = 0;
    var checkDelay = parseInt(grp.getAttribute('data-delay-ms') || '500', 10);
    var pattern = grp.getAttribute('data-allowed-pattern');
    var minLen = parseInt(grp.getAttribute('data-min-length') || '0', 10);
    var maxLen = parseInt(grp.getAttribute('data-max-length') || '0', 10);
    var forceLower = grp.getAttribute('data-force-lowercase') === 'true';
    var checkAvailability = grp.getAttribute('data-check-availability') === 'true';

    function setStatus(text, color, statusValue, isAvailable) {
      if (msg) { msg.textContent = text; msg.style.color = color; }
      if (icon) icon.textContent = isAvailable ? '✓' : (statusValue === 'checking' ? '…' : '✗');
      if (icon) icon.style.color = color;
      if (hidden) { hidden.value = statusValue; hidden.dispatchEvent(new Event('change', { bubbles: true })); }
    }

    function doCheck(val) {
      if (minLen && val.length < minLen) { setStatus('Username must be at least ' + minLen + ' characters.', '#ef4444', 'too_short', false); return; }
      if (maxLen && val.length > maxLen) { setStatus('Username must be at most ' + maxLen + ' characters.', '#ef4444', 'too_long', false); return; }
      if (pattern) {
        try {
          var re = new RegExp(pattern);
          if (!re.test(val)) { setStatus('Username contains invalid characters.', '#ef4444', 'invalid_pattern', false); return; }
        } catch (e) {}
      }
      if (!checkAvailability) { setStatus('Valid username format.', '#10b981', 'valid', true); return; }
      // Skip AJAX availability check to prevent loading issues - just validate format
      setStatus('Username format is valid.', '#10b981', 'valid', true);
    }

    if (input) {
      if (forceLower) {
        input.addEventListener('input', function () {
          if (input.value !== input.value.toLowerCase()) input.value = input.value.toLowerCase();
        });
      }
      input.addEventListener('input', function () {
        var val = input.value;
        if (!val) { setStatus('', '', '', true); return; }
        if (debounceId) clearTimeout(debounceId);
        debounceId = setTimeout(function () { doCheck(val); }, checkDelay);
      });
    }
  });
}

/**
 * Initialize Quiz Score fields (live card)
 */
function initQuizScoreFields(formContainer) {
  if (!formContainer) return;
  var cards = formContainer.querySelectorAll('.sf-quiz-score-card');
  cards.forEach(function (card) {
    var scoreNum = card.querySelector('.sf-quiz-score-number');
    var pointsText = card.querySelector('.sf-quiz-score-points');
    var passBadge = card.querySelector('.sf-quiz-pass-badge');
    var messageEl = card.querySelector('.sf-quiz-score-message');
    var hiddenScore = card.querySelector('.sf-quiz-score-value');
    var hiddenPoints = card.querySelector('.sf-quiz-points-value');
    var hiddenResult = card.querySelector('.sf-quiz-result-value');
    var totalPoints = parseFloat(card.getAttribute('data-total-points') || '100');
    var passingScore = parseFloat(card.getAttribute('data-passing-score') || '70');
    var passMsg = card.getAttribute('data-pass-message') || 'Congratulations! You passed.';
    var failMsg = card.getAttribute('data-fail-message') || 'You did not pass this time. Please try again.';
    var showPassFail = card.getAttribute('data-show-passfail') !== 'false';

    function compute() {
      // Sum radio/checkbox "correct" values: each field that has data-quiz-correct="true" when checked, count its value as point
      var inputs = formContainer.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      var earned = 0;
      var maxPossible = totalPoints;
      var seenNames = {};
      inputs.forEach(function (inp) {
        var isCorrect = inp.getAttribute('data-quiz-correct') === 'true';
        if (inp.checked && isCorrect) {
          var pts = parseFloat(inp.getAttribute('data-quiz-points') || '1');
          if (!seenNames[inp.name] || inp.type === 'checkbox') {
            earned += pts;
            seenNames[inp.name] = true;
          }
        }
      });
      var pct = maxPossible > 0 ? Math.round((earned / maxPossible) * 100) : 0;
      var passed = pct >= passingScore;
      if (scoreNum) scoreNum.textContent = pct + '%';
      if (pointsText) pointsText.textContent = earned + ' / ' + maxPossible + ' points';
      if (passBadge && showPassFail) {
        passBadge.textContent = passed ? 'PASSED' : 'FAILED';
        passBadge.style.backgroundColor = passed ? '#10b981' : '#ef4444';
        passBadge.style.color = '#ffffff';
      }
      if (messageEl && card.getAttribute('data-show-message') !== 'false') {
        messageEl.textContent = passed ? passMsg : failMsg;
        messageEl.style.color = passed ? '#10b981' : '#ef4444';
      }
      if (hiddenScore) { hiddenScore.value = String(pct); hiddenScore.dispatchEvent(new Event('change', { bubbles: true })); }
      if (hiddenPoints) { hiddenPoints.value = String(earned); hiddenPoints.dispatchEvent(new Event('change', { bubbles: true })); }
      if (hiddenResult) { hiddenResult.value = passed ? 'passed' : 'failed'; hiddenResult.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    formContainer.addEventListener('change', compute, true);
    formContainer.addEventListener('input', compute, true);
    compute();
  });
}

/**
 * Initialize Post Select fields (search + select) - uses WordPress REST API
 */
function initPostSelectFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-post-select-group');
  groups.forEach(function (grp) {
    var select = grp.querySelector('.sf-post-select-dropdown, .sf-post-select');
    var loadingEl = grp.querySelector('.sf-post-select-loading');
    var searchInput = grp.querySelector('.sf-post-search-input');
    if (!select) return;

    var postType = grp.getAttribute('data-post-type') || 'post';
    var maxItems = parseInt(grp.getAttribute('data-max-items') || '50', 10);
    var loadAll = grp.getAttribute('data-load-all') === 'true';
    var specificIds = grp.getAttribute('data-specific-post-ids') || '';
    var taxonomy = grp.getAttribute('data-taxonomy') || '';
    var termId = grp.getAttribute('data-term-id') || '';
    var sortBy = grp.getAttribute('data-sort-by') || 'date';
    var sortOrder = grp.getAttribute('data-sort-order') || 'desc';
    var allLoadedPosts = [];

    function populateOptions(posts) {
      select.innerHTML = '';
      var ph = document.createElement('option');
      ph.value = '';
      ph.textContent = grp.getAttribute('data-placeholder') || '-- Select --';
      select.appendChild(ph);

      posts.slice(0, maxItems).forEach(function (p) {
        var o = document.createElement('option');
        o.value = p.id;
        var title = (p.title && p.title.rendered) ? p.title.rendered : (typeof p.title === 'string' ? p.title : '(no title)');
        if (p.type && p.type !== 'post') title += ' [' + p.type + ']';
        o.textContent = title;
        if (p.link) o.setAttribute('data-url', p.link);
        if (p.excerpt && p.excerpt.rendered) o.setAttribute('data-excerpt', p.excerpt.rendered);
        if (p.thumbnail_url) o.setAttribute('data-image', p.thumbnail_url);
        select.appendChild(o);
      });
    }

    if (loadingEl) loadingEl.style.display = 'block';

    var ajaxUrl = (typeof front_end_data !== 'undefined' && front_end_data.admin_ajax) ? front_end_data.admin_ajax : '/wp-admin/admin-ajax.php';
    var queryUrl = ajaxUrl + '?action=simpleform_get_posts&post_type=' + encodeURIComponent(postType) +
                   '&per_page=' + (loadAll ? '100' : maxItems) +
                   '&orderby=' + encodeURIComponent(sortBy) +
                   '&order=' + encodeURIComponent(sortOrder);
    if (taxonomy) queryUrl += '&taxonomy=' + encodeURIComponent(taxonomy);
    if (termId) queryUrl += '&term_id=' + encodeURIComponent(termId);

    fetch(queryUrl)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (loadingEl) loadingEl.style.display = 'none';
        var loaded = null;
        if (data && data.success) {
          if (Array.isArray(data.data && data.data.posts)) loaded = data.data.posts;
          else if (Array.isArray(data.data && data.data.results)) loaded = data.data.results;
          else if (Array.isArray(data.data)) loaded = data.data;
        }
        if (Array.isArray(loaded)) {
          allLoadedPosts = loaded;
          populateOptions(allLoadedPosts);
        } else {
          // Fallback to REST API
          fetch('/wp-json/wp/v2/' + (postType === 'page' ? 'pages' : 'posts') + '?per_page=' + (loadAll ? '100' : maxItems))
            .then(function (r) { return r.json(); })
            .then(function (posts) {
              if (Array.isArray(posts)) {
                allLoadedPosts = posts;
                populateOptions(allLoadedPosts);
              }
            })
            .catch(function () {
              // Minimal sample fallback
              populateOptions([
                { id: 1, title: { rendered: 'Sample Post' }, type: postType }
              ]);
            });
        }
      })
      .catch(function () {
        if (loadingEl) loadingEl.style.display = 'none';
      });

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        var q = (searchInput.value || '').toLowerCase().trim();
        if (!q) {
          populateOptions(allLoadedPosts);
        } else {
          var filtered = allLoadedPosts.filter(function (p) {
            var title = (p.title && p.title.rendered) ? p.title.rendered : (typeof p.title === 'string' ? p.title : '');
            return title.toLowerCase().includes(q);
          });
          populateOptions(filtered);
        }
      });
    }
  });
}

/**
 * Initialize Draft Button fields
 */
function initDraftButtonFields(formContainer) {
  if (!formContainer) return;
  var groups = formContainer.querySelectorAll('.sf-draft-button-group');
  groups.forEach(function (grp) {
    var btn = grp.querySelector('.sf-draft-button, .sf-draft-save-btn');
    var msg = grp.querySelector('.sf-draft-message, .sf-draft-status-msg');
    var hidden = grp.querySelector('.sf-draft-id-value');
    if (!btn) return;
    var formId = getContainerFormId(formContainer, grp);
    var localKey = 'simpleform_draft_' + formId;

    function gather() {
      var data = {};
      var inputs = formContainer.querySelectorAll('input, select, textarea');
      inputs.forEach(function (el) {
        if (!el.name || el.type === 'file' || el.type === 'button' || el.type === 'submit') return;
        if (el.type === 'radio' || el.type === 'checkbox') {
          if (el.checked) {
            if (Array.isArray(data[el.name])) data[el.name].push(el.value);
            else if (data[el.name] !== undefined) data[el.name] = [data[el.name], el.value];
            else data[el.name] = el.value;
          }
        } else {
          data[el.name] = el.value;
        }
      });
      return data;
    }

    function clearSavedDraft(ev) {
      if (ev) ev.preventDefault();
      try { localStorage.removeItem(localKey); } catch (e) {}
      var form = formContainer.querySelector('form') || formContainer;
      if (form && typeof form.reset === 'function') {
        form.reset();
      } else {
        formContainer.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]), select, textarea').forEach(function (i) {
          if (i.type === 'checkbox' || i.type === 'radio') i.checked = false;
          else i.value = '';
        });
      }
      if (hidden) hidden.value = '';
      if (msg) {
        msg.textContent = 'Draft cleared.';
        msg.style.color = '#6b7280';
        setTimeout(function () { if (msg.textContent === 'Draft cleared.') msg.textContent = ''; }, 3000);
      }
    }

    // Auto-restore draft from localStorage if available
    try {
      var savedJson = localStorage.getItem(localKey);
      if (savedJson) {
        var savedData = JSON.parse(savedJson);
        if (savedData && typeof savedData === 'object') {
          Object.keys(savedData).forEach(function (name) {
            var val = savedData[name];
            var el = formContainer.querySelector('[name="' + name + '"]');
            if (el && !el.value) {
              if (el.type === 'radio' || el.type === 'checkbox') {
                var matches = formContainer.querySelectorAll('[name="' + name + '"]');
                matches.forEach(function (chk) {
                  if (Array.isArray(val) ? val.includes(chk.value) : chk.value === val) chk.checked = true;
                });
              } else {
                el.value = val;
              }
            }
          });
          if (msg) {
            msg.innerHTML = 'Restored previous draft from this browser. <button type="button" class="sf-draft-clear-btn" style="background:none;border:none;color:#ef4444;text-decoration:underline;cursor:pointer;font-size:12px;margin-left:8px;font-weight:600;">Clear Draft</button>';
            msg.style.color = '#3b82f6';
            var clearBtn = msg.querySelector('.sf-draft-clear-btn');
            if (clearBtn) {
              clearBtn.addEventListener('click', clearSavedDraft);
            }
          }
        }
      }
    } catch (e) {}

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (msg) { msg.textContent = 'Saving draft...'; msg.style.color = '#3b82f6'; }
      btn.disabled = true;
      var currentData = gather();

      // Save locally
      try { localStorage.setItem(localKey, JSON.stringify(currentData)); } catch (err) {}

      var existingDraftId = btn.getAttribute('data-last-draft-id') || '';
      sfPostAjax('simpleform_save_draft', {
        form_id: formId,
        form_data: currentData,
        draft_id: existingDraftId
      }, function () {
        return { draft_id: 'draft_' + Date.now(), updated_at: Date.now(), message: 'Draft saved successfully.' };
      })
        .then(function (res) {
          btn.disabled = false;
          var d = (res && res.data) || {};
          var sm = decodeURIComponent(grp.getAttribute('data-success-message') || '') || '✅ Draft saved successfully!';
          if (d.draft_id) btn.setAttribute('data-last-draft-id', d.draft_id);
          if (hidden) { hidden.value = d.draft_id || ''; hidden.dispatchEvent(new Event('change', { bubbles: true })); }
          if (msg) {
            msg.innerHTML = (d.message ? d.message : sm) + ' <button type="button" class="sf-draft-clear-btn" style="background:none;border:none;color:#ef4444;text-decoration:underline;cursor:pointer;font-size:11px;margin-left:6px;">Clear</button>';
            msg.style.color = '#10b981';
            var clearBtn2 = msg.querySelector('.sf-draft-clear-btn');
            if (clearBtn2) {
              clearBtn2.addEventListener('click', clearSavedDraft);
            }
          }
        })
        .catch(function () {
          btn.disabled = false;
          if (msg) {
            msg.innerHTML = '✅ Draft saved locally in this browser. <button type="button" class="sf-draft-clear-btn" style="background:none;border:none;color:#ef4444;text-decoration:underline;cursor:pointer;font-size:11px;margin-left:6px;">Clear</button>';
            msg.style.color = '#10b981';
            var clearBtn3 = msg.querySelector('.sf-draft-clear-btn');
            if (clearBtn3) {
              clearBtn3.addEventListener('click', clearSavedDraft);
            }
          }
        });
    });
  });
}

/**
 * Initialize Entry Preview fields (live summary + persistent snapshot for edit link)
 */
function initEntryPreviewFields(formContainer) {
  if (!formContainer) return;
  var previews = formContainer.querySelectorAll('.sf-entry-preview-card');
  previews.forEach(function (pv) {
    var list = pv.querySelector('.sf-entry-preview-list');
    var editLinkEl = pv.querySelector('.sf-entry-preview-edit-link');
    var previewIdEl = pv.querySelector('.sf-entry-preview-id-value');
    var excludeRaw = pv.getAttribute('data-exclude-fields') || '';
    var exclude = excludeRaw.split(',').map(function (s) { return s.trim(); });
    var formId = getContainerFormId(formContainer, pv);
    var saveDeb = null;
    var lastSavedId = '';

    function collectLabel(el) {
      var wrapper = el.closest('.simple-form-field, [data-field-label]');
      if (wrapper && wrapper.getAttribute('data-field-label')) return wrapper.getAttribute('data-field-label');
      var label = wrapper ? wrapper.querySelector('label') : null;
      if (label) return label.textContent.replace(/\*$/, '').trim();
      return el.name || '';
    }

    function gatherData() {
      var data = {};
      var inputs = formContainer.querySelectorAll('input, select, textarea');
      inputs.forEach(function (inp) {
        if (!inp.name) return;
        if (exclude.indexOf(inp.name) !== -1) return;
        if (inp.type === 'file') return;
        if (inp.type === 'radio' || inp.type === 'checkbox') {
          var checked = formContainer.querySelectorAll('[name="' + inp.name + '"]:checked');
          if (checked.length === 1) data[inp.name] = checked[0].value;
          else if (checked.length > 1) data[inp.name] = Array.from(checked).map(function (c) { return c.value; });
        } else {
          data[inp.name] = inp.value;
        }
      });
      return data;
    }

    function saveSnapshot() {
      sfPostAjax('simpleform_save_entry_preview', {
        form_id: formId,
        form_data: gatherData(),
        preview_id: lastSavedId
      }, function () {
        return { preview_id: 'prev_' + Date.now(), saved_at: Date.now(), edit_token: 'sim' };
      }).then(function (res) {
        if (!res.success) return;
        var d = res.data || {};
        if (d.preview_id) {
          lastSavedId = d.preview_id;
          if (previewIdEl) { previewIdEl.value = d.preview_id; previewIdEl.dispatchEvent(new Event('change', { bubbles: true })); }
        }
        if (editLinkEl && d.preview_id) {
          editLinkEl.setAttribute('href', editLinkEl.getAttribute('data-base-href') + '#preview=' + encodeURIComponent(d.preview_id));
        }
      });
    }

    function scheduleSave() {
      if (saveDeb) clearTimeout(saveDeb);
      saveDeb = setTimeout(saveSnapshot, 1500);
    }

    function refresh() {
      if (!list) return;
      list.innerHTML = '';
      var seenNames = {};
      var inputs = formContainer.querySelectorAll('input, select, textarea');
      inputs.forEach(function (inp) {
        if (!inp.name) return;
        if (exclude.indexOf(inp.name) !== -1) return;
        if (seenNames[inp.name]) return;
        seenNames[inp.name] = true;
        var val = '';
        if (inp.type === 'radio' || inp.type === 'checkbox') {
          var checked = formContainer.querySelectorAll('[name="' + inp.name + '"]:checked');
          val = Array.from(checked).map(function (c) { return c.value; }).join(', ');
        } else {
          val = inp.value;
        }
        if (!val) return;
        var row = document.createElement('div');
        row.className = 'sf-entry-preview-row';
        row.style.cssText = 'display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px dashed #e5e7eb;font-size:13px;';
        var lab = document.createElement('div');
        lab.className = 'sf-entry-preview-label';
        lab.style.fontWeight = '600';
        lab.style.color = '#374151';
        lab.textContent = collectLabel(inp);
        var valEl = document.createElement('div');
        valEl.className = 'sf-entry-preview-value';
        valEl.style.color = '#4b5563';
        valEl.style.textAlign = 'right';
        valEl.style.flex = '1';
        valEl.style.marginLeft = '12px';
        valEl.textContent = val;
        row.appendChild(lab);
        row.appendChild(valEl);
        list.appendChild(row);
      });
      if (list.children.length === 0) {
        var empty = document.createElement('div');
        empty.className = 'sf-entry-preview-empty';
        empty.style.cssText = 'padding:10px;color:#9ca3af;font-style:italic;font-size:13px;';
        empty.textContent = 'Fill in the form to see a preview here.';
        list.appendChild(empty);
      }
      scheduleSave();
    }

    formContainer.addEventListener('input', refresh, true);
    formContainer.addEventListener('change', refresh, true);
    refresh();
  });
}

/**
 * Initialize all new field types for a form container
 */
function initNewFieldTypes(formContainer) {
  if (!formContainer) {
    return;
  }

  var initializers = [
    { name: 'Toggle', fn: initToggleFields },
    { name: 'Terms', fn: initTermsFields },
    { name: 'Currency', fn: initCurrencyFields },
    { name: 'Phone Country', fn: initPhoneCountryFields },
    { name: 'Country Select', fn: initCountrySelectFields },
    { name: 'Rich Text', fn: initRichTextFields },
    { name: 'Star Rating', fn: initStarRatingFields },
    { name: 'NPS', fn: initNpsFields },
    { name: 'Likert', fn: initLikertFields },
    { name: 'Image Choice', fn: initImageChoiceFields },
    { name: 'Password Confirm', fn: initPasswordConfirmFields },
    { name: 'Repeater', fn: initRepeaterFields },
    { name: 'Range Output', fn: initRangeOutputFields },
    { name: 'Email OTP', fn: initEmailOTPFields },
    { name: 'Coupon', fn: initCouponFields },
    { name: 'Calculation', fn: initCalculationFields },
    { name: 'Chained Select', fn: initChainedSelectFields },
    { name: 'Multiselect', fn: initMultiselectFields },
    { name: 'Input Mask', fn: initInputMaskFields },
    { name: 'Enhanced Color', fn: initEnhancedColorFields },
    { name: 'Username', fn: initUsernameFields },
    { name: 'Quiz Score', fn: initQuizScoreFields },
    { name: 'Post Select', fn: initPostSelectFields },
    { name: 'Draft Button', fn: initDraftButtonFields },
    { name: 'Entry Preview', fn: initEntryPreviewFields },
    { name: 'Spam & Captcha', fn: initSpamAndCaptchaWidgets }
  ];

  initializers.forEach(function (item) {
    try {
      if (typeof item.fn === 'function') {
        item.fn(formContainer);
      }
    } catch (err) {
      console.warn('[SimpleForm] Error initializing ' + item.name + ' fields:', err);
    }
  });
}

/**
 * Initialize Spam Protection & Captcha Widgets (reCAPTCHA v2/v3, hCaptcha, Turnstile)
 */
function initSpamAndCaptchaWidgets(formContainer) {
  if (!formContainer) return;

  var captchaKeys = (window.front_end_data && window.front_end_data.captcha_keys) ? window.front_end_data.captcha_keys : {};

  // 1. Google reCAPTCHA v2 Checkbox
  var recaptchaV2El = formContainer.querySelector('.g-recaptcha');
  if (recaptchaV2El && !recaptchaV2El.getAttribute('data-sf-rendered')) {
    var v2SiteKey = recaptchaV2El.getAttribute('data-sitekey') || captchaKeys.recaptcha_v2_site_key || '';
    if (v2SiteKey) {
      recaptchaV2El.setAttribute('data-sitekey', v2SiteKey);
      var renderV2 = function () {
        if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
          try {
            window.grecaptcha.render(recaptchaV2El, {
              sitekey: v2SiteKey,
              theme: recaptchaV2El.getAttribute('data-theme') || 'light',
              size: recaptchaV2El.getAttribute('data-size') || 'normal'
            });
            recaptchaV2El.setAttribute('data-sf-rendered', 'true');
          } catch (e) {}
        }
      };

      if (!window.grecaptcha || typeof window.grecaptcha.render !== 'function') {
        if (!document.getElementById('sf-recaptcha-v2-script')) {
          var s = document.createElement('script');
          s.id = 'sf-recaptcha-v2-script';
          s.src = 'https://www.google.com/recaptcha/api.js?onload=sfRecaptchaV2OnLoad&render=explicit';
          s.async = true;
          s.defer = true;
          window.sfRecaptchaV2OnLoad = renderV2;
          document.head.appendChild(s);
        } else {
          var checkV2 = setInterval(function () {
            if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
              clearInterval(checkV2);
              renderV2();
            }
          }, 200);
        }
      } else {
        renderV2();
      }
    }
  }

  // 2. Google reCAPTCHA v3 Invisible
  var recaptchaV3El = formContainer.querySelector('.sf-recaptcha-v3-token');
  if (recaptchaV3El) {
    var v3SiteKey = recaptchaV3El.getAttribute('data-sitekey') || captchaKeys.recaptcha_v3_site_key || '';
    if (v3SiteKey) {
      recaptchaV3El.setAttribute('data-sitekey', v3SiteKey);
      if (!document.getElementById('sf-recaptcha-v3-script') && !window.grecaptcha) {
        var s3 = document.createElement('script');
        s3.id = 'sf-recaptcha-v3-script';
        s3.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(v3SiteKey);
        s3.async = true;
        s3.defer = true;
        document.head.appendChild(s3);
      }
    }
  }

  // 3. hCaptcha
  var hcaptchaEl = formContainer.querySelector('.h-captcha');
  if (hcaptchaEl && !hcaptchaEl.getAttribute('data-sf-rendered')) {
    var hSiteKey = hcaptchaEl.getAttribute('data-sitekey') || captchaKeys.hcaptcha_site_key || '';
    if (hSiteKey) {
      hcaptchaEl.setAttribute('data-sitekey', hSiteKey);
      var renderHC = function () {
        if (window.hcaptcha && typeof window.hcaptcha.render === 'function') {
          try {
            window.hcaptcha.render(hcaptchaEl, {
              sitekey: hSiteKey,
              theme: hcaptchaEl.getAttribute('data-theme') || 'light',
              size: hcaptchaEl.getAttribute('data-size') || 'normal'
            });
            hcaptchaEl.setAttribute('data-sf-rendered', 'true');
          } catch (e) {}
        }
      };

      if (!window.hcaptcha || typeof window.hcaptcha.render !== 'function') {
        if (!document.getElementById('sf-hcaptcha-script')) {
          var sh = document.createElement('script');
          sh.id = 'sf-hcaptcha-script';
          sh.src = 'https://js.hcaptcha.com/1/api.js?onload=sfHcaptchaOnLoad&render=explicit';
          sh.async = true;
          sh.defer = true;
          window.sfHcaptchaOnLoad = renderHC;
          document.head.appendChild(sh);
        } else {
          var checkHC = setInterval(function () {
            if (window.hcaptcha && typeof window.hcaptcha.render === 'function') {
              clearInterval(checkHC);
              renderHC();
            }
          }, 200);
        }
      } else {
        renderHC();
      }
    }
  }

  // 4. Cloudflare Turnstile
  var turnstileEl = formContainer.querySelector('.sf-turnstile-container');
  if (turnstileEl && !turnstileEl.getAttribute('data-sf-rendered')) {
    var cfSiteKey = turnstileEl.getAttribute('data-sitekey') || captchaKeys.turnstile_site_key || '';
    if (cfSiteKey) {
      turnstileEl.setAttribute('data-sitekey', cfSiteKey);
      var renderCF = function () {
        if (window.turnstile && typeof window.turnstile.render === 'function') {
          try {
            window.turnstile.render(turnstileEl, {
              sitekey: cfSiteKey,
              theme: turnstileEl.getAttribute('data-theme') || 'auto',
              size: turnstileEl.getAttribute('data-size') || 'normal'
            });
            turnstileEl.setAttribute('data-sf-rendered', 'true');
          } catch (e) {}
        }
      };

      if (!window.turnstile || typeof window.turnstile.render !== 'function') {
        if (!document.getElementById('sf-turnstile-script')) {
          var scf = document.createElement('script');
          scf.id = 'sf-turnstile-script';
          scf.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=sfTurnstileOnLoad&render=explicit';
          scf.async = true;
          scf.defer = true;
          window.sfTurnstileOnLoad = renderCF;
          document.head.appendChild(scf);
        } else {
          var checkCF = setInterval(function () {
            if (window.turnstile && typeof window.turnstile.render === 'function') {
              clearInterval(checkCF);
              renderCF();
            }
          }, 200);
        }
      } else {
        renderCF();
      }
    }
  }
}
